/**
 * The remaining register screens: fixed assets, vendor payables, master data,
 * user administration and the offline sync queue.
 */
import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import {
  AlertCircle, ArrowLeftRight, Boxes, CheckCircle2, Download, KeyRound, Pencil, Plus, RefreshCw, Trash2, Truck,
  UserCheck, Users as UsersIcon, UserX, Wallet,
  Eye, Copy,
} from 'lucide-react'
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import {
  Alert, Card, Confirm, EmptyState, Field, Input, Loading, Modal, SearchInput, Select, Stat,
  StatusPill, Textarea, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import { AttachmentCollector, NotPermitted, VendorDocuments } from '@/components/docs/shared'
import { DistributionButton, MaterialDistributionPanel, useMaterialDistribution } from '@/components/masters/MaterialDistribution'
import {
  CfStatusPill, MaterialCashflowApprovals, useCashflowLines, useIsSuperadminFlag,
} from '@/components/masters/MaterialCashflow'
import {
  useVendorAckOrders, VendorOrdersButton, VendorOrdersPanel,
} from '@/components/masters/VendorOrders'
import type { PendingAttachment } from '@/components/docs/shared'
import { queueAttachments } from '@/components/docs/shared'
import { useAuth } from '@/lib/auth'
import { useMaterials, useProfiles, useQuery, useSites, useVendors } from '@/lib/data'
import { useCompanySettings } from '@/lib/settings'
import { callFunction, downloadFromFunction, supabase } from '@/lib/supabase'
import { can, conversionText, factorShort, isHoRole, iso, ROLE_LABEL } from '@/lib/domain'
import { ChartLegend } from '@/components/charts/ChartLegend'
import { generateTempPassword, passwordProblem } from '@/components/layout/ChangePassword'
// Master Data access is a BOARD RESOLUTION of 31 Aug 2026, transcribed once in
// masters-access.ts and checked against the board's own spreadsheet by
// scripts/check-masters-access.py. No policy is decided in this file.
import {
  MASTER_TABS, masterDetailAccess, masterListAccess, maySeeBaseRate, visibleMasterTabs,
} from '@/lib/masters-access'
import type { MasterTab } from '@/lib/masters-access'
import { dateTime, ddmmyyyy, qty, rupees } from '@/lib/format'
import { discardEntry, flushOutbox, retryEntry, useSyncStore } from '@/lib/sync'
import { localDb } from '@/lib/db'
import { enqueue } from '@/lib/sync'
import { CHART_SERIES } from '@/lib/theme'

// ===========================================================================
// Fixed assets
// ===========================================================================
// The board decided on 02-09-2026 that the register is SEVEN separate tables,
// one per category, because a truck is described by a registration number, a
// fitness certificate and a permit, and a ceiling fan is described by none of
// those. Vehicles is the first. The rest arrive one at a time, and each gets
// its own tab here.
//
// "Other assets" is the original single-table register, untouched.
// ===========================================================================
type AssetTab = 'dashboard' | 'vehicles' | 'machinery' | 'tools' | 'furniture' | 'electronics'
  | 'electrical' | 'operating' | 'settings'

type AssetCategory = Exclude<AssetTab, 'settings' | 'dashboard'>

// One place that knows how the seven categories differ. The button label, what
// the thing is called, and which of the optional columns that table actually
// has. Everything else about the new-asset form is identical across the seven.
const NEW_ASSET: Record<AssetCategory, {
  noun: string; nameLabel: string; namePlaceholder: string
  hasRate: boolean; hasUom: boolean; hasReg: boolean; hasSerial: boolean
}> = {
  vehicles:    { noun: 'vehicle',              nameLabel: 'Vehicle type',  namePlaceholder: 'Tipper, Bus, Camper…',        hasRate: false, hasUom: false, hasReg: true,  hasSerial: false },
  machinery:   { noun: 'machine',              nameLabel: 'Machine name',  namePlaceholder: 'Backhoe Loader, Concrete Mixer…', hasRate: false, hasUom: false, hasReg: true,  hasSerial: true },
  tools:       { noun: 'tool',                 nameLabel: 'Tool name',     namePlaceholder: 'Welding Machine, Vibrator…',  hasRate: true,  hasUom: false, hasReg: false, hasSerial: true },
  furniture:   { noun: 'furniture item',       nameLabel: 'Item name',     namePlaceholder: 'Executive Chair, Steel Almirah…', hasRate: true, hasUom: false, hasReg: false, hasSerial: false },
  electronics: { noun: 'electronic appliance', nameLabel: 'Item name',     namePlaceholder: 'Computer Laptop, Printer…',   hasRate: true,  hasUom: false, hasReg: false, hasSerial: true },
  electrical:  { noun: 'electrical item',      nameLabel: 'Item name',     namePlaceholder: 'Ceiling Fan, Welding Cable…', hasRate: true,  hasUom: true,  hasReg: false, hasSerial: false },
  operating:   { noun: 'operating asset',      nameLabel: 'Item name',     namePlaceholder: 'Scaffolding Pipe, Water Pump…', hasRate: true, hasUom: true,  hasReg: false, hasSerial: false },
}

export function Assets() {
  const { profile } = useAuth()
  const role = profile!.role
  const mayEdit = can(role, 'manageAssets')
  // The dashboard is company-wide, so it is head office only. A site store-man
  // would otherwise see a Rs 13.59 crore headline built from his own site
  // alone — a total that looks complete and is not. He lands on Vehicles.
  const seesDashboard = isHoRole(role)
  const [tab, setTab] = useState<AssetTab>(seesDashboard ? 'dashboard' : 'vehicles')
  const [adding, setAdding] = useState(false)
  // Bumped after a successful create, to force the register underneath to
  // remount and re-read. Cheaper and more reliable than threading a refresh
  // callback down through seven separate components.
  const [bump, setBump] = useState(0)

  const TABS: { key: AssetTab; label: string }[] = [
    ...(seesDashboard ? [{ key: 'dashboard' as AssetTab, label: 'Dashboard' }] : []),
    { key: 'vehicles', label: 'Vehicles' },
    { key: 'machinery', label: 'Machineries & Equipment' },
    { key: 'tools', label: 'Tools & Tackles' },
    { key: 'furniture', label: 'Furniture & Fixture' },
    { key: 'electronics', label: 'Electronic Appliances' },
    { key: 'electrical', label: 'Electrical Appliances' },
    { key: 'operating', label: 'Operating Assets' },
    { key: 'settings', label: 'Settings' },
  ]
  const onCategoryTab = tab !== 'settings' && tab !== 'dashboard'
  return (
    <div className="space-y-4">
      <RegisterTotal key={`total-${bump}`} />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex w-fit flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                      tab === t.key ? 'bg-white text-oval-800 shadow-sm' : 'text-slate-600'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {seesDashboard && <ExportAssetPack />}
          {mayEdit && onCategoryTab && (
            <button className="btn-primary btn-sm" onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" /> New {NEW_ASSET[tab as AssetCategory].noun}
            </button>
          )}
        </div>
      </div>

      <div key={`reg-${bump}`}>
        {tab === 'dashboard' ? <AssetDashboard />
          : tab === 'vehicles' ? <VehicleRegister />
          : tab === 'machinery' ? <MachineryRegister />
          : tab === 'tools' ? <ToolsRegister />
          : tab === 'furniture' ? <FurnitureRegister />
          : tab === 'electronics' ? <ElectronicsRegister />
          : tab === 'electrical' ? <ElectricalRegister />
          : tab === 'operating' ? <OperatingRegister />
          : <DepreciationSettings />}
      </div>

      {adding && onCategoryTab && (
        <NewAssetModal category={tab as AssetCategory}
                       onClose={() => setAdding(false)}
                       onDone={() => { setAdding(false); setBump((b) => b + 1) }} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Moving an asset from one site to another
// ---------------------------------------------------------------------------
// Sits on every row of every register, because that is where the Head of Stores
// already is when he realises something has moved. Three clicks and no typing
// in the common case: press the arrows, choose the site, press Move — the
// quantity defaults to all of it and the date to today.
//
// The button carries its own open state so that adding it to a register costs
// one line rather than three.
// ---------------------------------------------------------------------------
function TransferButton({ category, item, onDone }: {
  category: AssetCategory; item: any; onDone: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <>
      {/* Carries the brand colour and, on a desktop, its own label. As two bare
          grey icons side by side it was there but not findable — the Head of
          Stores looked straight past it, which is the same as it not existing. */}
      <button className="btn-ghost btn-sm whitespace-nowrap font-semibold text-oval-700"
              title="Move some or all of this to another site"
              onClick={() => setOpen(true)}>
        <ArrowLeftRight className="h-4 w-4" />
        <span className="ml-1 hidden lg:inline">Move</span>
      </button>
      {open && (
        <TransferAssetModal category={category} item={item}
                            onClose={() => setOpen(false)}
                            onDone={() => { setOpen(false); onDone() }} />
      )}
    </>
  )
}

function TransferAssetModal({ category, item, onClose, onDone }: {
  category: AssetCategory; item: any; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const { data: sites } = useSites()
  const held = Number(item.quantity || 0)
  const [toSite, setToSite] = useState('')
  // Defaults to everything, so the common case needs no typing at all.
  const [qtyText, setQtyText] = useState(String(held))
  const [when, setWhen] = useState(iso(new Date()))
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)

  const q = Number(qtyText)
  const valid = Number.isFinite(q) && q > 0 && q <= held
  const whole = valid && q === held
  // What the vendor of this arithmetic is: cost moves in proportion, so the
  // buyer can see before he presses what leaves this site's books.
  const share = valid && held > 0 ? q / held : 0
  const costMoving = Math.round(Number(item.opening_wdv || 0) * share * 100) / 100
  const bookMoving = Math.round(Number(item.current_wdv || 0) * share * 100) / 100

  async function move() {
    if (!toSite) { toast.error('Choose the site it is moving to.'); return }
    if (!valid) { toast.error(`Enter a quantity between 1 and ${qty(held)}.`); return }
    setBusy(true)
    const { data, error } = await supabase.rpc('rpc_transfer_asset', {
      p_category: category,
      p_asset_id: item.id,
      p_to_site_id: toSite,
      p_quantity: q,
      p_date: when,
      p_remarks: why.trim() || null,
    })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    const r = data as any
    toast.success(
      r?.was_merged ? `Moved, and folded into ${r.moved_asset_id} which was already there.`
        : r?.was_split ? `${qty(q)} moved. The remainder stays here; the moved part is ${r.moved_asset_id}.`
        : 'Moved.')
    onDone()
  }

  return (
    <Modal open wide onClose={onClose}
           title={`Move ${item.item_name ?? item.machine_name ?? item.tool_name ?? item.vehicle_type ?? item.id}`}
           subtitle={`${qty(held)} ${item.uom && item.uom !== 'NO' ? item.uom : 'held'} at ${item.site_name} · ${rupees(item.opening_wdv)}`}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={move} disabled={busy}>
               {busy ? 'Moving…' : 'Move'}
             </button>
           </>}>
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Moving to" required>
            <Select value={toSite} onChange={(e) => setToSite(e.target.value)}
                    placeholder="Choose the destination site"
                    options={sites.filter((s) => s.id !== item.site_id)
                      .map((s) => ({ value: s.id, label: s.site_name }))} />
          </Field>
          <Field label="How many" required
                 hint={whole ? 'All of it — the line moves across whole.'
                             : `Splitting ${qty(held)}; ${qty(held - q)} stays at ${item.site_name}.`}>
            <Input type="number" step="any" min="0" max={held} value={qtyText}
                   onChange={(e) => setQtyText(e.target.value)} />
          </Field>
          <Field label="Moved on">
            <Input type="date" value={when} onChange={(e) => setWhen(e.target.value)} />
          </Field>
          <Field label="Note">
            <Input value={why} onChange={(e) => setWhy(e.target.value)}
                   placeholder="Optional — why it moved" />
          </Field>
        </div>

        {valid && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
            <div className="flex flex-wrap gap-x-8 gap-y-1">
              <span>Cost moving <strong>{rupees(costMoving)}</strong></span>
              <span className="text-slate-600">Book value <strong>{rupees(bookMoving)}</strong></span>
              <span className="text-slate-600">
                Staying <strong>{rupees(Number(item.opening_wdv || 0) - costMoving)}</strong>
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {whole
                ? 'The whole line moves and keeps its asset number. If an identical line already stands at the destination, the two are folded into one.'
                : `A new line is created at the destination with the next free number. ${qty(held - q)} stays here.`}
              {' '}The company still owns exactly the same — only where it sits changes,
              and the register is refused if that is not true.
            </p>
          </div>
        )}
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Settings — the depreciation categories
// ---------------------------------------------------------------------------
// Agreed with the CA, the CMD and the CFO on 03-09-2026: this one table
// decides what the company charges to the profit and loss account each year.
//
// The rate is never typed. Schedule II gives a useful life; with the 5% residual
// the Act prescribes, the written-down value rate is 1 - 0.05^(1/life). Enter
// ten years and 25.887% follows. The server derives it again on save, so
// nothing this screen sends can put a life and a rate that contradict each
// other onto an asset.
// ---------------------------------------------------------------------------
function DepreciationSettings() {
  const { profile } = useAuth()
  const toast = useToast()
  const mayEdit = can(profile!.role, 'manageAssets')
  const [editing, setEditing] = useState<any>(null)
  const [term, setTerm] = useState('')

  const { data, loading, error, refresh } = useQuery<any>('v_asset_dep_class_usage', () =>
    supabase.from('v_asset_dep_class_usage').select('*').order('label'), [], { idField: 'code' })

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return data
    return data.filter((c: any) =>
      (c.label ?? '').toLowerCase().includes(t) ||
      (c.code ?? '').toLowerCase().includes(t) ||
      (c.schedule_ref ?? '').toLowerCase().includes(t))
  }, [data, term])

  const { sorted, sort, toggle } = useAssetSort(rows, 'label')
  const offFormula = data.filter((c: any) => !c.rate_matches_formula && c.is_active)
  const drifted = data.filter((c: any) => Number(c.lines_off_rate) > 0)
  const covered = data.reduce((a: number, c: any) => a + Number(c.cost || 0), 0)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Depreciation categories" value={`${data.length}`}
              icon={<Boxes className="h-5 w-5" />} />
        <Stat label="In use" value={`${data.filter((c: any) => Number(c.lines) > 0).length}`} />
        <Stat label="Value they govern" value={rupees(covered)} tone="good" />
        <Stat label="Rate does not match its life"
              value={`${offFormula.length}`} tone={offFormula.length ? 'warn' : undefined} />
      </div>

      {drifted.length > 0 && (
        <Alert tone="error">
          <strong>{drifted.length} categor{drifted.length === 1 ? 'y has' : 'ies have'} assets whose
          own rate no longer matches the category.</strong> That should never happen — it means a
          rate was changed without bringing its assets with it. Open the category and save it again
          with "apply to existing assets" ticked.
        </Alert>
      )}

      {offFormula.length > 0 && (
        <Alert tone="warning">
          <strong>{offFormula.length} categor{offFormula.length === 1 ? 'y carries' : 'ies carry'} a
          rate that does not match the Schedule II formula for its life.</strong>{' '}
          {offFormula.map((c: any) => `${c.label} is on ${Number(c.wdv_rate_pct).toFixed(3)}% where ${c.useful_life_years} years gives ${Number(c.formula_rate).toFixed(3)}%`).join('; ')}.
          {' '}These are rounding differences from the original load, not errors of substance.
          Opening and saving a category corrects it — and tells you what it would move first.
        </Alert>
      )}

      <Card
        title="Depreciation categories"
        subtitle="Companies Act 2013 Schedule II Part C. The annual run, on 31 March, charges every asset at the rate of the category it belongs to."
        actions={mayEdit ? (
          <button className="btn-primary btn-sm"
                  onClick={() => setEditing({ __new: true, code: '', label: '', schedule_ref: '', useful_life_years: 10, is_active: true })}>
            <Plus className="h-4 w-4" /> New category
          </button>
        ) : undefined}
      >
        <SearchInput value={term} onChange={setTerm} placeholder="Search category, code, Schedule II reference…" />
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load the categories" message={error} /></div>}
        {rows.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="code" sort={sort} onSort={toggle}>Code</Th>
                  <Th id="label" sort={sort} onSort={toggle}>Category</Th>
                  <Th id="schedule_ref" sort={sort} onSort={toggle}>Companies Act, Schedule II</Th>
                  <Th id="useful_life_years" sort={sort} onSort={toggle} className="num" align="right">Life (yrs)</Th>
                  <Th id="wdv_rate_pct" sort={sort} onSort={toggle} className="num" align="right">WDV rate</Th>
                  <Th id="lines" sort={sort} onSort={toggle} className="num" align="right">Assets</Th>
                  <Th id="cost" sort={sort} onSort={toggle} className="num" align="right">Value governed</Th>
                  <Th id="is_active" sort={sort} onSort={toggle}>In use</Th>
                  {mayEdit && <th />}
                </tr>
              </thead>
              <tbody>
                {sorted.map((c: any) => (
                  <tr key={c.code}>
                    <td className="font-mono text-xs">{c.code}</td>
                    <td className="text-sm font-medium">{c.label}</td>
                    <td className="text-xs text-slate-500">{c.schedule_ref ?? '—'}</td>
                    <td className="num text-xs">{Number(c.useful_life_years)}</td>
                    <td className="num text-xs font-semibold">
                      {Number(c.wdv_rate_pct).toFixed(3)}%
                      {!c.rate_matches_formula && (
                        <span className="ml-1 text-[10px] font-normal text-amber-700"
                              title={`${c.useful_life_years} years gives ${Number(c.formula_rate).toFixed(3)}%`}>
                          ≠{Number(c.formula_rate).toFixed(3)}
                        </span>
                      )}
                    </td>
                    <td className="num text-xs">{Number(c.lines) || <span className="text-slate-300">—</span>}</td>
                    <td className="num text-xs">
                      {Number(c.cost) ? rupees(c.cost) : <span className="text-slate-300">—</span>}
                    </td>
                    <td>
                      <span className={c.is_active ? 'pill-green' : 'pill-grey'}>
                        {c.is_active ? 'Active' : 'Retired'}
                      </span>
                    </td>
                    {mayEdit && (
                      <td>
                        <button className="btn-ghost btn-sm" onClick={() => setEditing({ ...c })}>
                          <Pencil className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="text-xs text-slate-400">
        Changing a rate here changes what every asset on that category depreciates at, from the next
        annual run onwards. It never touches the audited value as at 31.08.2026, which is fixed.
        Every change is recorded in the audit trail with your name, the old figures and the new.
      </p>

      {editing && (
        <DepClassForm item={editing} onClose={() => setEditing(null)}
                      onDone={() => { setEditing(null); refresh(); toast.success('Saved.') }} />
      )}
    </div>
  )
}

function DepClassForm({ item, onClose, onDone }: {
  item: any; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const isNew = !!item.__new
  const [f, setF] = useState<any>({
    code: item.code ?? '',
    label: item.label ?? '',
    schedule_ref: item.schedule_ref ?? '',
    useful_life_years: String(item.useful_life_years ?? 10),
    is_active: item.is_active !== false,
  })
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState<any>(null)
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }))

  // The rate is never typed. This mirrors fn_wdv_rate_for_life on the server,
  // which derives it again on save — so the screen can only ever preview it.
  const life = Number(f.useful_life_years)
  const rate = Number.isFinite(life) && life >= 1 && life <= 60
    ? Math.round((1 - Math.pow(0.05, 1 / life)) * 100 * 1000) / 1000
    : null
  const changesRate = !isNew && rate !== null
    && Math.abs(rate - Number(item.wdv_rate_pct)) > 0.001
  const hasAssets = Number(item.lines ?? 0) > 0

  async function askThenSave() {
    if (!f.label.trim()) { toast.error('The category needs a name.'); return }
    if (rate === null) { toast.error('The useful life must be between 1 and 60 years.'); return }
    if (isNew && !/^[A-Z][A-Z0-9_]{1,15}$/.test(f.code.trim().toUpperCase())) {
      toast.error('The code must be 2 to 16 characters, start with a letter, and use only capitals, digits and underscore.')
      return
    }
    // Anything that moves a rate on assets that already exist gets shown first.
    if (changesRate && hasAssets && !preview) {
      setBusy(true)
      const { data, error } = await supabase.rpc('rpc_preview_dep_class',
        { p_code: item.code, p_life: life })
      setBusy(false)
      if (error) { toast.error(error.message); return }
      setPreview(data)
      return
    }
    await save()
  }

  async function save() {
    setBusy(true)
    const { data, error } = await supabase.rpc('rpc_save_dep_class', {
      p_code: (isNew ? f.code : item.code).trim().toUpperCase(),
      p_label: f.label.trim(),
      p_schedule_ref: f.schedule_ref.trim() || null,
      p_life: life,
      p_is_active: f.is_active,
      p_apply_to_existing: true,
    })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    const n = Number((data as any)?.lines_updated ?? 0)
    if (n > 0) toast.success(`${n} asset line${n === 1 ? '' : 's'} moved to the new rate.`)
    onDone()
  }

  return (
    <Modal open wide onClose={onClose}
           title={isNew ? 'New depreciation category' : `${item.label} — ${item.code}`}
           subtitle={isNew
             ? 'Enter the useful life from Schedule II. The rate follows from it.'
             : `${Number(item.lines ?? 0)} asset(s) on this category, worth ${rupees(item.cost ?? 0)}`}
           footer={<>
             <button className="btn-secondary" onClick={preview ? () => setPreview(null) : onClose}
                     disabled={busy}>
               {preview ? 'Back' : 'Cancel'}
             </button>
             <button className="btn-primary" onClick={preview ? save : askThenSave} disabled={busy}>
               {busy ? 'Working…' : preview ? 'Yes, change it' : 'Save'}
             </button>
           </>}>
      {preview ? (
        <div className="space-y-3">
          <Alert tone="warning">
            <strong>This changes what {Number(preview.lines)} asset
            {Number(preview.lines) === 1 ? '' : 's'} depreciate at.</strong> They are worth{' '}
            {rupees(preview.cost)} between them.
          </Alert>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Now</p>
              <p className="mt-1 text-lg font-semibold text-slate-800">
                {Number(preview.old_rate).toFixed(3)}%
              </p>
              <p className="text-xs text-slate-500">over {Number(preview.old_life)} years</p>
            </div>
            <div className="rounded-lg border border-amber-200 border-l-4 border-l-amber-600 bg-amber-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">After</p>
              <p className="mt-1 text-lg font-semibold text-amber-800">
                {Number(preview.new_rate).toFixed(3)}%
              </p>
              <p className="text-xs text-amber-700">over {Number(preview.new_life)} years</p>
            </div>
          </div>
          {(preview.by_category ?? []).length > 0 && (
            <div className="table-wrap mx-0">
              <table className="tbl">
                <thead><tr><th>Register</th><th className="num">Lines</th><th className="num">Value</th></tr></thead>
                <tbody>
                  {(preview.by_category ?? []).map((b: any) => (
                    <tr key={b.category}>
                      <td className="text-sm">{b.category}</td>
                      <td className="num text-xs">{Number(b.lines)}</td>
                      <td className="num text-xs">{rupees(b.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-slate-500">
            The audited value as at 31.08.2026 does not move. Only the rate at which these assets
            depreciate from the next annual run onwards changes. Your name, the old rate and the new
            one all go into the audit trail.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <Alert tone="info">
            The rate is not typed. Schedule II gives a useful life, and with the 5% residual the Act
            prescribes the written down value rate is <strong>1 − 0.05^(1/life)</strong>. The server
            works it out again when you save, so a life and a rate can never contradict each other.
          </Alert>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Code" required={isNew}
                   hint={isNew
                     ? 'Short, capitals, no spaces. Cannot be changed later — every asset points at it.'
                     : 'Cannot be changed. Every asset on this category points at it.'}>
              <Input value={f.code} disabled={!isNew}
                     placeholder="e.g. SOLAR"
                     onChange={(e) => set('code', e.target.value.toUpperCase())} />
            </Field>
            <Field label="Category name" required>
              <Input value={f.label} placeholder="e.g. Solar Power Equipment"
                     onChange={(e) => set('label', e.target.value)} />
            </Field>
            <Field label="Companies Act 2013, Schedule II reference" className="sm:col-span-2"
                   hint="Where in the Act this sits. Your auditor will look for it.">
              <Input value={f.schedule_ref}
                     placeholder="e.g. Part C 4(ii)(e) Plant and machinery used in civil construction - others"
                     onChange={(e) => set('schedule_ref', e.target.value)} />
            </Field>
            <Field label="Useful life, in years" required hint="Between 1 and 60.">
              <Input type="number" step="1" min="1" max="60" value={f.useful_life_years}
                     onChange={(e) => set('useful_life_years', e.target.value)} />
            </Field>
            <Field label="Written down value rate" hint="Calculated. Not editable.">
              <Input value={rate === null ? '—' : `${rate.toFixed(3)} %`} disabled />
            </Field>
            {!isNew && (
              <Field label="In use"
                     hint={hasAssets
                       ? `Cannot be retired — ${Number(item.lines)} asset(s) are on it.`
                       : 'A retired category cannot be chosen for new assets.'}>
                <Select value={f.is_active ? 'yes' : 'no'} disabled={hasAssets}
                        onChange={(e) => set('is_active', e.target.value === 'yes')}
                        options={[{ value: 'yes', label: 'Active' }, { value: 'no', label: 'Retired' }]} />
              </Field>
            )}
          </div>
          {changesRate && hasAssets && (
            <Alert tone="warning">
              This would move <strong>{Number(item.lines)} asset
              {Number(item.lines) === 1 ? '' : 's'}</strong> from{' '}
              {Number(item.wdv_rate_pct).toFixed(3)}% to {rate?.toFixed(3)}%. Pressing Save shows you
              exactly what it touches before anything changes.
            </Alert>
          )}
        </div>
      )}
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// One button, the whole register: summary sheet plus one sheet per category
// ---------------------------------------------------------------------------
function ExportAssetPack() {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  return (
    <button className="btn-secondary btn-sm" disabled={busy} onClick={async () => {
      setBusy(true)
      try {
        // export-asset-pack builds the formatted workbook with ExcelJS.
        // export-report still carries a plain SheetJS version of the same pack
        // under report: 'fixed_assets_pack' — swap the two lines below if the
        // formatted one ever stops working, and the download keeps going out.
        await downloadFromFunction('export-asset-pack', {},
          'OVAL_Fixed_Asset_Register.xlsx')
      } catch (e: any) {
        toast.error(e?.message ?? 'The export could not be built.')
      } finally {
        setBusy(false)
      }
    }}>
      <Download className="h-4 w-4" /> {busy ? 'Building…' : 'Export to Excel'}
    </button>
  )
}

// ---------------------------------------------------------------------------
// The Fixed Assets dashboard
// ---------------------------------------------------------------------------
// Four charts, chosen by the board on 03-09-2026. Everything is computed in
// one server call — rpc_asset_dashboard — so nothing here can disagree with
// the registers underneath, and a site user cannot pull a company-wide figure.
// ---------------------------------------------------------------------------
// Branding 30-09-2026: the shared on-brand series (src/lib/theme.ts).
const CHART_COLOURS = CHART_SERIES

function AssetDashboard() {
  const [d, setD] = useState<any>(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(true)

  // Every filter goes to the server, and the server sends back the charts AND
  // the register over the same filtered set. That is why the total on the last
  // line always equals the card at the top: it is the same arithmetic.
  const [fSite, setFSite] = useState('')
  const [fCat, setFCat] = useState('')
  const [fCls, setFCls] = useState('')
  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  const [groupBy, setGroupBy] = useState<'none' | 'category' | 'site_name' | 'dep_class_label'>('none')

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 300)
    return () => clearTimeout(t)
  }, [term])

  useEffect(() => {
    let alive = true
    setBusy(true)
    supabase.rpc('rpc_asset_dashboard', {
      p_site_id: fSite || null,
      p_category: fCat || null,
      p_dep_class_code: fCls || null,
      p_search: debounced || null,
    }).then(({ data, error }) => {
      if (!alive) return
      setBusy(false)
      if (error) setErr(error.message)
      else { setErr(''); setD(data) }
    })
    return () => { alive = false }
  }, [fSite, fCat, fCls, debounced])

  if (err) return <Card><EmptyState title="Could not build the dashboard" message={err} /></Card>
  if (!d) return <Loading />

  const t = d.totals ?? {}
  const cat = (d.by_category ?? []).map((c: any) => ({
    name: String(c.category).replace(' & Equipment', '').replace(' Appliances', ''),
    full: c.category, cost: Number(c.cost), book: Number(c.book), lines: Number(c.lines),
  }))
  const totalCost = cat.reduce((a: number, c: any) => a + c.cost, 0) || 1

  const sitesAll = (d.by_site ?? []).map((s: any) => ({
    name: String(s.site), cost: Number(s.cost), book: Number(s.book), lines: Number(s.lines),
  }))
  const top = sitesAll.slice(0, 10)
  const restCost = sitesAll.slice(10).reduce((a: number, s: any) => a + s.cost, 0)
  const sites = restCost > 0
    ? [...top, { name: `Other ${sitesAll.length - 10} sites`, cost: restCost, book: 0, lines: 0 }]
    : top

  const fc = (d.forecast ?? []).map((p: any) => ({ name: String(p.label), book: Number(p.book) }))
  const last = fc.length ? fc[fc.length - 1].book : 0
  const first = fc.length ? fc[0].book : 0

  const STATUS_TONE: Record<string, string> = {
    Active: '#15803D', Idle: '#71807C', 'Under Repair': '#806A45',
    Breakdown: '#C2414A', Disposed: '#9AA6A2',
  }
  const st = (d.by_status ?? []).map((s: any) => ({
    name: String(s.status), cost: Number(s.cost), lines: Number(s.lines),
    fill: STATUS_TONE[String(s.status)] ?? '#9AA6A2',
  }))
  const activeCost = st.find((s: any) => s.name === 'Active')?.cost ?? 0
  const notEarning = st.filter((s: any) => s.name !== 'Active')
  const notEarningCost = notEarning.reduce((a: number, s: any) => a + s.cost, 0)
  const notEarningLines = notEarning.reduce((a: number, s: any) => a + s.lines, 0)

  const filtered = !!d.filters?.any

  // Money on this page has to read sensibly at ₹13.59 crore AND at ₹4.25 lakh,
  // because the filters can take it from one to the other in a click. Fixing
  // the scale at crore made every axis read "0.0Cr" the moment anything was
  // filtered, which made four working charts look broken.
  const inWords = (n: number) => {
    const a = Math.abs(n)
    if (a >= 10000000) return `₹${(n / 10000000).toFixed(2)} crore`
    if (a >= 100000) return `₹${(n / 100000).toFixed(2)} lakh`
    return rupees(n)
  }
  const axisMoney = (n: number) => {
    const a = Math.abs(n)
    if (a >= 10000000) return `${(n / 10000000).toFixed(1)}Cr`
    if (a >= 100000) return `${(n / 100000).toFixed(1)}L`
    if (a >= 1000) return `${Math.round(n / 1000)}K`
    return String(Math.round(n))
  }
  // "of the register" is only true when nothing is filtered. Saying it while a
  // filter is on turns a correct chart into a false statement.
  const ofWhat = filtered ? 'of the selection' : 'of the register'
  const box = 'rounded-lg border border-slate-200 bg-white p-4'

  const opt = d.options ?? {}
  const clearAll = () => { setFSite(''); setFCat(''); setFCls(''); setTerm('') }

  return (
    <div className="space-y-4">
      {/* --- the filters drive everything below them --------------------- */}
      <Card bodyClass="py-3">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <SearchInput value={term} onChange={setTerm}
                       placeholder="Search asset, make, registration, site…" />
          <Select value={fSite} onChange={(e) => setFSite(e.target.value)} placeholder="All sites"
                  options={(opt.sites ?? []).map((s: any) => ({ value: s.id, label: s.name }))} />
          <Select value={fCat} onChange={(e) => setFCat(e.target.value)} placeholder="All categories"
                  options={(opt.categories ?? []).map((c: any) => ({ value: c.value, label: c.label }))} />
          <Select value={fCls} onChange={(e) => setFCls(e.target.value)} placeholder="All depreciation classes"
                  options={(opt.classes ?? []).map((c: any) => ({ value: c.value, label: c.label }))} />
        </div>
        {filtered && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span className="pill-amber">Filtered</span>
            <span className="text-slate-600">
              Every figure and chart on this page is for the {Number(t.lines ?? 0)} line
              {Number(t.lines) === 1 ? '' : 's'} you have selected — not the company position.
            </span>
            <button className="font-semibold text-oval-700 underline" onClick={clearAll}>
              Show everything
            </button>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label={filtered ? 'Selected' : 'On the register'}
              value={`${t.lines ?? 0} lines`} icon={<Boxes className="h-5 w-5" />} />
        <Stat label="Cost" value={rupees(t.cost)} />
        <Stat label="Book value today" value={rupees(t.book)} tone="good" />
        <Stat label="Depreciation charged" value={rupees(t.depreciation)} tone="warn" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* 1 ----------------------------------------------------------- */}
        <div className={box}>
          <h3 className="text-sm font-semibold text-slate-800">
            Where the {inWords(Number(t.cost ?? 0))} sits
          </h3>
          <p className="mb-2 text-xs text-slate-500">
            Cost by category. {cat.length === 0
              ? 'Nothing matches these filters.'
              : cat.length === 1
                ? <>All of it is {cat[0].full}.</>
                : <>{cat[0].full} alone is {Math.round((cat[0].cost / totalCost) * 100)}% {ofWhat},
                    across {cat.length} categories.</>}
          </p>
          {/* The key goes UNDERNEATH (12-09-2026) - see components/charts/ChartLegend. */}
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={cat} dataKey="cost" nameKey="full" innerRadius="52%" outerRadius="86%"
                   paddingAngle={2}>
                {cat.map((_: any, i: number) => (
                  <Cell key={i} fill={CHART_COLOURS[i % CHART_COLOURS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v: any) => rupees(Number(v))} />
            </PieChart>
          </ResponsiveContainer>
          <ChartLegend items={cat.map((c: any, i: number) => ({
            name: c.full, color: CHART_COLOURS[i % CHART_COLOURS.length], value: rupees(Number(c.cost)),
          }))} />
        </div>

        {/* 2 ----------------------------------------------------------- */}
        <div className={box}>
          <h3 className="text-sm font-semibold text-slate-800">Five-year book value forecast</h3>
          <p className="mb-2 text-xs text-slate-500">
            Companies Act Schedule II, at the rate on each line. Falls from {inWords(first)} to{' '}
            {inWords(last)} by 2031{first > 0 && <> — a fall of {Math.round(((first - last) / first) * 100)}%</>}.
            Assumes no additions or disposals.
          </p>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={fc} margin={{ top: 6, right: 8, left: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="fcFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3C55A5" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#3C55A5" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#DFE6E3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }}
                     tickFormatter={axisMoney} />
              <Tooltip formatter={(v: any) => rupees(Number(v))} />
              <Area type="monotone" dataKey="book" stroke="#293B78" strokeWidth={2}
                    fill="url(#fcFill)" name="Book value" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* 3 ----------------------------------------------------------- */}
        <div className={box}>
          <h3 className="text-sm font-semibold text-slate-800">Where it is deployed</h3>
          <p className="mb-2 text-xs text-slate-500">
            Cost by site{sitesAll.length > 10 ? ', largest ten' : ''}. {sites.length === 0
              ? 'Nothing matches these filters.'
              : sites.length === 1
                ? <>All of it is at {sites[0].name}.</>
                : <>{sites[0].name} holds {Math.round((sites[0].cost / totalCost) * 100)}% {ofWhat},
                    spread over {sitesAll.length} site{sitesAll.length === 1 ? '' : 's'}.</>}
          </p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={sites} layout="vertical" margin={{ top: 4, right: 18, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DFE6E3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }}
                     tickFormatter={axisMoney} />
              <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v: any) => rupees(Number(v))} />
              <Bar dataKey="cost" name="Cost" fill="#3C55A5" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 4 ----------------------------------------------------------- */}
        <div className={box}>
          <h3 className="text-sm font-semibold text-slate-800">Fleet readiness</h3>
          <p className="mb-2 text-xs text-slate-500">
            {notEarningLines === 0
              ? filtered ? 'Every line in this selection is active.' : 'Every line on the register is active.'
              : <>{rupees(notEarningCost)} across {notEarningLines} line
                  {notEarningLines === 1 ? '' : 's'} is not earning today —{' '}
                  {((notEarningCost / totalCost) * 100).toFixed(1)}% {ofWhat}.</>}
          </p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={st} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DFE6E3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }}
                     tickFormatter={axisMoney} />
              <Tooltip formatter={(v: any, _n: any, p: any) =>
                [rupees(Number(v)), `${p?.payload?.lines ?? 0} lines`]} />
              <Bar dataKey="cost" name="Cost" radius={[3, 3, 0, 0]}>
                {st.map((s: any, i: number) => <Cell key={i} fill={s.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="mt-1 text-xs text-slate-500">
            Active assets carry {rupees(activeCost)} of cost.
          </p>
        </div>
      </div>

      <AssetRegisterList lines={d.lines ?? []} totals={t} busy={busy}
                         groupBy={groupBy} setGroupBy={setGroupBy} filtered={filtered} />

      <p className="text-xs text-slate-400">
        {filtered ? 'Filtered view' : 'Company-wide, every site'}, as at {ddmmyyyy(d.as_at)}.
        The charts, the cards and the register below all come from a single server call over
        one set of assets, so the total on the last line is the same arithmetic as the card
        at the top. Disposed assets are excluded throughout.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The register itself, beneath the charts
// ---------------------------------------------------------------------------
// Every matching line, sortable, optionally grouped with subtotals, and a
// total pinned to the bottom of the panel so it stays visible while you
// scroll. The total is of everything selected, not of what happens to be on
// screen — which is the usual way a figure like this misleads people.
// ---------------------------------------------------------------------------
function AssetRegisterList({ lines, totals, busy, groupBy, setGroupBy, filtered }: {
  lines: any[]; totals: any; busy: boolean; filtered: boolean
  groupBy: 'none' | 'category' | 'site_name' | 'dep_class_label'
  setGroupBy: (g: 'none' | 'category' | 'site_name' | 'dep_class_label') => void
}) {
  const { sorted, sort, toggle } = useAssetSort(lines, 'id')

  // Grouped: keep the chosen sort inside each group, and order the groups by
  // value — the largest block of money first, which is what a director asks
  // about. A subtotal row closes each group.
  const blocks = useMemo(() => {
    if (groupBy === 'none') return [{ key: '', rows: sorted }]
    const map = new Map<string, any[]>()
    for (const r of sorted) {
      const k = String(r[groupBy] ?? '—')
      if (!map.has(k)) map.set(k, [])
      map.get(k)!.push(r)
    }
    return [...map.entries()]
      .map(([key, rows]) => ({ key, rows }))
      .sort((a, b) =>
        b.rows.reduce((x: number, r: any) => x + Number(r.opening_wdv || 0), 0)
        - a.rows.reduce((x: number, r: any) => x + Number(r.opening_wdv || 0), 0))
  }, [sorted, groupBy])

  const sum = (rows: any[], k: string) =>
    rows.reduce((a: number, r: any) => a + Number(r[k] || 0), 0)

  const COLS = 12
  const GROUPS: Array<[typeof groupBy, string]> = [
    ['none', 'No grouping'], ['category', 'Asset category'],
    ['site_name', 'Site'], ['dep_class_label', 'Depreciation class'],
  ]

  return (
    <Card
      title="The register"
      subtitle={filtered
        ? 'Every asset matching the filters above, from all seven categories.'
        : 'Every asset on the register, from all seven categories.'}
      bodyClass="p-0 sm:p-0"
      actions={
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Group by</span>
          <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value as any)}
                  options={GROUPS.map(([v, l]) => ({ value: v, label: l }))} />
        </div>
      }
    >
      {busy && <Loading />}
      {!busy && lines.length === 0 && (
        <EmptyState icon={<Boxes className="h-9 w-9" />}
                    title="No assets match these filters"
                    message="Widen the search, or press “Show everything” above." />
      )}
      {!busy && lines.length > 0 && (
        <>
          <div className="table-wrap mx-0 max-h-[560px] overflow-y-auto">
            <table className="tbl">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_#DFE6E3]">
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Asset ID</Th>
                  <Th id="item_name" sort={sort} onSort={toggle}>Asset</Th>
                  <Th id="category" sort={sort} onSort={toggle}>Category</Th>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="quantity" sort={sort} onSort={toggle} className="num" align="right">Qty</Th>
                  <Th id="uom" sort={sort} onSort={toggle}>Unit</Th>
                  <Th id="opening_wdv" sort={sort} onSort={toggle} className="num" align="right">Cost</Th>
                  <Th id="depreciation_to_date" sort={sort} onSort={toggle} className="num" align="right">Depreciation</Th>
                  <Th id="current_wdv" sort={sort} onSort={toggle} className="num" align="right">Book value</Th>
                  <Th id="dep_class_label" sort={sort} onSort={toggle}>Depreciation class</Th>
                  <Th id="wdv_rate_pct" sort={sort} onSort={toggle} className="num" align="right">Rate</Th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                </tr>
              </thead>
              <tbody>
                {blocks.map((b) => (
                  <Fragment key={b.key || 'all'}>
                    {groupBy !== 'none' && (
                      <tr className="bg-slate-100">
                        <td colSpan={COLS} className="py-1.5 text-xs font-semibold text-slate-700">
                          {b.key}
                          <span className="ml-2 font-normal text-slate-500">
                            {b.rows.length} line{b.rows.length === 1 ? '' : 's'}
                          </span>
                        </td>
                      </tr>
                    )}
                    {b.rows.map((x: any) => (
                      <tr key={x.id}>
                        <td className="font-mono text-xs">{x.id}</td>
                        <td className="text-sm font-medium">{x.item_name}</td>
                        <td className="text-xs text-slate-500">{x.category}</td>
                        <td className="text-xs">{x.site_name}</td>
                        <td className="num text-xs">{qty(x.quantity)}</td>
                        <td className="text-xs">
                          {x.uom === 'NO'
                            ? <span className="text-slate-400">nos</span>
                            : <span className={x.uom === 'LOT' ? 'pill-red' : 'pill-amber'}>{x.uom}</span>}
                        </td>
                        <td className="num text-xs">{rupees(x.opening_wdv)}</td>
                        <td className="num text-xs text-amber-700">{rupees(x.depreciation_to_date)}</td>
                        <td className="num text-xs font-semibold">{rupees(x.current_wdv)}</td>
                        <td className="text-xs text-slate-500">{x.dep_class_label}</td>
                        <td className="num text-xs">{Number(x.wdv_rate_pct).toFixed(3)}%</td>
                        <td>
                          <span className={x.status === 'Active' ? 'pill-green'
                            : x.status === 'Breakdown' ? 'pill-amber' : 'pill-grey'}>{x.status}</span>
                        </td>
                      </tr>
                    ))}
                    {groupBy !== 'none' && (
                      <tr className="bg-slate-50 font-semibold">
                        <td colSpan={4} className="text-xs text-slate-700">{b.key} — subtotal</td>
                        <td className="num text-xs">{qty(sum(b.rows, 'quantity'))}</td>
                        <td />
                        <td className="num text-xs">{rupees(sum(b.rows, 'opening_wdv'))}</td>
                        <td className="num text-xs text-amber-700">{rupees(sum(b.rows, 'depreciation_to_date'))}</td>
                        <td className="num text-xs">{rupees(sum(b.rows, 'current_wdv'))}</td>
                        <td colSpan={3} />
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* The total stays put while you scroll, and it is the total of
              everything selected — never of what happens to be visible. */}
          <div className="sticky bottom-[calc(3.5rem+env(safe-area-inset-bottom))] lg:bottom-0 border-t-2 border-oval-800 bg-oval-50">
            <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-1 px-4 py-2.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-oval-800">
                Total · {Number(totals.lines ?? 0)} line{Number(totals.lines) === 1 ? '' : 's'}
                {' · '}{qty(totals.units ?? 0)} units
              </div>
              <div className="flex flex-wrap items-baseline gap-x-8 gap-y-1 text-sm">
                <span className="text-slate-600">
                  Cost <strong className="text-slate-900">{rupees(totals.cost)}</strong>
                </span>
                <span className="text-slate-600">
                  Depreciation <strong className="text-amber-700">{rupees(totals.depreciation)}</strong>
                </span>
                <span className="text-slate-600">
                  Book value <strong className="text-emerald-800">{rupees(totals.book)}</strong>
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Add a new asset to any of the seven registers
// ---------------------------------------------------------------------------
// One form, seven categories. It sends the category and a payload to
// rpc_create_asset and lets the database do the deciding: the asset number, the
// depreciation rate and life, the 5% residual and the audit entry are all set
// on the server. Nothing this screen sends can put a wrong rate on a line.
// ---------------------------------------------------------------------------
function NewAssetModal({ category, onClose, onDone }: {
  category: AssetCategory; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const cfg = NEW_ASSET[category]
  const { data: sites } = useSites()
  const { data: classes } = useQuery<any>('asset_dep_classes', () =>
    supabase.from('asset_dep_classes').select('*').eq('is_active', true).order('label'))

  const [nextId, setNextId] = useState('')
  const [busy, setBusy] = useState(false)
  const [f, setF] = useState<any>({
    site_id: '', item_name: '', make_model: '', registration_no: '', serial_no: '',
    quantity: '1', uom: 'NO', rate_per_unit: '', opening_wdv: '',
    in_service_date: iso(new Date()), dep_class_code: '', status: 'Active', remarks: '',
  })
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }))

  useEffect(() => {
    let alive = true
    supabase.rpc('fn_next_asset_id', { p_category: category }).then(({ data }) => {
      if (alive && typeof data === 'string') setNextId(data)
    })
    return () => { alive = false }
  }, [category])

  const q = Number(f.quantity)
  const r = Number(f.rate_per_unit)
  const cost = cfg.hasRate
    ? (Number.isFinite(q) && Number.isFinite(r) ? Math.round(q * r * 100) / 100 : 0)
    : Number(f.opening_wdv) || 0
  const chosen = classes.find((c: any) => c.code === f.dep_class_code)

  async function save() {
    if (!f.site_id) { toast.error('Choose a site.'); return }
    if (!f.item_name.trim()) { toast.error(`${cfg.nameLabel} is needed.`); return }
    if (!Number.isFinite(q) || q <= 0) { toast.error('The quantity must be more than nought.'); return }
    if (!f.dep_class_code) { toast.error('Choose a depreciation category.'); return }
    if (!f.in_service_date) { toast.error('An in-service date is needed — depreciation runs from that day.'); return }
    if (f.in_service_date <= '2026-08-31') {
      toast.error('An asset in service on or before 31.08.2026 belongs in the audited opening balance. Speak to the System Administrator.')
      return
    }
    if (!(cost > 0)) { toast.error('The purchase cost must be more than nought.'); return }

    setBusy(true)
    const { data, error } = await supabase.rpc('rpc_create_asset', {
      p_category: category,
      p_payload: {
        site_id: f.site_id,
        item_name: f.item_name.trim(),
        make_model: f.make_model.trim() || null,
        registration_no: cfg.hasReg ? (f.registration_no.trim() || null) : null,
        serial_no: cfg.hasSerial ? (f.serial_no.trim() || null) : null,
        quantity: q,
        uom: cfg.hasUom ? f.uom : 'NO',
        rate_per_unit: cfg.hasRate ? r : null,
        opening_wdv: cfg.hasRate ? null : cost,
        in_service_date: f.in_service_date,
        dep_class_code: f.dep_class_code,
        status: f.status,
        remarks: f.remarks.trim() || null,
      },
    })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success(`${data} added to the register.`)
    onDone()
  }

  const UOMS = ['NO', 'SHEET', 'PAIR', 'SET', 'MTR', 'COIL', 'LOT']

  return (
    <Modal open wide onClose={onClose}
           title={`New ${cfg.noun}`}
           subtitle={nextId
             ? `It will be numbered ${nextId}. Bought after 31.08.2026, so it sits outside the audited opening balance.`
             : 'Bought after 31.08.2026, so it sits outside the audited opening balance.'}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>
               {busy ? 'Adding…' : 'Add to the register'}
             </button>
           </>}>
      <div className="space-y-4">
        <Alert tone="info">
          This is for something bought <strong>after 31.08.2026</strong>. The audited
          valuation of ₹13,59,17,702 is untouched by anything added here — it is
          reported separately, so the reconciliation to your CA's figure keeps working.
          Depreciation runs from the in-service date, pro-rata for the days it was
          actually in use.
        </Alert>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Site" required>
            <Select value={f.site_id} onChange={(e) => set('site_id', e.target.value)}
                    placeholder="Choose a site"
                    options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
          </Field>
          <Field label={cfg.nameLabel} required>
            <Input value={f.item_name} placeholder={cfg.namePlaceholder}
                   onChange={(e) => set('item_name', e.target.value)} />
          </Field>

          <Field label="Make / model">
            <Input value={f.make_model} onChange={(e) => set('make_model', e.target.value)} />
          </Field>
          {cfg.hasReg && (
            <Field label="Registration number">
              <Input value={f.registration_no} onChange={(e) => set('registration_no', e.target.value)} />
            </Field>
          )}
          {cfg.hasSerial && (
            <Field label="Serial number">
              <Input value={f.serial_no} onChange={(e) => set('serial_no', e.target.value)} />
            </Field>
          )}

          <Field label="Quantity" required>
            <Input type="number" step="any" value={f.quantity}
                   onChange={(e) => set('quantity', e.target.value)} />
          </Field>
          {cfg.hasUom && (
            <Field label="Unit" required
                   hint="How it is counted. Use MTR for cable and pipe, PAIR for footwear.">
              <Select value={f.uom} onChange={(e) => set('uom', e.target.value)}
                      options={UOMS.map((u) => ({ value: u, label: u === 'NO' ? 'NO (pieces)' : u }))} />
            </Field>
          )}

          {cfg.hasRate ? (
            <Field label="Rate per unit (₹)" required
                   hint="Purchase cost of one. The line value is quantity × rate.">
              <Input type="number" step="any" value={f.rate_per_unit}
                     onChange={(e) => set('rate_per_unit', e.target.value)} />
            </Field>
          ) : (
            <Field label="Purchase cost (₹)" required hint="What the whole line cost.">
              <Input type="number" step="any" value={f.opening_wdv}
                     onChange={(e) => set('opening_wdv', e.target.value)} />
            </Field>
          )}

          <Field label="In service from" required
                 hint="Depreciation is charged from this date, not from the start of the year.">
            <Input type="date" value={f.in_service_date}
                   onChange={(e) => set('in_service_date', e.target.value)} />
          </Field>

          <Field label="Depreciation category" required className="sm:col-span-2"
                 hint="Companies Act 2013 Schedule II. The rate and life come from this — they cannot be typed in.">
            <Select value={f.dep_class_code} onChange={(e) => set('dep_class_code', e.target.value)}
                    placeholder="Choose a category"
                    options={classes.map((c: any) => ({
                      value: c.code,
                      label: `${c.label} — ${c.useful_life_years} yr — ${Number(c.wdv_rate_pct).toFixed(3)}%`,
                    }))} />
          </Field>

          <Field label="Status">
            <Select value={f.status} onChange={(e) => set('status', e.target.value)}
                    options={['Active', 'Idle', 'Under Repair', 'Breakdown']
                      .map((s) => ({ value: s, label: s }))} />
          </Field>
          <Field label="Remarks">
            <Input value={f.remarks} onChange={(e) => set('remarks', e.target.value)} />
          </Field>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
          <div className="flex flex-wrap gap-x-8 gap-y-1">
            <span>Cost going on the register <strong>{rupees(cost)}</strong></span>
            <span className="text-slate-600">Residual at 5% <strong>{rupees(Math.round(cost * 5) / 100)}</strong></span>
            {chosen && (
              <span className="text-slate-600">
                Depreciates at <strong>{Number(chosen.wdv_rate_pct).toFixed(3)}%</strong> over{' '}
                <strong>{chosen.useful_life_years} years</strong>
              </span>
            )}
          </div>
          {chosen?.schedule_ref && (
            <p className="mt-1 text-xs text-slate-500">Schedule II {chosen.schedule_ref}</p>
          )}
        </div>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// The whole fixed asset register, in one line, on every tab
// ---------------------------------------------------------------------------
// The audited figure as at 31.08.2026 is Rs 13,59,17,702 — the CA-approved
// total the board resolved to keep intact. This shows the register against it,
// live, so that agreeing with the audit is something you can see rather than
// something you have to take on trust. It stays meaningful after the April
// 2027 depreciation run: the audited column will still read 13,59,17,702 and
// the book value will have fallen.
// ---------------------------------------------------------------------------
const AUDITED_REGISTER_TOTAL = 135917702

// ---------------------------------------------------------------------------
// Sorting the seven asset registers
// ---------------------------------------------------------------------------
// One map for all seven. Any column not named here sorts as text, which is the
// right answer for names, sites, categories, units and statuses. What matters
// is that money sorts as money and dates as dates: sorting "1,07,610" next to
// "63,000" as text puts the smaller number first, and sorting "01-04-2027"
// next to "16-09-2025" as text puts the later date first. Both are wrong in a
// way nobody notices until an auditor does.
//
// Blanks sink to the bottom whichever way the column points — that is
// sortRows' own behaviour, and it is correct: a machine with no in-service
// date is not the oldest one, it is simply unrecorded.
const ASSET_SORT_KINDS = {
  quantity:               'number',
  rate_per_unit:          'number',
  opening_wdv:            'number',
  current_wdv:            'number',
  depreciation_to_date:   'number',
  cumulative_depreciation:'number',
  residual_value:         'number',
  wdv_rate_pct:           'number',
  useful_life_years:      'number',
  in_service_date:        'date',
  insurance_expiry:       'date',
  fitness_expiry:         'date',
  permit_expiry:          'date',
  puc_expiry:             'date',
  calibration_due_date:   'date',
  warranty_expiry:        'date',
  amc_expiry:             'date',
  last_service_date:      'date',
  next_service_due:       'date',
  last_depreciation_date: 'date',
  // the original "Other assets" register names its columns differently
  purchase_value:         'number',
  current_book_value:     'number',
  purchase_date:          'date',
  // the Settings tab, where the columns are counts and totals per category
  lines:                  'number',
  cost:                   'number',
  book:                   'number',
  formula_rate:           'number',
} as const

/**
 * Sort state plus the sorted rows, for any of the asset registers.
 * initialKey must be a real property on the rows, or sortRows warns in dev and
 * the table refuses to move — which is how a broken sort column ships unnoticed.
 */
function useAssetSort(rows: any[], initialKey = 'id') {
  const { sort, toggle } = useSort(initialKey, 'asc')
  const sorted = useMemo(() => sortRows(rows, sort, ASSET_SORT_KINDS), [rows, sort])
  return { sorted, sort, toggle }
}

function RegisterTotal() {
  const { data, loading } = useQuery<any>('v_asset_register_total', () =>
    supabase.from('v_asset_register_total').select('*').order('sort_order'))

  if (loading || data.length === 0) return null

  const sum = (k: string) => data.reduce((a: number, r: any) => a + Number(r[k] || 0), 0)
  const audited = sum('audited_value_31_08_2026')
  const added = sum('additions_since')
  const current = sum('written_down_value_today')
  const lines = sum('lines')
  const linesAdded = sum('lines_added')
  const matches = Math.abs(audited - AUDITED_REGISTER_TOTAL) < 0.5

  return (
    <div className={`rounded-lg border px-4 py-3 ${
      matches ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">
          Fixed asset register · {data.length} categories · {lines} lines
        </div>
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
          <span className="text-slate-600">
            Audited 31.08.2026{' '}
            <strong className={matches ? 'text-emerald-800' : 'text-rose-800'}>
              {rupees(audited)}
            </strong>
          </span>
          {added > 0 && (
            <span className="text-slate-600">
              Added since <strong className="text-slate-900">{rupees(added)}</strong>
              <span className="text-xs text-slate-500"> ({linesAdded} line{linesAdded === 1 ? '' : 's'})</span>
            </span>
          )}
          <span className="text-slate-600">
            Book value today <strong className="text-slate-900">{rupees(current)}</strong>
          </span>
        </div>
      </div>
      <p className={`mt-1 text-xs ${matches ? 'text-emerald-700' : 'text-rose-700'}`}>
        {matches
          ? added > 0
            ? `The audited base agrees to the rupee with the CA-approved ₹13,59,17,702 as at 31.08.2026. The ${rupees(added)} added since was bought after that date and is deliberately kept out of that figure.`
            : 'Agrees to the rupee with the CA-approved audited valuation of ₹13,59,17,702 as at 31.08.2026.'
          : `The audited base does NOT agree with ₹13,59,17,702 — the difference is ${rupees(Math.abs(audited - AUDITED_REGISTER_TOTAL))}. Tell the System Administrator before relying on any figure on this page.`}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Operating Assets — fixed asset register, category 7 of 7, the last one
// ---------------------------------------------------------------------------
// Scaffolding, formwork, shuttering, porta cabins, water pumps, hand tools and
// PPE. Like the electrical register it carries a UNIT OF MEASURE, because not
// everything here is a piece: GCI sheet is counted in SHEET, footwear in PAIR,
// scaffolding towers in SET, MS pipe in MTR — and one line, the Unity Mall
// shuttering, is a single LOT worth ₹15,06,789 that still needs a physical
// piece count. That line is flagged on screen rather than left to be noticed.
// ---------------------------------------------------------------------------
function OperatingRegister() {
  const { profile } = useAuth()
  const toast = useToast()
  const role = profile!.role
  const mayEdit = can(role, 'manageAssets')

  const [term, setTerm] = useState('')
  const [site, setSite] = useState('')
  const [cls, setCls] = useState('')
  const [editing, setEditing] = useState<any>(null)
  const [depOpen, setDepOpen] = useState(false)
  const [fyEnd, setFyEnd] = useState('2027-03-31')
  const [busy, setBusy] = useState(false)

  const { data: sites } = useSites()
  const { data: classes } = useQuery<any>('asset_dep_classes', () =>
    supabase.from('asset_dep_classes').select('*').eq('is_active', true).order('code'))
  const { data, loading, error, refresh } = useQuery<any>('asset_operating', () => {
    let q = supabase.from('v_asset_operating').select('*')
    if (site) q = q.eq('site_id', site)
    if (cls) q = q.eq('dep_class_code', cls)
    return q.order('id')
  }, [site, cls], { idField: 'id' })

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return data
    return data.filter((x: any) =>
      (x.item_name ?? '').toLowerCase().includes(t) ||
      (x.make_model ?? '').toLowerCase().includes(t) ||
      (x.site_name ?? '').toLowerCase().includes(t) ||
      (x.id ?? '').toLowerCase().includes(t))
  }, [data, term])

  const audited = rows.reduce((a: number, r: any) => a + Number(r.opening_wdv || 0), 0)
  const current = rows.reduce((a: number, r: any) => a + Number(r.current_wdv || 0), 0)
  const scaf = rows.filter((r: any) => r.dep_class_code === 'SCAF')
    .reduce((a: number, r: any) => a + Number(r.opening_wdv || 0), 0)
  const uncounted = rows.filter((r: any) => r.uom === 'LOT')
  const { sorted, sort, toggle } = useAssetSort(rows)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Operating assets" value={`${rows.length} lines`}
              icon={<Boxes className="h-5 w-5" />} />
        <Stat label="Audited value 31.08.2026" value={rupees(audited)} />
        <Stat label="Written down value today" value={rupees(current)} tone="good" />
        <Stat label="Of which scaffolding & formwork" value={rupees(scaf)} />
      </div>

      {uncounted.length > 0 && (
        <Alert tone="warning">
          <strong>{uncounted.length} line{uncounted.length > 1 ? 's' : ''} carr{uncounted.length > 1 ? 'y' : 'ies'} a
          quantity of 1 LOT</strong>, worth{' '}
          {rupees(uncounted.reduce((a: number, r: any) => a + Number(r.opening_wdv || 0), 0))} between
          them. The 31.08.2026 workbook gave a value for {uncounted.length > 1 ? 'these' : 'this'} with no
          quantity and no rate, and the Head of Stores confirmed on 02-09-2026 that it is a genuine lump
          sum for a mixed lot. The valuation is right, but a lot cannot be verified at a physical stock
          count — a piece count is still owed.
        </Alert>
      )}

      <Card
        title="Operating asset register"
        subtitle="Scaffolding, formwork, shuttering, porta cabins, pumps, hand tools and PPE. Counted in pieces, sheets, pairs, sets, metres — or, for one line, a single lot."
        actions={mayEdit ? (
          <button className="btn-secondary btn-sm" onClick={() => setDepOpen(true)}>
            <RefreshCw className="h-4 w-4" /> Run annual depreciation
          </button>
        ) : undefined}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <SearchInput value={term} onChange={setTerm} placeholder="Search item, make, site…" />
          <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
                  options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
          <Select value={cls} onChange={(e) => setCls(e.target.value)} placeholder="All categories"
                  options={classes.map((c: any) => ({
                    value: c.code, label: `${c.label} (${c.useful_life_years} yr)` }))} />
        </div>
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load the operating register" message={error} /></div>}
        {!loading && rows.length === 0 && (
          <EmptyState icon={<Boxes className="h-9 w-9" />} title="No operating assets on the register" />
        )}
        {rows.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Asset ID</Th>
                  <Th id="item_name" sort={sort} onSort={toggle}>Item</Th>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="quantity" sort={sort} onSort={toggle} className="num" align="right">Qty</Th>
                  <Th id="uom" sort={sort} onSort={toggle}>Unit</Th>
                  <Th id="rate_per_unit" sort={sort} onSort={toggle} className="num" align="right">Rate each</Th>
                  <Th id="opening_wdv" sort={sort} onSort={toggle} className="num" align="right">Audited value</Th>
                  <Th id="depreciation_to_date" sort={sort} onSort={toggle} className="num" align="right">Depreciation</Th>
                  <Th id="current_wdv" sort={sort} onSort={toggle} className="num" align="right">Book value</Th>
                  <Th id="dep_class_label" sort={sort} onSort={toggle}>Category</Th>
                  <Th id="wdv_rate_pct" sort={sort} onSort={toggle} className="num" align="right">Rate</Th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                  {mayEdit && <th className="col-actions whitespace-nowrap">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map((x: any) => (
                  <tr key={x.id}>
                    <td className="font-mono text-xs">{x.id}</td>
                    <td className="text-sm font-medium">{x.item_name}</td>
                    <td className="text-xs">{x.site_name}</td>
                    <td className="num text-xs">{qty(x.quantity)}</td>
                    <td className="text-xs">
                      {x.uom === 'NO'
                        ? <span className="text-slate-400">nos</span>
                        : <span className={x.uom === 'LOT' ? 'pill-red' : 'pill-amber'}>{x.uom}</span>}
                    </td>
                    <td className="num text-xs">{rupees(x.rate_per_unit)}</td>
                    <td className="num text-xs">{rupees(x.opening_wdv)}</td>
                    <td className="num text-xs text-amber-700">{rupees(x.depreciation_to_date)}</td>
                    <td className="num text-xs font-semibold">{rupees(x.current_wdv)}</td>
                    <td className="text-xs text-slate-500" title={x.dep_class_ref ?? ''}>
                      {x.dep_class_label}
                    </td>
                    <td className="num text-xs">{Number(x.wdv_rate_pct).toFixed(3)}%</td>
                    <td>
                      <span className={x.status === 'Active' ? 'pill-green'
                        : x.status === 'Breakdown' ? 'pill-amber' : 'pill-grey'}>{x.status}</span>
                    </td>
                    {mayEdit && (
                      <td className="col-actions">
                        <div className="flex items-center gap-0.5">
                          <button className="btn-ghost btn-sm" onClick={() => setEditing(x)}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <TransferButton category="operating" item={x} onDone={refresh} />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <OperatingForm item={editing} onClose={() => setEditing(null)} onDone={refresh} />
      )}

      <Modal open={depOpen} onClose={() => setDepOpen(false)}
             title="Run the annual depreciation"
             subtitle="Once a year, at the financial year end"
             footer={<>
               <button className="btn-secondary" onClick={() => setDepOpen(false)} disabled={busy}>Cancel</button>
               <button className="btn-primary" disabled={busy} onClick={async () => {
                 setBusy(true)
                 const { data: res, error: err } =
                   await supabase.rpc('rpc_run_operating_depreciation', { p_fy_end: fyEnd })
                 setBusy(false)
                 if (err) { toast.error(err.message); return }
                 const r = Array.isArray(res) ? res[0] : res
                 toast.success(`${r?.lines_charged ?? 0} line(s) depreciated by ${rupees(r?.total_depreciation ?? 0)}.`)
                 setDepOpen(false); refresh()
               }}>Run it</button>
             </>}>
        <div className="space-y-3">
          <Alert tone="info">
            This tab runs on six different rates. Scaffolding and formwork depreciate at{' '}
            <strong>22.092% over 12 years</strong> — Schedule II Part C 4(ii)(e), civil
            construction. Porta cabins, pumps, GCI sheet and MS pipe are on 15 years; hand
            tools and PPE on 5; measuring and testing gear on 10; the tractor trolleys on 8.
            A line already charged for that year is skipped, so pressing this twice is harmless.
          </Alert>
          <Field label="Financial year end" required hint="31 March only. The first run is 31-03-2027.">
            <Input type="date" value={fyEnd} onChange={(e) => setFyEnd(e.target.value)} />
          </Field>
        </div>
      </Modal>
    </div>
  )
}

function OperatingForm({ item, onClose, onDone }: {
  item: any; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const [f, setF] = useState<any>({ ...item })
  const [busy, setBusy] = useState(false)
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }))

  async function save() {
    const wdv = Number(f.current_wdv)
    if (!Number.isFinite(wdv) || wdv < 0) { toast.error('The written down value must be a number, and not negative.'); return }
    if (wdv < Number(item.residual_value)) {
      toast.error(`The written down value cannot go below the 5% residual of ${rupees(item.residual_value)}.`); return
    }
    setBusy(true)
    const { error } = await supabase.from('asset_operating').update({
      current_wdv: wdv,
      status: f.status,
      make_model: f.make_model || null,
      location_in_site: f.location_in_site || null,
      remarks: f.remarks || null,
    }).eq('id', item.id)
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success('Operating asset updated.')
    onClose(); onDone()
  }

  return (
    <Modal open wide onClose={onClose}
           title={`${item.item_name} — ${qty(item.quantity)} ${item.uom === 'NO' ? 'nos' : item.uom}`}
           subtitle={`${item.site_name} · ${rupees(item.rate_per_unit)} per ${item.uom === 'NO' ? 'piece' : item.uom} · audited value ${rupees(item.opening_wdv)} as at 31.08.2026`}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>Save</button>
           </>}>
      <div className="space-y-4">
        <Alert tone="info">
          <strong>{item.dep_class_label}</strong> — {item.useful_life_years} year life,
          {' '}{Number(item.wdv_rate_pct).toFixed(3)}% written down value.
          {item.dep_class_ref ? ` Companies Act 2013 Schedule II ${item.dep_class_ref}.` : ''}
          {' '}Quantity, unit and rate are not edited here: the line value must always
          equal rate × quantity.
        </Alert>
        {item.uom === 'LOT' && (
          <Alert tone="warning">
            This line is a <strong>single lot</strong>, not a counted quantity. The valuation is
            the audited figure and is correct, but the lot has never been broken down into
            pieces — so it cannot be verified at a physical stock count. A piece count from the
            site store-man is still owed.
          </Alert>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Written down value" required
                 hint="Changing this is recorded in the audit trail with your name.">
            <Input type="number" step="any" value={f.current_wdv ?? ''}
                   onChange={(e) => set('current_wdv', e.target.value)} />
          </Field>
          <Field label="Status">
            <Select value={f.status} onChange={(e) => set('status', e.target.value)}
                    options={['Active', 'Breakdown', 'Under Repair', 'Idle', 'Disposed']
                      .map((s) => ({ value: s, label: s }))} />
          </Field>
          <Field label="Make / model">
            <Input value={f.make_model ?? ''} onChange={(e) => set('make_model', e.target.value)} />
          </Field>
          <Field label="Location within the site">
            <Input value={f.location_in_site ?? ''}
                   onChange={(e) => set('location_in_site', e.target.value)} />
          </Field>
          <Field label="Remarks" className="sm:col-span-2">
            <Textarea rows={2} value={f.remarks ?? ''} onChange={(e) => set('remarks', e.target.value)} />
          </Field>
        </div>
        {item.source_note && (
          <p className="text-xs text-slate-500">
            <strong>From the 31.08.2026 workbook:</strong> {item.source_note}
          </p>
        )}
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Electrical Appliances — fixed asset register, category 6 of 7
// ---------------------------------------------------------------------------
// The only register that shows a UNIT OF MEASURE, because a third of it is
// cable sold by the metre. "1,700" on a count sheet is meaningless and invites
// the reader to assume 1,700 pieces; "1,700 MTR" is countable.
// ---------------------------------------------------------------------------
function ElectricalRegister() {
  const { profile } = useAuth()
  const toast = useToast()
  const role = profile!.role
  const mayEdit = can(role, 'manageAssets')

  const [term, setTerm] = useState('')
  const [site, setSite] = useState('')
  const [cls, setCls] = useState('')
  const [editing, setEditing] = useState<any>(null)
  const [depOpen, setDepOpen] = useState(false)
  const [fyEnd, setFyEnd] = useState('2027-03-31')
  const [busy, setBusy] = useState(false)

  const { data: sites } = useSites()
  const { data: classes } = useQuery<any>('asset_dep_classes', () =>
    supabase.from('asset_dep_classes').select('*').eq('is_active', true).order('code'))
  const { data, loading, error, refresh } = useQuery<any>('asset_electrical', () => {
    let q = supabase.from('v_asset_electrical').select('*')
    if (site) q = q.eq('site_id', site)
    if (cls) q = q.eq('dep_class_code', cls)
    return q.order('id')
  }, [site, cls], { idField: 'id' })

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return data
    return data.filter((x: any) =>
      (x.item_name ?? '').toLowerCase().includes(t) ||
      (x.make_model ?? '').toLowerCase().includes(t) ||
      (x.site_name ?? '').toLowerCase().includes(t) ||
      (x.id ?? '').toLowerCase().includes(t))
  }, [data, term])

  const audited = rows.reduce((a: number, r: any) => a + Number(r.opening_wdv || 0), 0)
  const current = rows.reduce((a: number, r: any) => a + Number(r.current_wdv || 0), 0)
  const byLength = rows.filter((r: any) => r.uom !== 'NO')
    .reduce((a: number, r: any) => a + Number(r.opening_wdv || 0), 0)
  const { sorted, sort, toggle } = useAssetSort(rows)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Electrical items" value={`${rows.length} lines`}
              icon={<Boxes className="h-5 w-5" />} />
        <Stat label="Audited value 31.08.2026" value={rupees(audited)} />
        <Stat label="Written down value today" value={rupees(current)} tone="good" />
        <Stat label="Of which cable and wire" value={rupees(byLength)} />
      </div>

      <Card
        title="Electrical appliance register"
        subtitle="Fans, lights, cable and wire. Measured in pieces, metres or coils — the unit is on every line, so a physical count is possible."
        actions={mayEdit ? (
          <button className="btn-secondary btn-sm" onClick={() => setDepOpen(true)}>
            <RefreshCw className="h-4 w-4" /> Run annual depreciation
          </button>
        ) : undefined}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <SearchInput value={term} onChange={setTerm} placeholder="Search item, make, site…" />
          <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
                  options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
          <Select value={cls} onChange={(e) => setCls(e.target.value)} placeholder="All categories"
                  options={classes.map((c: any) => ({
                    value: c.code, label: `${c.label} (${c.useful_life_years} yr)` }))} />
        </div>
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load the electrical register" message={error} /></div>}
        {!loading && rows.length === 0 && (
          <EmptyState icon={<Boxes className="h-9 w-9" />} title="No electrical items on the register" />
        )}
        {rows.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Asset ID</Th>
                  <Th id="item_name" sort={sort} onSort={toggle}>Item</Th>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="quantity" sort={sort} onSort={toggle} className="num" align="right">Qty</Th>
                  <Th id="uom" sort={sort} onSort={toggle}>Unit</Th>
                  <Th id="rate_per_unit" sort={sort} onSort={toggle} className="num" align="right">Rate each</Th>
                  <Th id="opening_wdv" sort={sort} onSort={toggle} className="num" align="right">Audited value</Th>
                  <Th id="depreciation_to_date" sort={sort} onSort={toggle} className="num" align="right">Depreciation</Th>
                  <Th id="current_wdv" sort={sort} onSort={toggle} className="num" align="right">Book value</Th>
                  <Th id="dep_class_label" sort={sort} onSort={toggle}>Category</Th>
                  <Th id="wdv_rate_pct" sort={sort} onSort={toggle} className="num" align="right">Rate</Th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                  {mayEdit && <th className="col-actions whitespace-nowrap">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map((x: any) => (
                  <tr key={x.id}>
                    <td className="font-mono text-xs">{x.id}</td>
                    <td className="text-sm font-medium">{x.item_name}</td>
                    <td className="text-xs">{x.site_name}</td>
                    <td className="num text-xs">{qty(x.quantity)}</td>
                    <td className="text-xs">
                      {x.uom === 'NO'
                        ? <span className="text-slate-400">nos</span>
                        : <span className="pill-amber">{x.uom}</span>}
                    </td>
                    <td className="num text-xs">{rupees(x.rate_per_unit)}</td>
                    <td className="num text-xs">{rupees(x.opening_wdv)}</td>
                    <td className="num text-xs text-amber-700">{rupees(x.depreciation_to_date)}</td>
                    <td className="num text-xs font-semibold">{rupees(x.current_wdv)}</td>
                    <td className="text-xs text-slate-500" title={x.dep_class_ref ?? ''}>
                      {x.dep_class_label}
                    </td>
                    <td className="num text-xs">{Number(x.wdv_rate_pct).toFixed(3)}%</td>
                    <td>
                      <span className={x.status === 'Active' ? 'pill-green'
                        : x.status === 'Breakdown' ? 'pill-amber' : 'pill-grey'}>{x.status}</span>
                    </td>
                    {mayEdit && (
                      <td className="col-actions">
                        <div className="flex items-center gap-0.5">
                          <button className="btn-ghost btn-sm" onClick={() => setEditing(x)}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <TransferButton category="electrical" item={x} onDone={refresh} />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <ElectricalForm item={editing} onClose={() => setEditing(null)} onDone={refresh} />
      )}

      <Modal open={depOpen} onClose={() => setDepOpen(false)}
             title="Run the annual depreciation"
             subtitle="Once a year, at the financial year end"
             footer={<>
               <button className="btn-secondary" onClick={() => setDepOpen(false)} disabled={busy}>Cancel</button>
               <button className="btn-primary" disabled={busy} onClick={async () => {
                 setBusy(true)
                 const { data: res, error: err } =
                   await supabase.rpc('rpc_run_electrical_depreciation', { p_fy_end: fyEnd })
                 setBusy(false)
                 if (err) { toast.error(err.message); return }
                 const r = Array.isArray(res) ? res[0] : res
                 toast.success(`${r?.lines_charged ?? 0} line(s) depreciated by ${rupees(r?.total_depreciation ?? 0)}.`)
                 setDepOpen(false); refresh()
               }}>Run it</button>
             </>}>
        <div className="space-y-3">
          <Alert tone="info">
            Fans, lights, cable and wire depreciate at <strong>25.887% over 10 years</strong> —
            Schedule II Part C 3(iv), electrical installations. The electric motors are on
            15 years and the money counting machine on 5. A line already charged for that
            year is skipped, so pressing this twice is harmless.
          </Alert>
          <Field label="Financial year end" required hint="31 March only. The first run is 31-03-2027.">
            <Input type="date" value={fyEnd} onChange={(e) => setFyEnd(e.target.value)} />
          </Field>
        </div>
      </Modal>
    </div>
  )
}

function ElectricalForm({ item, onClose, onDone }: {
  item: any; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const [f, setF] = useState<any>({ ...item })
  const [busy, setBusy] = useState(false)
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }))

  async function save() {
    const wdv = Number(f.current_wdv)
    if (!Number.isFinite(wdv) || wdv < 0) { toast.error('The written down value must be a number, and not negative.'); return }
    if (wdv < Number(item.residual_value)) {
      toast.error(`The written down value cannot go below the 5% residual of ${rupees(item.residual_value)}.`); return
    }
    setBusy(true)
    const { error } = await supabase.from('asset_electrical').update({
      current_wdv: wdv,
      status: f.status,
      make_model: f.make_model || null,
      location_in_site: f.location_in_site || null,
      remarks: f.remarks || null,
    }).eq('id', item.id)
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success('Electrical item updated.')
    onClose(); onDone()
  }

  return (
    <Modal open wide onClose={onClose}
           title={`${item.item_name} — ${qty(item.quantity)} ${item.uom === 'NO' ? 'nos' : item.uom}`}
           subtitle={`${item.site_name} · ${rupees(item.rate_per_unit)} per ${item.uom === 'NO' ? 'piece' : item.uom} · audited value ${rupees(item.opening_wdv)} as at 31.08.2026`}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>Save</button>
           </>}>
      <div className="space-y-4">
        <Alert tone="info">
          <strong>{item.dep_class_label}</strong> — {item.useful_life_years} year life,
          {' '}{Number(item.wdv_rate_pct).toFixed(3)}% written down value.
          {item.dep_class_ref ? ` Companies Act 2013 Schedule II ${item.dep_class_ref}.` : ''}
          {' '}Quantity, unit and rate are not edited here: the line value must always
          equal rate × quantity.
        </Alert>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Written down value" required
                 hint="Changing this is recorded in the audit trail with your name.">
            <Input type="number" step="any" value={f.current_wdv ?? ''}
                   onChange={(e) => set('current_wdv', e.target.value)} />
          </Field>
          <Field label="Status">
            <Select value={f.status} onChange={(e) => set('status', e.target.value)}
                    options={['Active', 'Breakdown', 'Under Repair', 'Idle', 'Disposed']
                      .map((s) => ({ value: s, label: s }))} />
          </Field>
          <Field label="Make / model">
            <Input value={f.make_model ?? ''} onChange={(e) => set('make_model', e.target.value)} />
          </Field>
          <Field label="Location within the site">
            <Input value={f.location_in_site ?? ''}
                   onChange={(e) => set('location_in_site', e.target.value)} />
          </Field>
          <Field label="Remarks" className="sm:col-span-2">
            <Textarea rows={2} value={f.remarks ?? ''} onChange={(e) => set('remarks', e.target.value)} />
          </Field>
        </div>
        {item.source_note && (
          <p className="text-xs text-slate-500">
            <strong>From the 31.08.2026 workbook:</strong> {item.source_note}
          </p>
        )}
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Electronic Appliances — fixed asset register, category 5 of 7
// ---------------------------------------------------------------------------
// Split across two Schedule II classes, and the split is the point: end user
// IT devices at 3 years (Part C 6(ii)(b)) and electrical installations at 10
// (Part C 3(iv)). The CA ruled on 02-09-2026 that the air conditioners belong
// with the second, not on the 5-year office equipment class.
// ---------------------------------------------------------------------------
function ElectronicsRegister() {
  const { profile } = useAuth()
  const toast = useToast()
  const role = profile!.role
  const mayEdit = can(role, 'manageAssets')

  const [term, setTerm] = useState('')
  const [site, setSite] = useState('')
  const [cls, setCls] = useState('')
  const [editing, setEditing] = useState<any>(null)
  const [depOpen, setDepOpen] = useState(false)
  const [fyEnd, setFyEnd] = useState('2027-03-31')
  const [busy, setBusy] = useState(false)

  const { data: sites } = useSites()
  const { data: classes } = useQuery<any>('asset_dep_classes', () =>
    supabase.from('asset_dep_classes').select('*').eq('is_active', true).order('code'))
  const { data, loading, error, refresh } = useQuery<any>('asset_electronics', () => {
    let q = supabase.from('v_asset_electronics').select('*')
    if (site) q = q.eq('site_id', site)
    if (cls) q = q.eq('dep_class_code', cls)
    return q.order('id')
  }, [site, cls], { idField: 'id' })

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return data
    return data.filter((x: any) =>
      (x.item_name ?? '').toLowerCase().includes(t) ||
      (x.make_model ?? '').toLowerCase().includes(t) ||
      (x.serial_no ?? '').toLowerCase().includes(t) ||
      (x.site_name ?? '').toLowerCase().includes(t) ||
      (x.id ?? '').toLowerCase().includes(t))
  }, [data, term])

  const audited = rows.reduce((a: number, r: any) => a + Number(r.opening_wdv || 0), 0)
  const current = rows.reduce((a: number, r: any) => a + Number(r.current_wdv || 0), 0)
  const units = rows.reduce((a: number, r: any) => a + Number(r.quantity || 0), 0)
  const itValue = rows.filter((r: any) => r.dep_class_code === 'COMP')
    .reduce((a: number, r: any) => a + Number(r.opening_wdv || 0), 0)
  const { sorted, sort, toggle } = useAssetSort(rows)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Appliances" value={`${rows.length} lines · ${units} units`}
              icon={<Boxes className="h-5 w-5" />} />
        <Stat label="Audited value 31.08.2026" value={rupees(audited)} />
        <Stat label="Written down value today" value={rupees(current)} tone="good" />
        <Stat label="Of which IT, on 3-year life" value={rupees(itValue)} />
      </div>

      <Card
        title="Electronic appliance register"
        subtitle="IT end user devices depreciate over 3 years; air conditioners and white goods over 10. Both confirmed by the CA on 02-09-2026."
        actions={mayEdit ? (
          <button className="btn-secondary btn-sm" onClick={() => setDepOpen(true)}>
            <RefreshCw className="h-4 w-4" /> Run annual depreciation
          </button>
        ) : undefined}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <SearchInput value={term} onChange={setTerm} placeholder="Search appliance, make, serial…" />
          <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
                  options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
          <Select value={cls} onChange={(e) => setCls(e.target.value)} placeholder="All categories"
                  options={classes.map((c: any) => ({
                    value: c.code, label: `${c.label} (${c.useful_life_years} yr)` }))} />
        </div>
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load the electronics register" message={error} /></div>}
        {!loading && rows.length === 0 && (
          <EmptyState icon={<Boxes className="h-9 w-9" />} title="No appliances on the register" />
        )}
        {rows.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Asset ID</Th>
                  <Th id="item_name" sort={sort} onSort={toggle}>Appliance</Th>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="quantity" sort={sort} onSort={toggle} className="num" align="right">Qty</Th>
                  <Th id="rate_per_unit" sort={sort} onSort={toggle} className="num" align="right">Rate each</Th>
                  <Th id="opening_wdv" sort={sort} onSort={toggle} className="num" align="right">Audited value</Th>
                  <Th id="depreciation_to_date" sort={sort} onSort={toggle} className="num" align="right">Depreciation</Th>
                  <Th id="current_wdv" sort={sort} onSort={toggle} className="num" align="right">Book value</Th>
                  <Th id="dep_class_label" sort={sort} onSort={toggle}>Category</Th>
                  <Th id="wdv_rate_pct" sort={sort} onSort={toggle} className="num" align="right">Rate</Th>
                  <Th id="amc_expiry" sort={sort} onSort={toggle}>AMC</Th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                  {mayEdit && <th className="col-actions whitespace-nowrap">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map((x: any) => (
                  <tr key={x.id}>
                    <td className="font-mono text-xs">{x.id}</td>
                    <td className="text-sm font-medium">{x.item_name}</td>
                    <td className="text-xs">{x.site_name}</td>
                    <td className="num text-xs">{x.quantity}</td>
                    <td className="num text-xs">{rupees(x.rate_per_unit)}</td>
                    <td className="num text-xs">{rupees(x.opening_wdv)}</td>
                    <td className="num text-xs text-amber-700">{rupees(x.depreciation_to_date)}</td>
                    <td className="num text-xs font-semibold">{rupees(x.current_wdv)}</td>
                    <td className="text-xs text-slate-500" title={x.dep_class_ref ?? ''}>
                      {x.dep_class_label}
                    </td>
                    <td className="num text-xs">{Number(x.wdv_rate_pct).toFixed(3)}%</td>
                    <td className="text-xs">
                      {x.amc_state === 'EXPIRED'
                        ? <span className="pill-red">Expired</span>
                        : x.amc_state === 'DUE SOON'
                          ? <span className="pill-amber">Due soon</span>
                          : x.amc_state === 'OK'
                            ? <span className="pill-green">{ddmmyyyy(x.amc_expiry)}</span>
                            : <span className="text-slate-400">—</span>}
                    </td>
                    <td>
                      <span className={x.status === 'Active' ? 'pill-green'
                        : x.status === 'Breakdown' ? 'pill-amber' : 'pill-grey'}>{x.status}</span>
                    </td>
                    {mayEdit && (
                      <td className="col-actions">
                        <div className="flex items-center gap-0.5">
                          <button className="btn-ghost btn-sm" onClick={() => setEditing(x)}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <TransferButton category="electronics" item={x} onDone={refresh} />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <ElectronicsForm item={editing} onClose={() => setEditing(null)} onDone={refresh} />
      )}

      <Modal open={depOpen} onClose={() => setDepOpen(false)}
             title="Run the annual depreciation"
             subtitle="Once a year, at the financial year end"
             footer={<>
               <button className="btn-secondary" onClick={() => setDepOpen(false)} disabled={busy}>Cancel</button>
               <button className="btn-primary" disabled={busy} onClick={async () => {
                 setBusy(true)
                 const { data: res, error: err } =
                   await supabase.rpc('rpc_run_electronics_depreciation', { p_fy_end: fyEnd })
                 setBusy(false)
                 if (err) { toast.error(err.message); return }
                 const r = Array.isArray(res) ? res[0] : res
                 toast.success(`${r?.lines_charged ?? 0} line(s) depreciated by ${rupees(r?.total_depreciation ?? 0)}.`)
                 setDepOpen(false); refresh()
               }}>Run it</button>
             </>}>
        <div className="space-y-3">
          <Alert tone="info">
            Desktops, laptops and printers depreciate at <strong>63.160% over 3 years</strong> —
            Schedule II names end user devices explicitly. Air conditioners, refrigerators and
            washing machines depreciate at <strong>25.887% over 10 years</strong>. A line already
            charged for that year is skipped, so pressing this twice is harmless.
          </Alert>
          <Field label="Financial year end" required hint="31 March only. The first run is 31-03-2027.">
            <Input type="date" value={fyEnd} onChange={(e) => setFyEnd(e.target.value)} />
          </Field>
        </div>
      </Modal>
    </div>
  )
}

function ElectronicsForm({ item, onClose, onDone }: {
  item: any; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const [f, setF] = useState<any>({ ...item })
  const [busy, setBusy] = useState(false)
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }))

  async function save() {
    const wdv = Number(f.current_wdv)
    if (!Number.isFinite(wdv) || wdv < 0) { toast.error('The written down value must be a number, and not negative.'); return }
    if (wdv < Number(item.residual_value)) {
      toast.error(`The written down value cannot go below the 5% residual of ${rupees(item.residual_value)}.`); return
    }
    setBusy(true)
    const { error } = await supabase.from('asset_electronics').update({
      current_wdv: wdv,
      status: f.status,
      make_model: f.make_model || null,
      serial_no: f.serial_no || null,
      warranty_expiry: f.warranty_expiry || null,
      amc_expiry: f.amc_expiry || null,
      location_in_site: f.location_in_site || null,
      remarks: f.remarks || null,
    }).eq('id', item.id)
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success('Appliance updated.')
    onClose(); onDone()
  }

  return (
    <Modal open wide onClose={onClose}
           title={`${item.item_name} — ${item.quantity} nos`}
           subtitle={`${item.site_name} · ${rupees(item.rate_per_unit)} each · audited value ${rupees(item.opening_wdv)} as at 31.08.2026`}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>Save</button>
           </>}>
      <div className="space-y-4">
        <Alert tone="info">
          <strong>{item.dep_class_label}</strong> — {item.useful_life_years} year life,
          {' '}{Number(item.wdv_rate_pct).toFixed(3)}% written down value.
          {item.dep_class_ref ? ` Companies Act 2013 Schedule II ${item.dep_class_ref}.` : ''}
          {' '}The category is fixed when the asset goes on the register and is not edited here.
        </Alert>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Written down value" required
                 hint="Changing this is recorded in the audit trail with your name.">
            <Input type="number" step="any" value={f.current_wdv ?? ''}
                   onChange={(e) => set('current_wdv', e.target.value)} />
          </Field>
          <Field label="Status">
            <Select value={f.status} onChange={(e) => set('status', e.target.value)}
                    options={['Active', 'Breakdown', 'Under Repair', 'Idle', 'Disposed']
                      .map((s) => ({ value: s, label: s }))} />
          </Field>
          <Field label="Make / model">
            <Input value={f.make_model ?? ''} onChange={(e) => set('make_model', e.target.value)} />
          </Field>
          <Field label="Serial number">
            <Input value={f.serial_no ?? ''} onChange={(e) => set('serial_no', e.target.value)} />
          </Field>
          <Field label="Warranty expires">
            <Input type="date" value={f.warranty_expiry ?? ''}
                   onChange={(e) => set('warranty_expiry', e.target.value)} />
          </Field>
          <Field label="AMC expires" hint="Printers and copiers usually run on an annual contract">
            <Input type="date" value={f.amc_expiry ?? ''}
                   onChange={(e) => set('amc_expiry', e.target.value)} />
          </Field>
          <Field label="Location within the site" className="sm:col-span-2">
            <Input value={f.location_in_site ?? ''}
                   onChange={(e) => set('location_in_site', e.target.value)} />
          </Field>
          <Field label="Remarks" className="sm:col-span-2">
            <Textarea rows={2} value={f.remarks ?? ''} onChange={(e) => set('remarks', e.target.value)} />
          </Field>
        </div>
        {item.source_note && (
          <p className="text-xs text-slate-500">
            <strong>From the 31.08.2026 workbook:</strong> {item.source_note}
          </p>
        )}
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Furniture & Fixture — fixed asset register, category 4 of 7
// ---------------------------------------------------------------------------
// One depreciation class for the whole register: Schedule II Part C 2(i),
// furniture and fittings, 10 years. The 8-year hospitality line does not
// apply - the beds are staff accommodation, not a boarding house - and the CA
// confirmed the exclusion on 02-09-2026.
// ---------------------------------------------------------------------------
function FurnitureRegister() {
  const { profile } = useAuth()
  const toast = useToast()
  const role = profile!.role
  const mayEdit = can(role, 'manageAssets')

  const [term, setTerm] = useState('')
  const [site, setSite] = useState('')
  const [editing, setEditing] = useState<any>(null)
  const [depOpen, setDepOpen] = useState(false)
  const [fyEnd, setFyEnd] = useState('2027-03-31')
  const [busy, setBusy] = useState(false)

  const { data: sites } = useSites()
  const { data, loading, error, refresh } = useQuery<any>('asset_furniture', () => {
    let q = supabase.from('v_asset_furniture').select('*')
    if (site) q = q.eq('site_id', site)
    return q.order('id')
  }, [site], { idField: 'id' })

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return data
    return data.filter((x: any) =>
      (x.item_name ?? '').toLowerCase().includes(t) ||
      (x.location_in_site ?? '').toLowerCase().includes(t) ||
      (x.site_name ?? '').toLowerCase().includes(t) ||
      (x.id ?? '').toLowerCase().includes(t))
  }, [data, term])

  const { sorted, sort, toggle } = useAssetSort(rows)

  const audited = rows.reduce((a: number, r: any) => a + Number(r.opening_wdv || 0), 0)
  const current = rows.reduce((a: number, r: any) => a + Number(r.current_wdv || 0), 0)
  const pieces = rows.reduce((a: number, r: any) => a + Number(r.quantity || 0), 0)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Furniture" value={`${rows.length} lines · ${pieces} pieces`}
              icon={<Boxes className="h-5 w-5" />} />
        <Stat label="Audited value 31.08.2026" value={rupees(audited)} />
        <Stat label="Written down value today" value={rupees(current)} tone="good" />
        <Stat label="Not in use" value={rows.filter((r: any) => r.status !== 'Active').length} />
      </div>

      <Card
        title="Furniture & fixture register"
        subtitle="Schedule II Part C 2(i), furniture and fittings, 10 years. Depreciated once a year on 31 March."
        actions={mayEdit ? (
          <button className="btn-secondary btn-sm" onClick={() => setDepOpen(true)}>
            <RefreshCw className="h-4 w-4" /> Run annual depreciation
          </button>
        ) : undefined}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <SearchInput value={term} onChange={setTerm} placeholder="Search item, location, site…" />
          <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
                  options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
        </div>
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load the furniture register" message={error} /></div>}
        {!loading && rows.length === 0 && (
          <EmptyState icon={<Boxes className="h-9 w-9" />} title="No furniture on the register" />
        )}
        {rows.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Asset ID</Th>
                  <Th id="item_name" sort={sort} onSort={toggle}>Furniture / fixture</Th>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="location_in_site" sort={sort} onSort={toggle}>Location</Th>
                  <Th id="quantity" sort={sort} onSort={toggle} className="num" align="right">Qty</Th>
                  <Th id="rate_per_unit" sort={sort} onSort={toggle} className="num" align="right">Rate each</Th>
                  <Th id="opening_wdv" sort={sort} onSort={toggle} className="num" align="right">Audited value</Th>
                  <Th id="depreciation_to_date" sort={sort} onSort={toggle} className="num" align="right">Depreciation</Th>
                  <Th id="current_wdv" sort={sort} onSort={toggle} className="num" align="right">Book value</Th>
                  <Th id="condition" sort={sort} onSort={toggle}>Condition</Th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                  {mayEdit && <th className="col-actions whitespace-nowrap">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map((x: any) => (
                  <tr key={x.id}>
                    <td className="font-mono text-xs">{x.id}</td>
                    <td className="text-sm font-medium">{x.item_name}</td>
                    <td className="text-xs">{x.site_name}</td>
                    <td className="text-xs text-slate-500">{x.location_in_site ?? '—'}</td>
                    <td className="num text-xs">{x.quantity}</td>
                    <td className="num text-xs">{rupees(x.rate_per_unit)}</td>
                    <td className="num text-xs">{rupees(x.opening_wdv)}</td>
                    <td className="num text-xs text-amber-700">{rupees(x.depreciation_to_date)}</td>
                    <td className="num text-xs font-semibold">{rupees(x.current_wdv)}</td>
                    <td className="text-xs text-slate-500">{x.condition ?? '—'}</td>
                    <td>
                      <span className={x.status === 'Active' ? 'pill-green'
                        : x.status === 'Damaged' ? 'pill-amber' : 'pill-grey'}>{x.status}</span>
                    </td>
                    {mayEdit && (
                      <td className="col-actions">
                        <div className="flex items-center gap-0.5">
                          <button className="btn-ghost btn-sm" onClick={() => setEditing(x)}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <TransferButton category="furniture" item={x} onDone={refresh} />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <FurnitureForm item={editing} onClose={() => setEditing(null)} onDone={refresh} />
      )}

      <Modal open={depOpen} onClose={() => setDepOpen(false)}
             title="Run the annual depreciation"
             subtitle="Once a year, at the financial year end"
             footer={<>
               <button className="btn-secondary" onClick={() => setDepOpen(false)} disabled={busy}>Cancel</button>
               <button className="btn-primary" disabled={busy} onClick={async () => {
                 setBusy(true)
                 const { data: res, error: err } =
                   await supabase.rpc('rpc_run_furniture_depreciation', { p_fy_end: fyEnd })
                 setBusy(false)
                 if (err) { toast.error(err.message); return }
                 const r = Array.isArray(res) ? res[0] : res
                 toast.success(`${r?.lines_charged ?? 0} line(s) depreciated by ${rupees(r?.total_depreciation ?? 0)}.`)
                 setDepOpen(false); refresh()
               }}>Run it</button>
             </>}>
        <div className="space-y-3">
          <Alert tone="info">
            Every line depreciates at 25.887% written down value — Schedule II Part C 2(i),
            furniture and fittings, 10 years. A line already charged for that year is
            skipped, so pressing this twice is harmless.
          </Alert>
          <Field label="Financial year end" required hint="31 March only. The first run is 31-03-2027.">
            <Input type="date" value={fyEnd} onChange={(e) => setFyEnd(e.target.value)} />
          </Field>
        </div>
      </Modal>
    </div>
  )
}

function FurnitureForm({ item, onClose, onDone }: {
  item: any; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const [f, setF] = useState<any>({ ...item })
  const [busy, setBusy] = useState(false)
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }))

  async function save() {
    const wdv = Number(f.current_wdv)
    if (!Number.isFinite(wdv) || wdv < 0) { toast.error('The written down value must be a number, and not negative.'); return }
    if (wdv < Number(item.residual_value)) {
      toast.error(`The written down value cannot go below the 5% residual of ${rupees(item.residual_value)}.`); return
    }
    setBusy(true)
    const { error } = await supabase.from('asset_furniture').update({
      current_wdv: wdv,
      status: f.status,
      condition: f.condition || null,
      make_model: f.make_model || null,
      location_in_site: f.location_in_site || null,
      remarks: f.remarks || null,
    }).eq('id', item.id)
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success('Furniture line updated.')
    onClose(); onDone()
  }

  return (
    <Modal open wide onClose={onClose}
           title={`${item.item_name} — ${item.quantity} nos`}
           subtitle={`${item.site_name} · ${rupees(item.rate_per_unit)} each · audited value ${rupees(item.opening_wdv)} as at 31.08.2026`}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>Save</button>
           </>}>
      <div className="space-y-4">
        <Alert tone="info">
          <strong>{item.dep_class_label}</strong> — {item.useful_life_years} year life,
          {' '}{Number(item.wdv_rate_pct).toFixed(3)}% written down value.
          {item.dep_class_ref ? ` Companies Act 2013 Schedule II ${item.dep_class_ref}.` : ''}
          {' '}Quantity and unit rate are not edited here: the line value must always equal
          rate × quantity.
        </Alert>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Written down value" required
                 hint="Changing this is recorded in the audit trail with your name.">
            <Input type="number" step="any" value={f.current_wdv ?? ''}
                   onChange={(e) => set('current_wdv', e.target.value)} />
          </Field>
          <Field label="Status">
            <Select value={f.status} onChange={(e) => set('status', e.target.value)}
                    options={['Active', 'Damaged', 'Under Repair', 'Idle', 'Disposed']
                      .map((s) => ({ value: s, label: s }))} />
          </Field>
          <Field label="Condition">
            <Select value={f.condition ?? 'Good'} onChange={(e) => set('condition', e.target.value)}
                    options={['Excellent', 'Good', 'Fair', 'Poor']
                      .map((s) => ({ value: s, label: s }))} />
          </Field>
          <Field label="Make / model">
            <Input value={f.make_model ?? ''} onChange={(e) => set('make_model', e.target.value)} />
          </Field>
          <Field label="Location within the site" className="sm:col-span-2"
                 hint="Which floor, room or guest house — useful when the physical count is done">
            <Input value={f.location_in_site ?? ''}
                   onChange={(e) => set('location_in_site', e.target.value)} />
          </Field>
          <Field label="Remarks" className="sm:col-span-2">
            <Textarea rows={2} value={f.remarks ?? ''} onChange={(e) => set('remarks', e.target.value)} />
          </Field>
        </div>
        {item.source_note && (
          <p className="text-xs text-slate-500">
            <strong>From the 31.08.2026 workbook:</strong> {item.source_note}
          </p>
        )}
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Tools & Tackles — fixed asset register, category 3 of 7
// ---------------------------------------------------------------------------
// Pooled by type and site: "24 grinding machines at HO" is one line with a
// quantity, not 24 rows. So this screen shows the unit rate as well as the
// line value, because that is how the store counts them.
// ---------------------------------------------------------------------------
function ToolsRegister() {
  const { profile } = useAuth()
  const toast = useToast()
  const role = profile!.role
  const mayEdit = can(role, 'manageAssets')

  const [term, setTerm] = useState('')
  const [site, setSite] = useState('')
  const [cls, setCls] = useState('')
  const [editing, setEditing] = useState<any>(null)
  const [depOpen, setDepOpen] = useState(false)
  const [fyEnd, setFyEnd] = useState('2027-03-31')
  const [busy, setBusy] = useState(false)

  const { data: sites } = useSites()
  const { data: classes } = useQuery<any>('asset_dep_classes', () =>
    supabase.from('asset_dep_classes').select('*').order('code'))
  const { data, loading, error, refresh } = useQuery<any>('asset_tools', () => {
    let q = supabase.from('v_asset_tools').select('*')
    if (site) q = q.eq('site_id', site)
    if (cls) q = q.eq('dep_class_code', cls)
    return q.order('id')
  }, [site, cls], { idField: 'id' })

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return data
    return data.filter((x: any) =>
      (x.tool_name ?? '').toLowerCase().includes(t) ||
      (x.make_model ?? '').toLowerCase().includes(t) ||
      (x.site_name ?? '').toLowerCase().includes(t) ||
      (x.id ?? '').toLowerCase().includes(t))
  }, [data, term])

  const { sorted, sort, toggle } = useAssetSort(rows)

  const audited = rows.reduce((a: number, r: any) => a + Number(r.opening_wdv || 0), 0)
  const current = rows.reduce((a: number, r: any) => a + Number(r.current_wdv || 0), 0)
  const units = rows.reduce((a: number, r: any) => a + Number(r.quantity || 0), 0)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Tools & tackles" value={`${rows.length} lines · ${units} units`}
              icon={<Boxes className="h-5 w-5" />} />
        <Stat label="Audited value 31.08.2026" value={rupees(audited)} />
        <Stat label="Written down value today" value={rupees(current)} tone="good" />
        <Stat label="Not in use" value={rows.filter((r: any) => r.status !== 'Active').length} />
      </div>

      <Card
        title="Tools & tackles register"
        subtitle="Pooled by type and site. Opening values are the CA-audited valuation as at 31.08.2026, depreciated once a year on 31 March."
        actions={mayEdit ? (
          <button className="btn-secondary btn-sm" onClick={() => setDepOpen(true)}>
            <RefreshCw className="h-4 w-4" /> Run annual depreciation
          </button>
        ) : undefined}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <SearchInput value={term} onChange={setTerm} placeholder="Search tool, make, site…" />
          <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
                  options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
          <Select value={cls} onChange={(e) => setCls(e.target.value)} placeholder="All categories"
                  options={classes.map((c: any) => ({
                    value: c.code, label: `${c.label} (${c.useful_life_years} yr)` }))} />
        </div>
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load the tools register" message={error} /></div>}
        {!loading && rows.length === 0 && (
          <EmptyState icon={<Boxes className="h-9 w-9" />} title="No tools on the register" />
        )}
        {rows.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Asset ID</Th>
                  <Th id="tool_name" sort={sort} onSort={toggle}>Tool / tackle</Th>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="quantity" sort={sort} onSort={toggle} className="num" align="right">Qty</Th>
                  <Th id="rate_per_unit" sort={sort} onSort={toggle} className="num" align="right">Rate each</Th>
                  <Th id="opening_wdv" sort={sort} onSort={toggle} className="num" align="right">Audited value</Th>
                  <Th id="depreciation_to_date" sort={sort} onSort={toggle} className="num" align="right">Depreciation</Th>
                  <Th id="current_wdv" sort={sort} onSort={toggle} className="num" align="right">Book value</Th>
                  <Th id="dep_class_label" sort={sort} onSort={toggle}>Category</Th>
                  <Th id="wdv_rate_pct" sort={sort} onSort={toggle} className="num" align="right">Rate</Th>
                  <Th id="calibration_due_date" sort={sort} onSort={toggle}>Calibration</Th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                  {mayEdit && <th className="col-actions whitespace-nowrap">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map((x: any) => (
                  <tr key={x.id}>
                    <td className="font-mono text-xs">{x.id}</td>
                    <td className="text-sm font-medium">{x.tool_name}</td>
                    <td className="text-xs">{x.site_name}</td>
                    <td className="num text-xs">{x.quantity}</td>
                    <td className="num text-xs">{rupees(x.rate_per_unit)}</td>
                    <td className="num text-xs">{rupees(x.opening_wdv)}</td>
                    <td className="num text-xs text-amber-700">{rupees(x.depreciation_to_date)}</td>
                    <td className="num text-xs font-semibold">{rupees(x.current_wdv)}</td>
                    <td className="text-xs text-slate-500" title={x.dep_class_ref ?? ''}>
                      {x.dep_class_label}
                    </td>
                    <td className="num text-xs">{Number(x.wdv_rate_pct).toFixed(3)}%</td>
                    <td className="text-xs">
                      {x.calibration_state === 'OVERDUE'
                        ? <span className="pill-red">Overdue</span>
                        : x.calibration_state === 'DUE SOON'
                          ? <span className="pill-amber">Due soon</span>
                          : x.calibration_state === 'OK'
                            ? <span className="pill-green">{ddmmyyyy(x.calibration_due_date)}</span>
                            : <span className="text-slate-400">—</span>}
                    </td>
                    <td>
                      <span className={x.status === 'Active' ? 'pill-green'
                        : x.status === 'Breakdown' ? 'pill-amber' : 'pill-grey'}>{x.status}</span>
                    </td>
                    {mayEdit && (
                      <td className="col-actions">
                        <div className="flex items-center gap-0.5">
                          <button className="btn-ghost btn-sm" onClick={() => setEditing(x)}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <TransferButton category="tools" item={x} onDone={refresh} />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <ToolsForm tool={editing} classes={classes}
                   onClose={() => setEditing(null)} onDone={refresh} />
      )}

      <Modal open={depOpen} onClose={() => setDepOpen(false)}
             title="Run the annual depreciation"
             subtitle="Once a year, at the financial year end"
             footer={<>
               <button className="btn-secondary" onClick={() => setDepOpen(false)} disabled={busy}>Cancel</button>
               <button className="btn-primary" disabled={busy} onClick={async () => {
                 setBusy(true)
                 const { data: res, error: err } =
                   await supabase.rpc('rpc_run_tools_depreciation', { p_fy_end: fyEnd })
                 setBusy(false)
                 if (err) { toast.error(err.message); return }
                 const r = Array.isArray(res) ? res[0] : res
                 toast.success(`${r?.tools_charged ?? 0} line(s) depreciated by ${rupees(r?.total_depreciation ?? 0)}.`)
                 setDepOpen(false); refresh()
               }}>Run it</button>
             </>}>
        <div className="space-y-3">
          <Alert tone="info">
            Each line depreciates at its own Schedule II rate — 5 years for hand power
            tools, 10 for survey and testing instruments, 12 for concreting and welding
            equipment, 15 for generators and pumps. A line already charged for that year
            is skipped, so pressing this twice is harmless.
          </Alert>
          <Field label="Financial year end" required hint="31 March only. The first run is 31-03-2027.">
            <Input type="date" value={fyEnd} onChange={(e) => setFyEnd(e.target.value)} />
          </Field>
        </div>
      </Modal>
    </div>
  )
}

function ToolsForm({ tool, classes, onClose, onDone }: {
  tool: any; classes: any[]; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const [f, setF] = useState<any>({ ...tool })
  const [busy, setBusy] = useState(false)
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }))

  async function save() {
    const wdv = Number(f.current_wdv)
    if (!Number.isFinite(wdv) || wdv < 0) { toast.error('The written down value must be a number, and not negative.'); return }
    if (wdv < Number(tool.residual_value)) {
      toast.error(`The written down value cannot go below the 5% residual of ${rupees(tool.residual_value)}.`); return
    }
    setBusy(true)
    const { error } = await supabase.from('asset_tools').update({
      current_wdv: wdv,
      status: f.status,
      make_model: f.make_model || null,
      serial_no: f.serial_no || null,
      calibration_due_date: f.calibration_due_date || null,
      warranty_expiry: f.warranty_expiry || null,
      remarks: f.remarks || null,
    }).eq('id', tool.id)
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success('Tool line updated.')
    onClose(); onDone()
  }

  const cls = classes.find((c: any) => c.code === tool.dep_class_code)

  return (
    <Modal open wide onClose={onClose}
           title={`${tool.tool_name} — ${tool.quantity} nos`}
           subtitle={`${tool.site_name} · ${rupees(tool.rate_per_unit)} each · audited value ${rupees(tool.opening_wdv)} as at 31.08.2026`}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>Save</button>
           </>}>
      <div className="space-y-4">
        <Alert tone="info">
          <strong>{cls?.label ?? tool.dep_class_label}</strong> — {tool.useful_life_years} year
          life, {Number(tool.wdv_rate_pct).toFixed(3)}% written down value.
          {cls?.schedule_ref ? ` Companies Act 2013 Schedule II ${cls.schedule_ref}.` : ''}
          {' '}Quantity and unit rate are not edited here: the line value must always equal
          rate × quantity, and changing either would break that. Tell the administrator if
          the count is wrong.
        </Alert>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Written down value" required
                 hint="Changing this is recorded in the audit trail with your name.">
            <Input type="number" step="any" value={f.current_wdv ?? ''}
                   onChange={(e) => set('current_wdv', e.target.value)} />
          </Field>
          <Field label="Status">
            <Select value={f.status} onChange={(e) => set('status', e.target.value)}
                    options={['Active', 'Breakdown', 'Under Repair', 'Idle', 'Disposed']
                      .map((s) => ({ value: s, label: s }))} />
          </Field>
          <Field label="Make / model">
            <Input value={f.make_model ?? ''} onChange={(e) => set('make_model', e.target.value)} />
          </Field>
          <Field label="Serial number">
            <Input value={f.serial_no ?? ''} onChange={(e) => set('serial_no', e.target.value)} />
          </Field>
          <Field label="Calibration due"
                 hint="Total station, auto levels and cube testing machines">
            <Input type="date" value={f.calibration_due_date ?? ''}
                   onChange={(e) => set('calibration_due_date', e.target.value)} />
          </Field>
          <Field label="Warranty expires">
            <Input type="date" value={f.warranty_expiry ?? ''}
                   onChange={(e) => set('warranty_expiry', e.target.value)} />
          </Field>
          <Field label="Remarks" className="sm:col-span-2">
            <Textarea rows={2} value={f.remarks ?? ''} onChange={(e) => set('remarks', e.target.value)} />
          </Field>
        </div>
        {tool.source_note && (
          <p className="text-xs text-slate-500">
            <strong>From the 31.08.2026 workbook:</strong> {tool.source_note}
          </p>
        )}
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Machineries & Equipment — fixed asset register, category 2 of 7
// ---------------------------------------------------------------------------
// Unlike vehicles, every machine carries its OWN Schedule II class. A JCB
// depreciates over 9 years, an Ajax mixer over 12, a DG set over 15, a cube
// mould over 10. The class is shown on screen so the Head of Stores can see
// which rate a machine is on without opening anything.
// ---------------------------------------------------------------------------
function MachineryRegister() {
  const { profile } = useAuth()
  const toast = useToast()
  const role = profile!.role
  const mayEdit = can(role, 'manageAssets')

  const [term, setTerm] = useState('')
  const [site, setSite] = useState('')
  const [cls, setCls] = useState('')
  const [editing, setEditing] = useState<any>(null)
  const [depOpen, setDepOpen] = useState(false)
  const [fyEnd, setFyEnd] = useState('2027-03-31')
  const [busy, setBusy] = useState(false)

  const { data: sites } = useSites()
  const { data: classes } = useQuery<any>('asset_dep_classes', () =>
    supabase.from('asset_dep_classes').select('*').order('code'))
  const { data, loading, error, refresh } = useQuery<any>('asset_machinery', () => {
    let q = supabase.from('v_asset_machinery').select('*')
    if (site) q = q.eq('site_id', site)
    if (cls) q = q.eq('dep_class_code', cls)
    return q.order('id')
  }, [site, cls], { idField: 'id' })

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return data
    return data.filter((m: any) =>
      (m.machine_name ?? '').toLowerCase().includes(t) ||
      (m.make_model ?? '').toLowerCase().includes(t) ||
      (m.registration_no ?? '').toLowerCase().includes(t) ||
      (m.site_name ?? '').toLowerCase().includes(t) ||
      (m.id ?? '').toLowerCase().includes(t))
  }, [data, term])

  const audited = rows.reduce((a: number, r: any) => a + Number(r.opening_wdv || 0), 0)
  const current = rows.reduce((a: number, r: any) => a + Number(r.current_wdv || 0), 0)
  const units = rows.reduce((a: number, r: any) => a + Number(r.quantity || 0), 0)
  const down = rows.filter((r: any) => r.status !== 'Active').length
  const { sorted, sort, toggle } = useAssetSort(rows)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Machines" value={`${rows.length} rows · ${units} units`} icon={<Boxes className="h-5 w-5" />} />
        <Stat label="Audited value 31.08.2026" value={rupees(audited)} />
        <Stat label="Written down value today" value={rupees(current)} tone="good" />
        <Stat label="Not in use" value={down} tone={down ? 'warn' : undefined} />
      </div>

      <Card
        title="Machinery & equipment register"
        subtitle="Opening values are the CA-audited valuation as at 31.08.2026. Each machine depreciates at its own Schedule II rate, once a year on 31 March."
        actions={mayEdit ? (
          <button className="btn-secondary btn-sm" onClick={() => setDepOpen(true)}>
            <RefreshCw className="h-4 w-4" /> Run annual depreciation
          </button>
        ) : undefined}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <SearchInput value={term} onChange={setTerm}
                       placeholder="Search machine, make, registration, site…" />
          <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
                  options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
          <Select value={cls} onChange={(e) => setCls(e.target.value)} placeholder="All categories"
                  options={classes.map((c: any) => ({
                    value: c.code, label: `${c.label} (${c.useful_life_years} yr)` }))} />
        </div>
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load the machinery register" message={error} /></div>}
        {!loading && rows.length === 0 && (
          <EmptyState icon={<Boxes className="h-9 w-9" />} title="No machinery on the register" />
        )}
        {rows.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Asset ID</Th>
                  <Th id="machine_name" sort={sort} onSort={toggle}>Machine</Th>
                  <Th id="make_model" sort={sort} onSort={toggle}>Make</Th>
                  <Th id="registration_no" sort={sort} onSort={toggle}>Registration</Th>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="quantity" sort={sort} onSort={toggle} className="num" align="right">Qty</Th>
                  <Th id="in_service_date" sort={sort} onSort={toggle}>In service</Th>
                  <Th id="opening_wdv" sort={sort} onSort={toggle} className="num" align="right">Audited value</Th>
                  <Th id="depreciation_to_date" sort={sort} onSort={toggle} className="num" align="right">Depreciation</Th>
                  <Th id="current_wdv" sort={sort} onSort={toggle} className="num" align="right">Book value</Th>
                  <Th id="dep_class_label" sort={sort} onSort={toggle}>Category</Th>
                  <Th id="wdv_rate_pct" sort={sort} onSort={toggle} className="num" align="right">Rate</Th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                  {mayEdit && <th className="col-actions whitespace-nowrap">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map((m: any) => (
                  <tr key={m.id}>
                    <td className="font-mono text-xs">{m.id}</td>
                    <td className="text-sm font-medium">{m.machine_name}</td>
                    <td className="text-xs text-slate-500">{m.make_model ?? '—'}</td>
                    <td className="font-mono text-xs">{m.registration_no ?? '—'}</td>
                    <td className="text-xs">{m.site_name}</td>
                    <td className="num text-xs">{m.quantity}</td>
                    <td className="text-xs">{ddmmyyyy(m.in_service_date)}</td>
                    <td className="num text-xs">{rupees(m.opening_wdv)}</td>
                    <td className="num text-xs text-amber-700">{rupees(m.depreciation_to_date)}</td>
                    <td className="num text-xs font-semibold">{rupees(m.current_wdv)}</td>
                    <td className="text-xs text-slate-500" title={m.dep_class_ref ?? ''}>
                      {m.dep_class_label}
                    </td>
                    <td className="num text-xs">{Number(m.wdv_rate_pct).toFixed(3)}%</td>
                    <td>
                      <span className={m.status === 'Active' ? 'pill-green'
                        : m.status === 'Breakdown' ? 'pill-amber' : 'pill-grey'}>{m.status}</span>
                    </td>
                    {mayEdit && (
                      <td className="col-actions">
                        <div className="flex items-center gap-0.5">
                          <button className="btn-ghost btn-sm" onClick={() => setEditing(m)}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <TransferButton category="machinery" item={m} onDone={refresh} />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <MachineryForm machine={editing} classes={classes}
                       onClose={() => setEditing(null)} onDone={refresh} />
      )}

      <Modal open={depOpen} onClose={() => setDepOpen(false)}
             title="Run the annual depreciation"
             subtitle="Once a year, at the financial year end"
             footer={<>
               <button className="btn-secondary" onClick={() => setDepOpen(false)} disabled={busy}>Cancel</button>
               <button className="btn-primary" disabled={busy} onClick={async () => {
                 setBusy(true)
                 const { data: res, error: err } =
                   await supabase.rpc('rpc_run_machinery_depreciation', { p_fy_end: fyEnd })
                 setBusy(false)
                 if (err) { toast.error(err.message); return }
                 const r = Array.isArray(res) ? res[0] : res
                 toast.success(`${r?.machines_charged ?? 0} machine(s) depreciated by ${rupees(r?.total_depreciation ?? 0)}.`)
                 setDepOpen(false); refresh()
               }}>Run it</button>
             </>}>
        <div className="space-y-3">
          <Alert tone="info">
            Every machine is depreciated from its last charge to the year end you give,
            <strong> at its own Schedule II rate</strong> — 9 years for earth-moving,
            12 for concreting and pipeline, 15 for cranes and DG sets, 10 for testing
            equipment, 5 for small tools. A machine already charged for that year is
            skipped, so pressing this twice is harmless.
          </Alert>
          <Field label="Financial year end" required hint="31 March only. The first run is 31-03-2027.">
            <Input type="date" value={fyEnd} onChange={(e) => setFyEnd(e.target.value)} />
          </Field>
        </div>
      </Modal>
    </div>
  )
}

function MachineryForm({ machine, classes, onClose, onDone }: {
  machine: any; classes: any[]; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const [f, setF] = useState<any>({ ...machine })
  const [busy, setBusy] = useState(false)
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }))

  async function save() {
    const wdv = Number(f.current_wdv)
    if (!Number.isFinite(wdv) || wdv < 0) { toast.error('The written down value must be a number, and not negative.'); return }
    if (wdv < Number(machine.residual_value)) {
      toast.error(`The written down value cannot go below the 5% residual of ${rupees(machine.residual_value)}.`); return
    }
    setBusy(true)
    const { error } = await supabase.from('asset_machinery').update({
      current_wdv: wdv,
      status: f.status,
      capacity_rating: f.capacity_rating || null,
      serial_no: f.serial_no || null,
      last_service_date: f.last_service_date || null,
      next_service_due: f.next_service_due || null,
      remarks: f.remarks || null,
    }).eq('id', machine.id)
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success('Machine updated.')
    onClose(); onDone()
  }

  const cls = classes.find((c: any) => c.code === machine.dep_class_code)

  return (
    <Modal open wide onClose={onClose}
           title={`${machine.machine_name}${machine.registration_no ? ' — ' + machine.registration_no : ''}`}
           subtitle={`${machine.site_name} · audited value ${rupees(machine.opening_wdv)} as at 31.08.2026`}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>Save</button>
           </>}>
      <div className="space-y-4">
        <Alert tone="info">
          <strong>{cls?.label ?? machine.dep_class_label}</strong> — {machine.useful_life_years} year
          life, {Number(machine.wdv_rate_pct).toFixed(3)}% written down value.
          {cls?.schedule_ref ? ` Companies Act 2013 Schedule II ${cls.schedule_ref}.` : ''}
          {' '}The category is fixed when the machine goes on the register and is not edited here —
          Schedule II sets an asset's life at the point it is put to use.
        </Alert>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Written down value" required
                 hint="Changing this is recorded in the audit trail with your name.">
            <Input type="number" step="any" value={f.current_wdv ?? ''}
                   onChange={(e) => set('current_wdv', e.target.value)} />
          </Field>
          <Field label="Status">
            <Select value={f.status} onChange={(e) => set('status', e.target.value)}
                    options={['Active', 'Breakdown', 'Under Repair', 'Idle', 'Disposed']
                      .map((s) => ({ value: s, label: s }))} />
          </Field>
          <Field label="Capacity / rating" hint="e.g. 25 KVA, 4500 litres, 14 ton">
            <Input value={f.capacity_rating ?? ''} onChange={(e) => set('capacity_rating', e.target.value)} />
          </Field>
          <Field label="Serial number">
            <Input value={f.serial_no ?? ''} onChange={(e) => set('serial_no', e.target.value)} />
          </Field>
          <Field label="Last serviced">
            <Input type="date" value={f.last_service_date ?? ''}
                   onChange={(e) => set('last_service_date', e.target.value)} />
          </Field>
          <Field label="Next service due">
            <Input type="date" value={f.next_service_due ?? ''}
                   onChange={(e) => set('next_service_due', e.target.value)} />
          </Field>
          <Field label="Remarks" className="sm:col-span-2">
            <Textarea rows={2} value={f.remarks ?? ''} onChange={(e) => set('remarks', e.target.value)} />
          </Field>
        </div>
        {machine.source_note && (
          <p className="text-xs text-slate-500">
            <strong>From the 31.08.2026 workbook:</strong> {machine.source_note}
          </p>
        )}
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Vehicles — fixed asset register, category 1 of 7
// ---------------------------------------------------------------------------
function VehicleRegister() {
  const { profile } = useAuth()
  const toast = useToast()
  const role = profile!.role
  const mayEdit = can(role, 'manageAssets')

  const [term, setTerm] = useState('')
  const [site, setSite] = useState('')
  const [editing, setEditing] = useState<any>(null)
  const [depOpen, setDepOpen] = useState(false)
  const [fyEnd, setFyEnd] = useState('2027-03-31')
  const [busy, setBusy] = useState(false)

  const { data: sites } = useSites()
  const { data, loading, error, refresh } = useQuery<any>('asset_vehicles', () => {
    let q = supabase.from('v_asset_vehicles').select('*')
    if (site) q = q.eq('site_id', site)
    return q.order('id')
  }, [site], { idField: 'id' })

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return data
    return data.filter((v: any) =>
      (v.registration_no ?? '').toLowerCase().includes(t) ||
      (v.vehicle_type ?? '').toLowerCase().includes(t) ||
      (v.make_model ?? '').toLowerCase().includes(t) ||
      (v.site_name ?? '').toLowerCase().includes(t) ||
      (v.id ?? '').toLowerCase().includes(t))
  }, [data, term])

  const audited = rows.reduce((a: number, r: any) => a + Number(r.opening_wdv || 0), 0)
  const current = rows.reduce((a: number, r: any) => a + Number(r.current_wdv || 0), 0)
  const broken = rows.filter((r: any) => r.status === 'Breakdown').length
  const { sorted, sort, toggle } = useAssetSort(rows)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Vehicles" value={rows.length} icon={<Truck className="h-5 w-5" />} />
        <Stat label="Audited value 31.08.2026" value={rupees(audited)} />
        <Stat label="Written down value today" value={rupees(current)} tone="good" />
        <Stat label="Off the road" value={broken} tone={broken ? 'warn' : undefined} />
      </div>

      <Card
        title="Vehicle register"
        subtitle="Opening values are the CA-audited valuation as at 31.08.2026. Depreciation runs once a year on 31 March."
        actions={mayEdit ? (
          <button className="btn-secondary btn-sm" onClick={() => setDepOpen(true)}>
            <RefreshCw className="h-4 w-4" /> Run annual depreciation
          </button>
        ) : undefined}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <SearchInput value={term} onChange={setTerm}
                       placeholder="Search registration, type, make, site…" />
          <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
                  options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
        </div>
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load the vehicle register" message={error} /></div>}
        {!loading && rows.length === 0 && (
          <EmptyState icon={<Truck className="h-9 w-9" />} title="No vehicles on the register" />
        )}
        {rows.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Asset ID</Th>
                  <Th id="vehicle_type" sort={sort} onSort={toggle}>Type</Th>
                  <Th id="make_model" sort={sort} onSort={toggle}>Make / model</Th>
                  <Th id="registration_no" sort={sort} onSort={toggle}>Registration</Th>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="in_service_date" sort={sort} onSort={toggle}>In service</Th>
                  <Th id="opening_wdv" sort={sort} onSort={toggle} className="num" align="right">Audited value</Th>
                  <Th id="depreciation_to_date" sort={sort} onSort={toggle} className="num" align="right">Depreciation</Th>
                  <Th id="current_wdv" sort={sort} onSort={toggle} className="num" align="right">Book value</Th>
                  <Th id="insurance_expiry" sort={sort} onSort={toggle}>Insurance</Th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                  {mayEdit && <th className="col-actions whitespace-nowrap">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map((v: any) => (
                  <tr key={v.id}>
                    <td className="font-mono text-xs">{v.id}</td>
                    <td className="text-sm font-medium">{v.vehicle_type}</td>
                    <td className="text-xs text-slate-500">{v.make_model ?? '—'}</td>
                    <td className="font-mono text-xs">{v.registration_no ?? '—'}</td>
                    <td className="text-xs">{v.site_name}</td>
                    <td className="text-xs">{ddmmyyyy(v.in_service_date)}</td>
                    <td className="num text-xs">{rupees(v.opening_wdv)}</td>
                    <td className="num text-xs text-amber-700">{rupees(v.depreciation_to_date)}</td>
                    <td className="num text-xs font-semibold">{rupees(v.current_wdv)}</td>
                    <td className="text-xs">
                      {v.insurance_state === 'EXPIRED'
                        ? <span className="pill-red">Expired</span>
                        : v.insurance_state === 'DUE SOON'
                          ? <span className="pill-amber">Due soon</span>
                          : v.insurance_state === 'OK'
                            ? <span className="pill-green">{ddmmyyyy(v.insurance_expiry)}</span>
                            : <span className="text-slate-400">—</span>}
                    </td>
                    <td>
                      <span className={v.status === 'Active' ? 'pill-green'
                        : v.status === 'Breakdown' ? 'pill-amber' : 'pill-grey'}>{v.status}</span>
                    </td>
                    {mayEdit && (
                      <td className="col-actions">
                        <div className="flex items-center gap-0.5">
                          <button className="btn-ghost btn-sm" onClick={() => setEditing(v)}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <TransferButton category="vehicles" item={v} onDone={refresh} />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <VehicleForm vehicle={editing} onClose={() => setEditing(null)} onDone={refresh} />
      )}

      <Modal open={depOpen} onClose={() => setDepOpen(false)}
             title="Run the annual depreciation"
             subtitle="Once a year, at the financial year end"
             footer={<>
               <button className="btn-secondary" onClick={() => setDepOpen(false)} disabled={busy}>Cancel</button>
               <button className="btn-primary" disabled={busy} onClick={async () => {
                 setBusy(true)
                 const { data: res, error: err } =
                   await supabase.rpc('rpc_run_vehicle_depreciation', { p_fy_end: fyEnd })
                 setBusy(false)
                 if (err) { toast.error(err.message); return }
                 const r = Array.isArray(res) ? res[0] : res
                 toast.success(`${r?.vehicles_charged ?? 0} vehicle(s) depreciated by ${rupees(r?.total_depreciation ?? 0)}.`)
                 setDepOpen(false); refresh()
               }}>Run it</button>
             </>}>
        <div className="space-y-3">
          <Alert tone="info">
            Every vehicle is depreciated from its last charge to the year end you give,
            at 31.230% written down value — Schedule II, motor vehicles, 8 year life.
            A vehicle already charged for that year is skipped, so pressing this twice
            is harmless.
          </Alert>
          <Field label="Financial year end" required hint="31 March only. The first run is 31-03-2027.">
            <Input type="date" value={fyEnd} onChange={(e) => setFyEnd(e.target.value)} />
          </Field>
        </div>
      </Modal>
    </div>
  )
}

// The Head of Stores keeps this up to date. The written down value is editable
// on purpose — the board's decision — and every change to it is written to the
// audit trail by trg_asset_vehicle_wdv_audit.
function VehicleForm({ vehicle, onClose, onDone }: {
  vehicle: any; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const [f, setF] = useState<any>({ ...vehicle })
  const [busy, setBusy] = useState(false)
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }))

  async function save() {
    const wdv = Number(f.current_wdv)
    if (!Number.isFinite(wdv) || wdv < 0) { toast.error('The written down value must be a number, and not negative.'); return }
    if (wdv < Number(vehicle.residual_value)) {
      toast.error(`The written down value cannot go below the 5% residual of ${rupees(vehicle.residual_value)}.`); return
    }
    setBusy(true)
    const { error } = await supabase.from('asset_vehicles').update({
      current_wdv: wdv,
      status: f.status,
      chassis_no: f.chassis_no || null,
      engine_no: f.engine_no || null,
      insurance_expiry: f.insurance_expiry || null,
      fitness_expiry: f.fitness_expiry || null,
      permit_expiry: f.permit_expiry || null,
      puc_expiry: f.puc_expiry || null,
      remarks: f.remarks || null,
    }).eq('id', vehicle.id)
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success('Vehicle updated.')
    onClose(); onDone()
  }

  return (
    <Modal open wide onClose={onClose}
           title={`${vehicle.vehicle_type} — ${vehicle.registration_no ?? vehicle.id}`}
           subtitle={`${vehicle.site_name} · audited value ${rupees(vehicle.opening_wdv)} as at 31.08.2026`}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>Save</button>
           </>}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Written down value" required
               hint="Changing this is recorded in the audit trail with your name.">
          <Input type="number" step="any" value={f.current_wdv ?? ''}
                 onChange={(e) => set('current_wdv', e.target.value)} />
        </Field>
        <Field label="Status">
          <Select value={f.status} onChange={(e) => set('status', e.target.value)}
                  options={['Active', 'Breakdown', 'Under Repair', 'Idle', 'Disposed']
                    .map((s) => ({ value: s, label: s }))} />
        </Field>
        <Field label="Chassis number">
          <Input value={f.chassis_no ?? ''} onChange={(e) => set('chassis_no', e.target.value)} />
        </Field>
        <Field label="Engine number">
          <Input value={f.engine_no ?? ''} onChange={(e) => set('engine_no', e.target.value)} />
        </Field>
        <Field label="Insurance expires">
          <Input type="date" value={f.insurance_expiry ?? ''}
                 onChange={(e) => set('insurance_expiry', e.target.value)} />
        </Field>
        <Field label="Fitness expires">
          <Input type="date" value={f.fitness_expiry ?? ''}
                 onChange={(e) => set('fitness_expiry', e.target.value)} />
        </Field>
        <Field label="Permit expires">
          <Input type="date" value={f.permit_expiry ?? ''}
                 onChange={(e) => set('permit_expiry', e.target.value)} />
        </Field>
        <Field label="PUC expires">
          <Input type="date" value={f.puc_expiry ?? ''}
                 onChange={(e) => set('puc_expiry', e.target.value)} />
        </Field>
        <Field label="Remarks" className="sm:col-span-2">
          <Textarea rows={2} value={f.remarks ?? ''} onChange={(e) => set('remarks', e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}

function OtherAssets() {
  const { profile } = useAuth()
  const toast = useToast()
  const role = profile!.role
  const [term, setTerm] = useState('')
  const [site, setSite] = useState('')
  const [open, setOpen] = useState(false)
  const [depOpen, setDepOpen] = useState(false)

  const { data: sites } = useSites()
  const { data: categories } = useQuery<any>('asset_categories', () =>
    supabase.from('asset_categories').select('*').order('id'))
  const { data, loading, error, refresh } = useQuery<any>('assets', () => {
    let q = supabase.from('v_asset_register').select('*')
    if (site) q = q.eq('site_id', site)
    return q.order('site_name').order('asset_name')
  }, [site], { idField: 'asset_uid' })

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return data
    return data.filter((a: any) =>
      (a.asset_name ?? '').toLowerCase().includes(t) ||
      (a.asset_id ?? '').toLowerCase().includes(t) ||
      (a.serial_number ?? '').toLowerCase().includes(t) ||
      (a.assigned_to ?? '').toLowerCase().includes(t))
  }, [data, term])

  const grossBlock = rows.reduce((a: number, r: any) => a + Number(r.purchase_value || 0), 0)
  const netBlock = rows.reduce((a: number, r: any) => a + Number(r.current_book_value || 0), 0)
  const { sorted, sort, toggle } = useAssetSort(rows, 'asset_id')

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Assets" value={rows.length} icon={<Truck className="h-5 w-5" />} />
        <Stat label="Gross block" value={rupees(grossBlock)} />
        <Stat label="Net book value" value={rupees(netBlock)} tone="good" />
        <Stat label="Accumulated depreciation" value={rupees(grossBlock - netBlock)} tone="warn" />
      </div>

      <Card
        title="Fixed asset register"
        subtitle="Maintained by the Head of Stores"
        actions={<>
          <button className="btn-secondary btn-sm" onClick={() =>
            downloadFromFunction('export-report', { report: 'asset_register', filters: site ? { site_id: site } : {} }, 'asset_register.xlsx')}>
            <Download className="h-4 w-4" /> Export
          </button>
          {can(role, 'manageAssets') && <>
            <button className="btn-secondary btn-sm" onClick={() => setDepOpen(true)}>
              <RefreshCw className="h-4 w-4" /> Run depreciation
            </button>
            <button className="btn-primary btn-sm" onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" /> Add asset
            </button>
          </>}
        </>}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <SearchInput value={term} onChange={setTerm} placeholder="Search asset, serial number, custodian…" />
          <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
                  options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
        </div>
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load assets" message={error} /></div>}
        {!loading && rows.length === 0 && <EmptyState icon={<Truck className="h-9 w-9" />} title="No assets recorded" />}
        {rows.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="asset_id" sort={sort} onSort={toggle}>Asset ID</Th>
                  <Th id="asset_name" sort={sort} onSort={toggle}>Asset</Th>
                  <Th id="category_name" sort={sort} onSort={toggle}>Category</Th>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="serial_number" sort={sort} onSort={toggle}>Serial</Th>
                  <Th id="purchase_date" sort={sort} onSort={toggle}>Purchased</Th>
                  <Th id="purchase_value" sort={sort} onSort={toggle} className="num" align="right">Cost</Th>
                  <Th id="cumulative_depreciation" sort={sort} onSort={toggle} className="num" align="right">Accum. depn</Th>
                  <Th id="current_book_value" sort={sort} onSort={toggle} className="num" align="right">Book value</Th>
                  <Th id="assigned_to" sort={sort} onSort={toggle}>Custodian</Th>
                  <Th id="asset_status" sort={sort} onSort={toggle}>Status</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((a: any) => (
                  <tr key={a.asset_uid}>
                    <td className="font-mono text-xs">{a.asset_id}</td>
                    <td className="text-sm font-medium">{a.asset_name}</td>
                    <td className="text-xs text-slate-500">{a.category_name ?? a.category_id}</td>
                    <td className="text-xs">{a.site_name}</td>
                    <td className="font-mono text-xs text-slate-500">{a.serial_number ?? '—'}</td>
                    <td className="text-xs">{ddmmyyyy(a.purchase_date)}</td>
                    <td className="num text-xs">{rupees(a.purchase_value)}</td>
                    <td className="num text-xs text-amber-700">{rupees(a.cumulative_depreciation)}</td>
                    <td className="num text-xs font-semibold">{rupees(a.current_book_value)}</td>
                    <td className="max-w-[160px] truncate text-xs">{a.assigned_to ?? '—'}</td>
                    <td><span className={a.asset_status === 'Active' ? 'pill-green' : 'pill-grey'}>{a.asset_status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AssetForm open={open} onClose={() => setOpen(false)} sites={sites} categories={categories} onDone={refresh} />

      <Confirm
        open={depOpen}
        title="Run depreciation to today?"
        message="Every active asset is depreciated from its last run date to today using its category's method and rate. Each run is recorded separately, so the register can always be reconciled."
        confirmLabel="Yes, run depreciation"
        onCancel={() => setDepOpen(false)}
        onConfirm={async () => {
          const { data, error } = await supabase.rpc('rpc_run_depreciation', { p_as_of: iso(new Date()) })
          if (error) toast.error(error.message)
          else toast.success(`Depreciation posted for ${data} asset(s).`)
          setDepOpen(false); refresh()
        }}
      />
    </div>
  )
}

function AssetForm({ open, onClose, sites, categories, onDone }: {
  open: boolean; onClose: () => void; sites: any[]; categories: any[]; onDone: () => void
}) {
  const { profile } = useAuth()
  const toast = useToast()
  const [f, setF] = useState<any>({ asset_status: 'Active', asset_condition: 'Good', purchase_date: iso(new Date()) })
  const [busy, setBusy] = useState(false)
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }))

  async function save() {
    if (!f.asset_name || !f.site_id || !f.category_id) {
      toast.error('Asset name, site and category are required.'); return
    }
    setBusy(true)
    try {
      const uid = crypto.randomUUID().slice(0, 8)
      const stamp = new Date()
      const assetId = `AST-${stamp.getFullYear()}${String(stamp.getMonth() + 1).padStart(2, '0')}${String(stamp.getDate()).padStart(2, '0')}-${String(stamp.getHours()).padStart(2, '0')}${String(stamp.getMinutes()).padStart(2, '0')}${String(stamp.getSeconds()).padStart(2, '0')}`
      const cost = Number(f.purchase_value) || 0
      const cat = categories.find((c: any) => c.id === f.category_id)
      const residual = Math.round(cost * (Number(cat?.residual_value_pct ?? 5) / 100) * 100) / 100

      await enqueue({
        kind: 'asset.create', docType: 'ASSET',
        label: `Add asset ${f.asset_name}`,
        payload: {
          asset_uid: uid, asset_id: assetId,
          site_id: f.site_id, category_id: f.category_id,
          asset_name: f.asset_name, asset_description: f.asset_description ?? null,
          serial_number: f.serial_number ?? null, purchase_date: f.purchase_date,
          purchase_value: cost, residual_value: residual, current_book_value: cost,
          asset_condition: f.asset_condition, asset_status: f.asset_status,
          location_in_site: f.location_in_site ?? null, assigned_to: f.assigned_to ?? null,
          purchase_reference: f.purchase_reference ?? null, remarks: f.remarks ?? null,
          added_by: profile!.email, date_added: iso(new Date()),
        },
      })
      toast.success('Asset added to the register.')
      setF({ asset_status: 'Active', asset_condition: 'Good', purchase_date: iso(new Date()) })
      onClose(); onDone()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not add the asset')
    } finally { setBusy(false) }
  }

  return (
    <Modal open={open} onClose={onClose} wide title="Add a fixed asset"
           subtitle="Residual value defaults to the category percentage of cost"
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>Add asset</button>
           </>}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Asset name" required><Input value={f.asset_name ?? ''} onChange={(e) => set('asset_name', e.target.value)} /></Field>
        <Field label="Serial / identification number"><Input value={f.serial_number ?? ''} onChange={(e) => set('serial_number', e.target.value)} /></Field>
        <Field label="Site" required>
          <Select value={f.site_id ?? ''} onChange={(e) => set('site_id', e.target.value)} placeholder="Select site…"
                  options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
        </Field>
        <Field label="Category" required hint="Drives the depreciation method and rate">
          <Select value={f.category_id ?? ''} onChange={(e) => set('category_id', e.target.value)} placeholder="Select category…"
                  options={categories.map((c: any) => ({ value: c.id, label: `${c.category_name} (${c.depreciation_method} ${c.wdv_rate_pct}%)` }))} />
        </Field>
        <Field label="Purchase date" required><Input type="date" value={f.purchase_date ?? ''} onChange={(e) => set('purchase_date', e.target.value)} /></Field>
        <Field label="Purchase value" required><Input type="number" step="any" value={f.purchase_value ?? ''} onChange={(e) => set('purchase_value', e.target.value)} /></Field>
        <Field label="Condition">
          <Select value={f.asset_condition} onChange={(e) => set('asset_condition', e.target.value)}
                  options={['Excellent', 'Good', 'Fair', 'Poor'].map((c) => ({ value: c, label: c }))} />
        </Field>
        <Field label="Status">
          <Select value={f.asset_status} onChange={(e) => set('asset_status', e.target.value)}
                  options={['Active', 'Under Repair', 'Idle', 'Disposed'].map((c) => ({ value: c, label: c }))} />
        </Field>
        <Field label="Location within site"><Input value={f.location_in_site ?? ''} onChange={(e) => set('location_in_site', e.target.value)} /></Field>
        <Field label="Custodian"><Input value={f.assigned_to ?? ''} onChange={(e) => set('assigned_to', e.target.value)} /></Field>
        <Field label="Purchase reference"><Input value={f.purchase_reference ?? ''} onChange={(e) => set('purchase_reference', e.target.value)} placeholder="PO number or invoice" /></Field>
        <Field label="Description" className="sm:col-span-2"><Textarea rows={2} value={f.asset_description ?? ''} onChange={(e) => set('asset_description', e.target.value)} /></Field>
      </div>
    </Modal>
  )
}

// ===========================================================================
// Vendor payables
// ===========================================================================
export function Payables() {
  const { profile } = useAuth()
  const role = profile!.role
  const [status, setStatus] = useState('')
  const [term, setTerm] = useState('')
  // When a payable counts as overdue: a System Setting since 0184, 45 days
  // until the administrator changes it.
  const { payableOverdueDays: od } = useCompanySettings()

  const { data, loading, error } = useQuery<any>('vendor_payables', () => {
    // The settlement view knows the difference between what the receipt accrued
    // at purchase-order rates and what the vendor actually billed.
    //
    // ra_bill_id IS NULL, added 16-09-2026. A contractor payable comes from a
    // running account bill - no goods receipt, no purchase order, no accepted
    // quantity, no rejection - and none of the columns on this screen mean
    // anything for one. They have their own ledger on the Service side, and
    // the note under the search box says so rather than leaving people to
    // wonder where the money went.
    let q = supabase.from('v_payable_settlement').select('*').is('ra_bill_id', null)
    if (status) q = q.eq('status', status)
    return q.order('generated_at', { ascending: false }).limit(500)
  }, [status], { cacheable: false })

  /**
   * THE CARDS COUNT EVERY PAYABLE, NOT JUST THE ROWS ON SCREEN. 28-09-2026.
   *
   * The table below shows the latest 500, and the four cards used to add up
   * those same 500 rows - cancelled payables included. So "Total outstanding"
   * could disagree with Liabilities -> Total owed, which counts everything and
   * leaves cancelled payables out.
   *
   * This second query reads only the few columns the cards need, for EVERY
   * payable matching the status filter, a page of 1,000 at a time. The table
   * query above is untouched and still shows the latest 500.
   */
  const { data: allForCards, loading: cardsLoading } = useQuery<any>('vendor_payables_cards', async () => {
    const out: any[] = []
    for (let from = 0; ; from += 1000) {
      let q = supabase.from('v_payable_settlement')
        .select('id, vendor_name, vendor_gstin, site_name, grn_id, po_id, status, outstanding_amount, age_days')
        .is('ra_bill_id', null)
      if (status) q = q.eq('status', status)
      const { data: page, error: e } = await q.order('id').range(from, from + 999)
      if (e) return { data: null, error: e }
      out.push(...(page ?? []))
      if (!page || page.length < 1000) break
    }
    return { data: out, error: null }
  }, [status], { cacheable: false })

  // THE PAYABLE NUMBER IS SEARCHABLE. It was not until 16-09-2026, which is
  // how the CMD came to search for one by its number, get nothing back, and
  // reasonably conclude it did not exist - the number he typed is the first
  // column of the very table this filters.
  //
  // One test, used by the table and by the cards, so both answer the same
  // search the same way.
  const matchesTerm = (p: any, t: string) =>
    (p.id ?? '').toLowerCase().includes(t) ||
    (p.vendor_name ?? '').toLowerCase().includes(t) ||
    (p.vendor_gstin ?? '').toLowerCase().includes(t) ||
    (p.site_name ?? '').toLowerCase().includes(t) ||
    (p.grn_id ?? '').toLowerCase().includes(t) ||
    (p.po_id ?? '').toLowerCase().includes(t)

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (!t) return data
    return data.filter((p: any) => matchesTerm(p, t))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, term])

  // What the cards count: still owed, and not cancelled. The same rule as
  // v_liability_current, so with no filter and no search "Total outstanding"
  // equals Liabilities -> Total owed.
  const owing = useMemo(() => {
    const t = term.trim().toLowerCase()
    return allForCards.filter((p: any) =>
      p.status !== 'CANCELLED'
      && Number(p.outstanding_amount) > 0.01
      && (!t || matchesTerm(p, t)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allForCards, term])

  const { sort, toggle } = useSort('generated_at')
  const sortedRows = useMemo(() => sortRows(rows, sort, {
    taxable_amount: 'number', gst_amount: 'number', payable_amount: 'number',
    invoiced_amount: 'number', variance_amount: 'number', outstanding_amount: 'number',
    age_days: 'number', generated_at: 'date',
  }), [rows, sort])

  const outstanding = owing.reduce((a: number, r: any) => a + Number(r.outstanding_amount || 0), 0)
  const overdue = owing.filter((r: any) => Number(r.age_days) > od)
  const dash = cardsLoading ? '…' : null

  if (!isHoRole(role)) return <NotPermitted what="view vendor payables" who="head-office procurement and stores team" />

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Open payables" value={dash ?? owing.length} icon={<Wallet className="h-5 w-5" />}
              sub="Money still owed · cancelled and fully paid left out" />
        <Stat label="Total outstanding" value={dash ?? rupees(outstanding)} tone="warn"
              sub="Invoiced value less payments made" />
        <Stat label={`Over ${od} days`} value={dash ?? overdue.length} tone={overdue.length ? 'bad' : 'good'} />
        <Stat label={`Value over ${od} days`} value={dash ?? rupees(overdue.reduce((a: number, r: any) => a + Number(r.outstanding_amount || 0), 0))} />
      </div>

      <Card
        title="Vendor payables"
        subtitle="Raised automatically when the Head of Stores approves a goods receipt — accepted quantity only, rejections excluded. Works-contractor bills are NOT here: they are on Service Procurement → Contractor Payables, numbered CPAY-."
        actions={<button className="btn-secondary btn-sm" onClick={() =>
          downloadFromFunction('export-report', { report: 'vendor_payables', filters: {} }, 'vendor_payables.xlsx')}>
          <Download className="h-4 w-4" /> Export
        </button>}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <SearchInput value={term} onChange={setTerm}
                       placeholder="Payable number, vendor, GSTIN, site, GRN or PO…" />
          <Select value={status} onChange={(e) => setStatus(e.target.value)} placeholder="All statuses"
                  options={[
                    { value: 'OPEN', label: 'Open — awaiting invoice' },
                    { value: 'PARTIALLY_INVOICED', label: 'Partially invoiced' },
                    { value: 'INVOICED', label: 'Invoiced — awaiting payment' },
                    { value: 'PARTIALLY_PAID', label: 'Partially paid' },
                    { value: 'PAID', label: 'Paid' },
                    // 0228: approved on or before 31-10-2026, inside the Finance opening.
                    { value: 'IN_OPENING', label: 'Taken into opening (paid from Finance)' },
                  ]} />
        </div>
        {/* Added 28-09-2026. Only appears once there are more payables than
            the list shows, so nobody mistakes the cards for a sum of it. */}
        {!loading && !cardsLoading && data.length >= 500 && allForCards.length > data.length && (
          <p className="mt-2 text-xs text-slate-500">
            The list below shows the latest 500 of {allForCards.length} payables. The four cards above
            count all {allForCards.length}.
          </p>
        )}
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load payables" message={error} /></div>}
        {!loading && rows.length === 0 && (
          <EmptyState icon={<Wallet className="h-9 w-9" />} title="No payables yet"
                      message="A payable is created the moment a goods receipt is approved." />
        )}
        {rows.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Payable</Th><Th id="vendor_name" sort={sort} onSort={toggle}>Vendor</Th><Th id="grn_id" sort={sort} onSort={toggle}>Against GRN</Th><Th id="po_id" sort={sort} onSort={toggle}>PO</Th><Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="taxable_amount" sort={sort} onSort={toggle} className="num" align="right">Taxable</Th><Th id="gst_amount" sort={sort} onSort={toggle} className="num" align="right">GST</Th><Th id="payable_amount" sort={sort} onSort={toggle} className="num" align="right">Payable</Th>
                  <Th id="invoiced_amount" sort={sort} onSort={toggle} className="num" align="right">Invoiced</Th><Th id="variance_amount" sort={sort} onSort={toggle} className="num" align="right">Variance</Th>
                  <Th id="outstanding_amount" sort={sort} onSort={toggle} className="num" align="right">Outstanding</Th>
                  <Th id="age_days" sort={sort} onSort={toggle} className="num" align="right">Age</Th><Th id="status" sort={sort} onSort={toggle}>Status</Th><th />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((p: any) => (
                  <tr key={p.id} className={Number(p.age_days) > od ? 'bg-amber-50/60' : ''}>
                    <td className="font-mono text-xs">
                      <Link to={`/payables/${p.id}`} className="text-oval-700 hover:underline">{p.id}</Link>
                    </td>
                    <td className="max-w-[200px] truncate text-sm">{p.vendor_name}</td>
                    <td className="font-mono text-xs text-slate-500">{p.grn_id ?? '—'}</td>
                    <td className="font-mono text-xs text-slate-500">{p.po_id ?? '—'}</td>
                    <td className="text-xs">{p.site_name}</td>
                    <td className="num text-xs">{rupees(p.taxable_amount)}</td>
                    <td className="num text-xs">{rupees(p.gst_amount)}</td>
                    <td className="num text-xs font-semibold">{rupees(p.payable_amount)}</td>
                    <td className="num text-xs">{rupees(p.invoiced_amount)}</td>
                    {/*
                      Variance is "what the vendor billed, against what we
                      accrued". On a petty-cash purchase no bill is coming, so
                      that sum is invoiced 0 minus the full amount — which came
                      out as a red -100% on a receipt that is settled and needs
                      nothing from anybody. It read like a problem and sent you
                      looking for an invoice that does not exist.
                    */}
                    {p.settled_in_cash ? (
                      <td className="num text-xs text-slate-400" title="Paid in cash at the site — no vendor invoice is expected, so there is nothing to compare against.">
                        n/a — cash
                      </td>
                    ) : (
                      <td className={`num text-xs ${Math.abs(Number(p.variance_amount || 0)) > 0.01
                        ? (Number(p.variance_amount) > 0 ? 'font-semibold text-amber-700' : 'font-semibold text-emerald-700')
                        : 'text-slate-400'}`}>
                        {Math.abs(Number(p.variance_amount || 0)) > 0.01
                          ? `${rupees(p.variance_amount)} (${Number(p.variance_pct) > 0 ? '+' : ''}${p.variance_pct}%)`
                          : '—'}
                      </td>
                    )}
                    <td className={`num text-xs font-semibold ${Number(p.outstanding_amount) > 0.01 ? 'text-amber-700' : 'text-emerald-700'}`}>
                      {rupees(p.outstanding_amount)}
                    </td>
                    <td className={`num text-xs ${Number(p.age_days) > od ? 'font-bold text-red-600' : ''}`}>{p.age_days}d</td>
                    <td><StatusPill status={p.status} /></td>
                    <td className="text-right">
                      <Link to={`/payables/${p.id}`} className="btn-ghost btn-sm">Open</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

/**
 * One vendor-paper marker: is this document currently on file?
 *
 * Three columns draw the same three states, so the states are described once.
 * Written out three times, the day somebody changes what N looks like they
 * change two of them.
 *
 *   Y   a current, non-empty document is attached
 *   N   it should be there and is not
 *   —   the question does not apply (an unregistered vendor has no GST
 *       certificate), so it is neither a pass nor a thing to chase
 */
function PaperMark({ value, what }: { value: string; what: string }) {
  if (value === 'Y') {
    return <span className="pill-green" title={`A current ${what} is on file for this vendor`}>Y</span>
  }
  if (value === 'N') {
    return <span className="pill-amber"
                 title={`No current ${what} on file — a superseded or empty one does not count`}>N</span>
  }
  return <span className="text-xs text-slate-400"
               title="Not applicable — this vendor is not GST registered">—</span>
}

// ===========================================================================
// Master data
// ===========================================================================
export function Masters() {
  const { profile } = useAuth()
  const role = profile!.role
  const toast = useToast()

  // Two levels per master, straight from the board matrix: the LIST (the
  // table) and the DETAIL (opening one row). They are not the same thing - a
  // Project Manager may see every material and open none of them.
  const mayEdit: Record<string, boolean> =
    Object.fromEntries(MASTER_TABS.map((t) => [t, masterListAccess(role, t) === 'CRUD']))
  const mayOpen: Record<string, string> =
    Object.fromEntries(MASTER_TABS.map((t) => [t, masterDetailAccess(role, t)]))
  const visibleTabs = visibleMasterTabs(role)
  // "View - except base rate" for the three site-facing roles.
  const showBaseRate = maySeeBaseRate(role)

  // The opening tab is the first one this person may open - not always
  // 'materials'. Landing on a tab that is not in the strip showed an empty
  // screen with nothing selected.
  const [tab, setTab] = useState<MasterTab>(() => visibleTabs[0] ?? 'materials')
  // 0233: only the System Administrator approves a material's cash-flow values.
  const isSuper = useIsSuperadminFlag()
  const { data: materials, loading: ml, refresh: refreshMat } = useMaterials()
  const { data: vendors, loading: vl, refresh: refreshVend } = useVendors()

  /**
   * INACTIVE RECORDS STAY ON THIS SCREEN. 29-09-2026.
   *
   * useMaterials() and useVendors() return ACTIVE rows only - correctly, for
   * the purchase-order builder, the goods receipt and every other screen that
   * must never offer a switched-off vendor. But this is the screen where a
   * record is switched off, and it is the only place it can be switched back
   * on. Reading the same active-only list here made a deactivated vendor
   * vanish, with no way to reopen it.
   *
   * So the two tables below read their own complete lists, under their own
   * cache names. The shared hooks, and the 18 screens that use them, are
   * untouched. `materials` and `vendors` above stay active-only for the
   * places on this screen that want exactly that (units in use, for one).
   */
  const { data: allMaterials, loading: aml, refresh: refreshAllMat } = useQuery<any>(
    'masters_materials_all', () => supabase.from('materials').select('*').order('item_name'))
  const { data: allVendors, loading: avl, refresh: refreshAllVend } = useQuery<any>(
    'masters_vendors_all', () => supabase.from('vendors').select('*').order('company_name'))
  const [showActive, setShowActive] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL')
  const byActive = (rows: any[]) =>
    showActive === 'ALL' ? rows
      : rows.filter((r) => (r.is_active !== false) === (showActive === 'ACTIVE'))
  const inactiveCount = (rows: any[]) => rows.filter((r) => r.is_active === false).length

  const { data: sites, refresh: refreshSites } = useSites()
  const { data: uoms, refresh: refreshUoms } = useQuery<any>('uoms', () =>
    supabase.from('uoms').select('*').order('sort_order').order('code'))
  const { data: poTerms, refresh: refreshTerms } =
    useQuery<any>('po_terms', () => supabase.from('po_terms').select('*').order('sort_order'))
  const { data: categories, refresh: refreshCats } =
    useQuery<any>('category_master', () =>
      supabase.from('category_master').select('*').order('domain').order('sort_order').order('name'))

  /**
   * Which papers each vendor has CURRENTLY on file — the three Y/N columns on
   * the Vendors tab: bank mandate, GST certificate, PAN.
   *
   * A SEPARATE query rather than a change to useVendors(), which feeds the
   * purchase-order builder, the goods-receipt screen, every payables screen
   * and the vendor form. Widening it to serve three columns on one tab would
   * put a join in front of all of them for no benefit.
   *
   * The view reports booleans and nothing else — no file, no number. That is
   * what lets the Head of Stores, the Project Manager and the Project Head see
   * the columns at all: they can read the vendor list but may not open a
   * vendor document, and a query against document_attachments would have
   * returned N for every vendor in the company without saying why.
   */
  const { data: vendorPapers } = useQuery<any>('v_vendor_bank_doc', () =>
    supabase.from('v_vendor_bank_doc')
      .select('vendor_id, has_bank_doc, has_gst_doc, gst_applicable, has_pan_doc'),
    [], { cacheable: false, idField: 'vendor_id' })
  const papersById = useMemo(
    () => Object.fromEntries(vendorPapers.map((p: any) => [p.vendor_id, p])) as Record<string, any>,
    [vendorPapers])

  const [term, setTerm] = useState('')

  // "Where is this material?" (11-09-2026). A button on each material row that
  // opens the site-wise distribution: on hand, value, coming in, asked for,
  // stock health. Head of Stores, Head of Procurement, Project Head, CMD, CXO
  // and the administrator only; for every other role this loads nothing and
  // the list is exactly as it was. See components/masters/MaterialDistribution.
  const mayLocate = can(role, 'viewMaterialDistribution')
  const dist = useMaterialDistribution(mayLocate, sites)
  const [locating, setLocating] = useState<any | null>(null)

  // "Orders awaiting delivery" (12-09-2026): the same idea on the Vendors tab.
  // A button on each vendor row carrying the number of purchase orders that
  // vendor has acknowledged and not yet delivered; pressing it lists them.
  // See components/masters/VendorOrders.
  const mayVendorOrders = can(role, 'viewVendorOrders')
  const ack = useVendorAckOrders(mayVendorOrders, sites)
  const [vendorOrders, setVendorOrders] = useState<any | null>(null)

  const OWNER: Record<string, string> = {
    materials: 'Head of Stores',
    vendors:   'Procurement Executive or Procurement Manager',
    // Sysadm alone under the board resolution of 31 Aug 2026. Procurement
            // dropped to view, so naming them here would send people to somebody
            // who can no longer help.
    sites:     'System Administrator',
    uoms:      'Head of Stores, Procurement Manager or Procurement Executive',
    terms:     'Procurement Executive or Procurement Manager',
    categories: 'Head of Stores or Procurement',
    settings:  'System Administrator',
  }

  // Each tab keeps its own sort, so switching tabs does not carry a column
  // that only makes sense on another table.
  const matSort  = useSort('item_name', 'asc')
  const vendSort = useSort('company_name', 'asc')
  const siteSort = useSort('site_name', 'asc')
  const termSort = useSort('sort_order', 'asc')

  const [editing, setEditing] = useState<{ kind: string; row: any; copyOf?: any } | null>(null)
  // Read-only record, for every master and every role the board gave View
  // rather than CRUD. A separate piece of state from `editing` on purpose: the
  // edit form can save, and a viewer must never be one prop away from a Save
  // button.
  const [viewing, setViewing] = useState<{ kind: MasterTab; row: any } | null>(null)

  // The one row action, decided once for all six tables.
  //   CRUD -> pencil    VIEW -> eye    NONE -> nothing at all
  const rowAction = (kind: MasterTab, row: any) =>
    mayOpen[kind] === 'CRUD'
      ? (<button className="btn-ghost btn-sm" title="Edit"
                 onClick={() => setEditing({ kind, row })}>
           <Pencil className="h-4 w-4" />
         </button>)
      : mayOpen[kind] === 'VIEW'
        ? (<button className="btn-ghost btn-sm" title="Open the full record"
                   onClick={() => setViewing({ kind, row })}>
             <Eye className="h-4 w-4" />
           </button>)
        : null
  const refreshFor = (kind: string) => {
    if (kind === 'uoms') refreshUoms()
    if (kind === 'materials') { refreshMat(); refreshAllMat() }
    else if (kind === 'vendors') { refreshVend(); refreshAllVend() }
    else if (kind === 'sites') refreshSites()
    else if (kind === 'categories') refreshCats()
    else refreshTerms()
  }

  const filter = (rows: any[], fields: string[]) => {
    const t = term.trim().toLowerCase()
    if (!t) return rows
    return rows.filter((r) => fields.some((f) => String(r[f] ?? '').toLowerCase().includes(t)))
  }

  // The route itself was never guarded - only the sidebar link was hidden, so
  // anybody who typed /masters reached every tab. Refuse here, where it counts.
  if (visibleTabs.length === 0) {
    return <NotPermitted what="open master data"
                         who="the Head of Stores, Procurement, Accounts and the board" />
  }

  return (
    <div className={`space-y-4 ${
      (locating && tab === 'materials') || (vendorOrders && tab === 'vendors')
        ? 'lg:pr-[400px] xl:pr-[440px]' : ''}`}>
      <Card title="Master data"
            subtitle={mayEdit.vendors || mayEdit.materials
              ? 'Migrated from the legacy workbooks and maintained here from now on'
              : 'Read only — maintained by the Head of Stores and Procurement'}>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
            {visibleTabs.map((t) => (
              <button key={t} onClick={() => { setTab(t); setLocating(null); setVendorOrders(null) }}
                      className={`rounded-md px-3 py-1.5 text-xs font-semibold capitalize ${
                        tab === t ? 'bg-white text-oval-800 shadow-sm' : 'text-slate-600'}`}>
                {t}
              </button>
            ))}
          </div>
          <SearchInput value={term} onChange={setTerm} placeholder="Search…" className="w-full sm:w-72" />
          {(tab === 'materials' || tab === 'vendors') && (
            <Select value={showActive} className="w-40"
                    onChange={(e) => setShowActive(e.target.value as 'ALL' | 'ACTIVE' | 'INACTIVE')}
                    options={[
                      { value: 'ALL', label: 'Show: All' },
                      { value: 'ACTIVE', label: 'Show: Active' },
                      { value: 'INACTIVE', label: 'Show: Inactive' },
                    ]} />
          )}
          <div className="ml-auto">
            {tab === 'settings' ? null : mayEdit[tab] ? (
              <button className="btn-primary btn-sm" onClick={() => setEditing({ kind: tab, row: null })}>
                <Plus className="h-4 w-4" /> Add {
                  tab === 'terms' ? 'term'
                  : tab === 'uoms' ? 'unit'
                  : tab === 'categories' ? 'category'
                  : tab.replace(/s$/, '')}
              </button>
            ) : (
              <span className="text-xs text-slate-500">
                Maintained by the {OWNER[tab]}
              </span>
            )}
          </div>
        </div>
      </Card>

      {tab === 'settings' && <SystemSettings canEdit={role === 'admin'} />}

      {tab === 'categories' && (() => {
        // Shown as the tree it is: each category followed by its own
        // sub-categories, indented. A flat alphabetical list of 368 rows would
        // be technically the same information and no use to anybody.
        const all = filter(categories, ['name', 'domain'])
        const tops = categories.filter((c: any) => c.parent_id === null)
        const hit = new Set(all.map((c: any) => c.id))
        return (
          <Card title={`Categories (${categories.length})`}
                subtitle="The list behind the Category and Sub category boxes on the Material and Vendor forms">
            <div className="space-y-4">
              {(['MATERIAL', 'VENDOR'] as const).map((dom) => {
                const domTops = tops
                  .filter((c: any) => c.domain === dom)
                  .sort((a: any, b: any) => a.sort_order - b.sort_order)
                return (
                  <div key={dom}>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {dom === 'MATERIAL' ? 'Materials' : 'Vendors'}
                    </h3>
                    <div className="space-y-1.5">
                      {domTops.map((c: any) => {
                        const kids = categories
                          .filter((k: any) => k.parent_id === c.id)
                          .sort((a: any, b: any) =>
                            (a.name === 'Others' ? 1 : b.name === 'Others' ? -1 : 0)
                            || a.sort_order - b.sort_order)
                        // Hide a whole branch only when neither the category
                        // nor any of its children matched the search.
                        if (term.trim() && !hit.has(c.id) && !kids.some((k: any) => hit.has(k.id))) return null
                        return (
                          <div key={c.id} className="rounded-lg border border-slate-200 p-2">
                            <button className="text-sm font-semibold text-oval-800 hover:underline"
                                    disabled={!mayEdit.categories}
                                    onClick={() => setEditing({ kind: 'categories', row: c })}>
                              {c.name}
                            </button>
                            {!c.is_active && <span className="ml-2 text-xs text-slate-400">switched off</span>}
                            <div className="mt-1 flex flex-wrap gap-1">
                              {kids.map((k: any) => (
                                <button key={k.id}
                                        disabled={!mayEdit.categories}
                                        onClick={() => setEditing({ kind: 'categories', row: k })}
                                        className={`rounded-md px-2 py-0.5 text-xs ${
                                          k.is_active ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                                      : 'bg-slate-50 text-slate-400 line-through'}`}>
                                  {k.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </Card>
        )
      })()}

      {tab === 'materials' && (
        <MaterialCashflowApprovals materials={allMaterials} isSuper={isSuper} mayEdit={mayEdit.materials}
                                   onChanged={() => { refreshAllMat(); refreshMat() }}
                                   onEdit={(m) => setEditing({ kind: 'materials', row: m })} />
      )}

      {tab === 'materials' && (
        <Card title={`Materials (${allMaterials.length}${inactiveCount(allMaterials) ? ` · ${inactiveCount(allMaterials)} inactive` : ''})`}
              subtitle="Conversion factor turns the stock UOM into the purchase UOM — this is what makes TMT bars orderable in KG">
          {aml && <Loading />}
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={matSort.sort} onSort={matSort.toggle}>ID</Th>
                  <Th id="item_code" sort={matSort.sort} onSort={matSort.toggle}>Code</Th>
                  <Th id="item_name" sort={matSort.sort} onSort={matSort.toggle}>Material</Th>
                  <Th id="category" sort={matSort.sort} onSort={matSort.toggle}>Category</Th>
                  <Th id="hsn_code" sort={matSort.sort} onSort={matSort.toggle}>HSN</Th>
                  <Th id="unit_of_measure" sort={matSort.sort} onSort={matSort.toggle}>Stock UOM</Th>
                  <Th id="purchase_uom" sort={matSort.sort} onSort={matSort.toggle}>Purchase UOM</Th>
                  <Th id="conversion_factor" sort={matSort.sort} onSort={matSort.toggle} className="num" align="right">Factor</Th>
                  <Th id="gst_rate" sort={matSort.sort} onSort={matSort.toggle} className="num" align="right">GST %</Th>
                  {/* Board resolution: the Site Store-man, the Resident
                      Construction Manager and the Stores Executive see the
                      material list WITHOUT what the company pays for it. */}
                  {showBaseRate && (
                    <Th id="base_rate" sort={matSort.sort} onSort={matSort.toggle} className="num" align="right">Base rate</Th>
                  )}
                  <Th id="min_stock_level" sort={matSort.sort} onSort={matSort.toggle} className="num" align="right">Min</Th>
                  <Th id="max_stock_level" sort={matSort.sort} onSort={matSort.toggle} className="num" align="right">Max</Th>
                  {/* Pinned to the right edge. With fourteen columns this one
                      falls off a 1366 or 1440 laptop, and on 11-09-2026 the
                      "where is it" pill was half cut off in full screen. */}
                  <th className="col-actions" />
                </tr>
              </thead>
              <tbody>
                {sortRows(filter(byActive(allMaterials), ['id', 'item_code', 'item_name', 'category', 'sub_category']), matSort.sort, {
                  conversion_factor: 'number', gst_rate: 'number', base_rate: 'number',
                  min_stock_level: 'number', max_stock_level: 'number',
                }).map((m: any) => (
                  <tr key={m.id} className={`${m.is_active === false ? 'bg-slate-100 text-slate-400 opacity-70'
                                              : m.unit_of_measure !== m.purchase_uom ? 'row-tint bg-oval-50/40' : ''} ${locating?.id === m.id ? 'row-picked bg-oval-100/70' : ''}`}
                      title={m.is_active === false ? 'Inactive — not offered on any requisition, order or receipt. Open it and tick Active to bring it back.' : undefined}>
                    <td className="font-mono text-xs">{m.id}</td>
                    <td className="font-mono text-xs">{m.item_code}</td>
                    <td className="text-sm font-medium">
                      {m.item_name}
                      {m.is_active === false && <span className="pill-grey ml-1.5">Inactive</span>}
                      <CfStatusPill m={m} />
                    </td>
                    <td className="text-xs text-slate-500">{m.category}</td>
                    <td className="font-mono text-xs">{m.hsn_code}</td>
                    <td className="text-xs">{m.unit_of_measure}</td>
                    <td className="text-xs">{m.purchase_uom}</td>
                    <td className={`num text-xs ${m.conversion_factor !== 1 ? 'font-bold text-oval-700' : 'text-slate-400'}`}
                        title={m.conversion_factor !== 1 ? conversionText(m) : undefined}>
                      {factorShort(m.conversion_factor)}
                    </td>
                    <td className="num text-xs">{m.gst_rate}</td>
                    {showBaseRate && (
                      <td className="num text-xs">{m.base_rate ? rupees(m.base_rate) : '—'}</td>
                    )}
                    <td className="num text-xs text-slate-500">{qty(m.min_stock_level)}</td>
                    <td className="num text-xs text-slate-500">{qty(m.max_stock_level)}</td>
                    <td className="col-actions text-right">
                      <div className="flex items-center justify-end gap-1">
                        {mayLocate && (
                          <DistributionButton count={dist.countFor(m.id)} ready={dist.ready}
                                              active={locating?.id === m.id}
                                              onClick={() => setLocating(locating?.id === m.id ? null : m)} />
                        )}
                        {/* Copy, 29-09-2026: the same material in another size.
                            Shown only to whoever may already add a material
                            (Head of Stores, administrator) - it opens the
                            ordinary Add form, pre-filled, so it grants nothing
                            new. */}
                        {mayEdit.materials && (
                          <button className="btn-ghost btn-sm" title="Copy — add another size of this material"
                                  onClick={() => setEditing({ kind: 'materials', row: null, copyOf: m })}>
                            <Copy className="h-4 w-4" />
                          </button>
                        )}
                        {rowAction('materials', m)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === 'materials' && locating && (
        <MaterialDistributionPanel material={locating} lines={dist.linesFor(locating.id)}
                                   loading={dist.loading} onClose={() => setLocating(null)}
                                   onRefresh={dist.refresh} />
      )}

      {tab === 'vendors' && vendorOrders && (
        <VendorOrdersPanel vendor={vendorOrders} orders={ack.ordersFor(vendorOrders.id)}
                           loading={ack.loading} onClose={() => setVendorOrders(null)}
                           onRefresh={ack.refresh} />
      )}

      {tab === 'vendors' && (
        <Card title={`Vendors (${allVendors.length}${inactiveCount(allVendors) ? ` · ${inactiveCount(allVendors)} inactive` : ''})`}
              subtitle="Unregistered vendors are charged no GST, exactly as on the approved PO format">
          {avl && <Loading />}
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={vendSort.sort} onSort={vendSort.toggle}>ID</Th>
                  <Th id="company_name" sort={vendSort.sort} onSort={vendSort.toggle}>Company</Th>
                  <Th id="gstin" sort={vendSort.sort} onSort={vendSort.toggle}>GSTIN</Th>
                  {/* 12-09-2026: the PAN number, the contact name and the
                      rating came off this list - on a laptop they pushed the
                      paperwork flags off the right-hand edge, and all three are
                      one click away on the vendor itself. */}
                  <Th id="state_name" sort={vendSort.sort} onSort={vendSort.toggle}>State</Th>
                  <Th id="category" sort={vendSort.sort} onSort={vendSort.toggle}>Category</Th>
                  <Th id="phone" sort={vendSort.sort} onSort={vendSort.toggle}>Phone</Th>
                  <Th id="is_gst_registered" sort={vendSort.sort} onSort={vendSort.toggle}>GST</Th>
                  {/* Sorted on the derived value, not on anything hidden, so
                      clicking a header groups the vendors missing that paper
                      together — which is the whole reason to click it. */}
                  <Th id="gst_doc" sort={vendSort.sort} onSort={vendSort.toggle}>GST cert</Th>
                  <Th id="pan_doc" sort={vendSort.sort} onSort={vendSort.toggle}>PAN</Th>
                  <Th id="bank_doc" sort={vendSort.sort} onSort={vendSort.toggle}>Bank</Th>
                  <th className="col-actions" />
                </tr>
              </thead>
              <tbody>
                {sortRows(filter(
                  // The three markers are folded in ONCE, here, so the same
                  // value is what each cell shows and what its column sorts on.
                  //
                  // An unregistered vendor has no GST certificate to give, so
                  // its GST cell is a dash rather than an N — N means "a
                  // document that should be here is missing", and a chase list
                  // padded with vendors nobody can chase is not a chase list.
                  byActive(allVendors).map((v: any) => {
                    const p = papersById[v.id]
                    return {
                      ...v,
                      gst_doc: p?.gst_applicable === false ? '—' : (p?.has_gst_doc ? 'Y' : 'N'),
                      pan_doc: p?.has_pan_doc ? 'Y' : 'N',
                      bank_doc: p?.has_bank_doc ? 'Y' : 'N',
                    }
                  }),
                  ['id', 'company_name', 'gstin', 'category', 'state_name', 'contact_person'],
                ), vendSort.sort, {
                  rating: 'number',
                }).map((v: any) => (
                  <tr key={v.id}
                      className={`${v.is_active === false ? 'bg-slate-100 text-slate-400 opacity-70' : ''} ${vendorOrders?.id === v.id ? 'row-picked bg-oval-100/70' : ''}`}
                      title={v.is_active === false ? 'Inactive — not offered on any purchase order or payment. Open it and tick Active to bring it back.' : undefined}>
                    <td className="font-mono text-xs">{v.id}</td>
                    <td className="text-sm font-medium">
                      {v.company_name}
                      {v.is_active === false && <span className="pill-grey ml-1.5">Inactive</span>}
                    </td>
                    <td className="font-mono text-xs">{v.gstin ?? '—'}</td>
                    <td className="text-xs">{v.state_name}</td>
                    <td className="text-xs text-slate-500">{v.category}</td>
                    <td className="font-mono text-xs">{v.phone}</td>
                    <td>{v.is_gst_registered ? <span className="pill-green">Registered</span> : <span className="pill-grey">Unregistered</span>}</td>
                    <td><PaperMark value={v.gst_doc} what="GST registration certificate" /></td>
                    <td><PaperMark value={v.pan_doc} what="PAN card" /></td>
                    <td><PaperMark value={v.bank_doc} what="bank mandate" /></td>
                    <td className="col-actions text-right">
                      <div className="flex items-center justify-end gap-1">
                        {mayVendorOrders && (
                          <VendorOrdersButton count={ack.countFor(v.id)} overdue={ack.overdueFor(v.id)}
                                              ready={ack.ready} active={vendorOrders?.id === v.id}
                                              onClick={() => setVendorOrders(vendorOrders?.id === v.id ? null : v)} />
                        )}
                        {/* Copy, 29-09-2026: another firm in the same trade.
                            Only for those who may already add a vendor. */}
                        {mayEdit.vendors && (
                          <button className="btn-ghost btn-sm" title="Copy — add another vendor in the same trade"
                                  onClick={() => setEditing({ kind: 'vendors', row: null, copyOf: v })}>
                            <Copy className="h-4 w-4" />
                          </button>
                        )}
                        {rowAction('vendors', v)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === 'sites' && (
        <Card title={`Sites (${sites.length})`}>
          <div className="table-wrap">
            <table className="tbl">
              <thead><tr>
                <Th id="id" sort={siteSort.sort} onSort={siteSort.toggle}>ID</Th>
                <Th id="site_name" sort={siteSort.sort} onSort={siteSort.toggle}>Site</Th>
                <Th id="site_code" sort={siteSort.sort} onSort={siteSort.toggle}>Code</Th>
                <Th id="customer_name" sort={siteSort.sort} onSort={siteSort.toggle}>Customer</Th>
                <Th id="project_type" sort={siteSort.sort} onSort={siteSort.toggle}>Type</Th>
                <Th id="state_name" sort={siteSort.sort} onSort={siteSort.toggle}>State</Th>
                <Th id="gst_state_code" sort={siteSort.sort} onSort={siteSort.toggle}>GST code</Th>
                <Th id="address" sort={siteSort.sort} onSort={siteSort.toggle}>Address</Th>
                <th className="col-actions" />
              </tr></thead>
              <tbody>
                {sortRows(filter(sites, ['id', 'site_name', 'site_code', 'customer_name']), siteSort.sort, {}).map((s: any) => (
                  <tr key={s.id}>
                    <td className="font-mono text-xs">{s.id}</td>
                    <td className="text-sm font-medium">
                      {s.site_name}
                      {s.is_head_office && <span className="pill-blue ml-1.5">HO</span>}
                      {/* So the Sysadmin can see at a glance which projects
                          carry the JV indent rule, without opening each row. */}
                      {/* Two distinct facts, so two distinct pills. A single
                          "JV" badge would have hidden the board's 01-09-2026
                          decision entirely: the site is still JV-financed, but
                          its orders are billed to OVAL, and the Sysadmin should
                          be able to see which without opening the row. */}
                      {s.requires_original_indent && (
                        <span className="pill-violet ml-1.5"
                              title={s.jv_partner_name
                                ? `Financed by ${s.jv_partner_name} — every PO must quote the original indent`
                                : 'Financed by a joint venture — every PO must quote the original indent'}>JV</span>
                      )}
                      {s.requires_original_indent && (
                        s.bill_to_jv
                          ? <span className="pill-amber ml-1.5"
                                  title={`Purchase orders are billed to ${s.jv_partner_name ?? 'the JV'} — GSTIN ${s.jv_gstin ?? '?'}`}>
                              billed to JV
                            </span>
                          : <span className="pill-grey ml-1.5"
                                  title="Board 01-09-2026 — interim: OVAL contracts in its own name, so these orders are billed to OVAL">
                              billed to OVAL
                            </span>
                      )}
                    </td>
                    <td className="font-mono text-xs">{s.site_code}</td>
                    <td className="text-xs">{s.customer_name}</td>
                    <td className="text-xs">{s.project_type}</td>
                    <td className="text-xs">{s.state_name}</td>
                    <td className="font-mono text-xs">{s.gst_state_code}</td>
                    <td className="max-w-[300px] truncate text-xs text-slate-500">{s.address}</td>
                    <td className="col-actions text-right">{rowAction('sites', s)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {viewing && (
        <MasterRecord kind={viewing.kind} row={viewing.row} onClose={() => setViewing(null)} />
      )}

      {editing && (
        <MasterForm
          kind={editing.kind}
          row={editing.row}
          copyOf={editing.copyOf}
          onClose={() => setEditing(null)}
          onDone={() => { refreshFor(editing.kind); setEditing(null) }}
        />
      )}

      {tab === 'uoms' && (
        <Card title={`Units of measure (${uoms.length})`}
              subtitle="A closed list. Materials pick from it, so a unit that is not here cannot be typed by mistake">
          <Alert tone="info" title="Why this is a list and not a text box">
            "NO", "NOS", "No." and "Nos" are four units to a database and one unit to a person.
            Once two spellings exist for the same thing, stock reports split and conversion
            factors stop meaning anything. Add a unit here first, then pick it on the material.
          </Alert>
          <div className="table-wrap mt-3">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Code</th><th>Description</th><th>Measures</th>
                  <th className="num">Order</th><th>Status</th>
                  <th className="num">Materials using it</th><th className="col-actions" />
                </tr>
              </thead>
              <tbody>
                {uoms.map((u: any) => {
                  const used = materials.filter(
                    (m) => m.unit_of_measure === u.code || m.purchase_uom === u.code).length
                  return (
                    <tr key={u.code} className={u.is_active ? '' : 'bg-slate-50 text-slate-400'}>
                      <td className="font-mono text-xs font-bold">{u.code}</td>
                      <td className="text-sm">{u.description}</td>
                      <td className="text-xs text-slate-500">{u.uom_class}</td>
                      <td className="num text-xs">{u.sort_order}</td>
                      <td>
                        <span className={u.is_active ? 'pill-green' : 'pill-grey'}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="num text-xs">
                        {used > 0 ? used : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="col-actions text-right">{rowAction('uoms', u)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            A unit in use by a material cannot sensibly be deleted — switch it <strong>inactive</strong>
            instead. It stops appearing in the dropdown while every existing material keeps working.
          </p>
        </Card>
      )}

      {tab === 'terms' && (
        <Card title="Purchase order standard terms" subtitle="Printed on every purchase order PDF">
          {/* A short list, so it gets buttons rather than a table header. */}
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-slate-500">Sort by</span>
            {[['id', 'Number'], ['sort_order', 'Order on the PO'], ['term_text', 'Text'],
              ['is_active', 'Active first']].map(([k, l]) => (
              <button key={k} onClick={() => termSort.toggle(k)}
                      className={`rounded-md px-2 py-1 font-semibold ${
                        termSort.sort.key === k ? 'bg-oval-100 text-oval-800' : 'bg-slate-100 text-slate-600'}`}>
                {l}{termSort.sort.key === k ? (termSort.sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
              </button>
            ))}
          </div>
          <ol className="space-y-2">
            {sortRows(filter(poTerms, ['id', 'term_text']), termSort.sort, { sort_order: 'number' }).map((t: any) => (
              <li key={t.id} className="flex gap-3 rounded-lg border border-slate-200 px-3 py-2">
                <span className="font-mono text-xs font-bold text-slate-400">{t.id}</span>
                <span className="flex-1 text-sm">{t.term_text}</span>
                <span className={t.is_active ? 'pill-green' : 'pill-grey'}>{t.is_active ? 'Active' : 'Inactive'}</span>
                {rowAction('terms', t)}
              </li>
            ))}
          </ol>
        </Card>
      )}
    </div>
  )
}

// ===========================================================================
// Users
// ===========================================================================
/**
 * How each column of the user list compares.
 *
 * Role and Status sort on the DERIVED label, not on the raw column. Sorting
 * `role` would order by the enum spelling — accounts_spoc, admin, cmd … — which
 * is not what the reader can see, and sorting `is_active` would compare the
 * booleans false and true and read as backwards. Both are given a *_label field
 * below so the sort follows the eye.
 */
const USER_SORT_KINDS = {
  user_code: 'text',
  full_name: 'text',
  email: 'text',
  phone: 'text',
  role_label: 'text',
  site_id: 'text',
  designation: 'text',
  can_approve_upto: 'number',
  status_label: 'text',
} as const

export function Users() {
  const { profile } = useAuth()
  const toast = useToast()
  const { data: people, loading, refresh } = useProfiles()
  const { data: sites } = useSites()
  const [open, setOpen] = useState(false)
  // 14-10-2026: no shared starting password any more. Opening "Add user" fills
  // in a random temporary one (see openAdd), and the person must set their own
  // at first sign-in.
  const [f, setF] = useState<any>({ role: 'storeman', site_id: 'HO', password: '' })
  // The temporary password just handed out, shown ONCE so it can be given to
  // the person in person. Cleared as soon as the admin closes it.
  const [handover, setHandover] = useState<{ name: string; login: string; password: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [exporting, setExporting] = useState(false)
  // Row-level admin: edit a person, reset their password, deactivate them.
  const [editing, setEditing] = useState<any>(null)
  const [pwdFor, setPwdFor] = useState<any>(null)
  const [newPwd, setNewPwd] = useState('')
  const [toggling, setToggling] = useState<any>(null)
  // user_code ascending is the order this list has always come back in, so the
  // page looks unchanged until somebody clicks a heading.
  const { sort, toggle } = useSort('user_code', 'asc')

  const rows = useMemo(
    () => people.map((p) => ({
      ...p,
      role_label: ROLE_LABEL[p.role] ?? p.role,
      status_label: p.is_active ? 'Active' : 'Inactive',
    })),
    [people],
  )
  const sorted = useMemo(() => sortRows(rows, sort, USER_SORT_KINDS), [rows, sort])

  if (!can(profile!.role, 'manageUsers')) return <NotPermitted what="manage users" who="system administrator" />

  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }))
  const setE = (k: string, v: any) => setEditing((p: any) => ({ ...p, [k]: v }))

  /** Every admin action goes through the same edge function. */
  async function callAdmin(payload: Record<string, unknown>, okMessage: string) {
    setBusy(true)
    try {
      const res = await callFunction('admin-provision-users', payload)
      const body = await res.json()
      if (!res.ok) throw new Error(body.error ?? 'The change could not be saved')
      toast.success(okMessage)
      refresh()
      return true
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'The change could not be saved')
      return false
    } finally { setBusy(false) }
  }

  function openAdd() {
    setF((p: any) => ({ ...p, password: generateTempPassword() }))
    setOpen(true)
  }

  async function create() {
    if (!f.email || !f.full_name) { toast.error('Email and full name are required.'); return }
    const problem = await passwordProblem(f.password)
    if (problem) { toast.error(`Temporary password: ${problem}`); return }
    setBusy(true)
    try {
      const res = await callFunction('admin-provision-users', { action: 'create', ...f })
      const body = await res.json()
      if (!res.ok) throw new Error(body.error ?? 'Could not create the user')
      toast.success(`${f.full_name} can now sign in as ${body.user_code ?? 'a new user'}.`)
      setHandover({ name: f.full_name, login: f.email, password: f.password })
      setOpen(false); setF({ role: 'storeman', site_id: 'HO', password: '' }); refresh()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not create the user')
    } finally { setBusy(false) }
  }

  async function saveEdit() {
    if (!editing?.full_name) { toast.error('Full name is required.'); return }
    const ok = await callAdmin({
      action: 'update',
      email: editing.email,
      full_name: editing.full_name,
      phone: editing.phone ?? null,
      role: editing.role,
      site_id: editing.site_id,
      designation: editing.designation ?? null,
      can_approve_upto: editing.can_approve_upto ?? 0,
      contact_email: editing.contact_email ?? null,
    }, `${editing.full_name} updated.`)
    if (ok) setEditing(null)
  }

  async function savePassword() {
    const problem = await passwordProblem(newPwd)
    if (problem) { toast.error(problem); return }
    const ok = await callAdmin(
      { action: 'reset_password', email: pwdFor.email, password: newPwd },
      `Password reset for ${pwdFor.full_name}. They must set their own at the next sign-in.`,
    )
    if (ok) {
      setHandover({ name: pwdFor.full_name, login: pwdFor.email, password: newPwd })
      setPwdFor(null); setNewPwd('')
    }
  }

  async function copyHandover() {
    if (!handover) return
    try {
      await navigator.clipboard.writeText(handover.password)
      toast.success('Temporary password copied.')
    } catch {
      toast.error('This browser would not copy it - select the password and copy it by hand.')
    }
  }

  /**
   * The register as a formatted workbook.
   *
   * Built on the server, not here. SheetJS in the browser cannot write cell
   * styling, and shipping ExcelJS to every phone that opens this app to format
   * one occasional download is not a trade worth making.
   */
  async function exportUsers() {
    setExporting(true)
    try {
      await downloadFromFunction('export-users', {}, 'OVAL_Users_and_Roles.xlsx')
      toast.success('Users and roles register downloaded.')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'The export failed')
    } finally { setExporting(false) }
  }

  return (
    <div className="space-y-4">
      <Card
        title="Users and roles"
        subtitle="Self sign-up is disabled — every account is created here"
        actions={<>
          <button className="btn-secondary btn-sm" onClick={exportUsers} disabled={exporting}>
            <Download className="h-4 w-4" /> {exporting ? 'Preparing…' : 'Export to Excel'}
          </button>
          <button className="btn-primary btn-sm" onClick={openAdd}><Plus className="h-4 w-4" /> Add user</button>
        </>}
      >
        <Alert tone="info">
          A user's role decides what they can see and do, and the database enforces it independently of the
          screen. A site user can only ever read and write their own site's documents.
        </Alert>
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        <div className="table-wrap mx-0">
          <table className="tbl">
            <thead><tr>
              <Th id="user_code" sort={sort} onSort={toggle}>Code</Th>
              <Th id="full_name" sort={sort} onSort={toggle}>Name</Th>
              <Th id="email" sort={sort} onSort={toggle}>Email</Th>
              <Th id="phone" sort={sort} onSort={toggle}>Phone</Th>
              <Th id="role_label" sort={sort} onSort={toggle}>Role</Th>
              <Th id="site_id" sort={sort} onSort={toggle}>Site</Th>
              <Th id="designation" sort={sort} onSort={toggle}>Designation</Th>
              <Th id="can_approve_upto" sort={sort} onSort={toggle} className="num" align="right">Approval limit</Th>
              <Th id="status_label" sort={sort} onSort={toggle}>Status</Th>
              <th className="text-right">Actions</th>
            </tr></thead>
            <tbody>
              {sorted.map((p) => (
                <tr key={p.id} className={p.is_active ? '' : 'opacity-60'}>
                  <td className="font-mono text-xs">{p.user_code ?? '—'}</td>
                  <td className="text-sm font-medium">{p.full_name}</td>
                  <td className="font-mono text-xs text-slate-500">{p.email}</td>
                  <td className="font-mono text-xs whitespace-nowrap">
                    {/* Tappable, because this list is read on a phone as often
                        as on a desktop and the next thing anyone does with a
                        number is ring it. */}
                    {p.phone
                      ? <a className="text-oval-700 hover:underline" href={`tel:${p.phone}`}>{p.phone}</a>
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="text-xs">{p.role_label}</td>
                  <td className="text-xs">{p.site_id}</td>
                  <td className="text-xs text-slate-500">{p.designation ?? '—'}</td>
                  <td className="num text-xs">{p.can_approve_upto ? rupees(p.can_approve_upto) : '—'}</td>
                  <td>{p.is_active ? <span className="pill-green">Active</span> : <span className="pill-grey">Inactive</span>}</td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      <button className="btn-ghost btn-sm" title="Edit this person"
                              onClick={() => setEditing({ ...p })} disabled={busy}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button className="btn-ghost btn-sm" title="Set a new password"
                              onClick={() => { setPwdFor(p); setNewPwd(generateTempPassword()) }} disabled={busy}>
                        <KeyRound className="h-4 w-4" />
                      </button>
                      <button className="btn-ghost btn-sm"
                              title={p.is_active ? 'Deactivate — blocks sign-in, keeps the audit trail' : 'Reactivate'}
                              onClick={() => setToggling(p)}
                              disabled={busy || p.id === profile!.id}>
                        {p.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} wide title="Add a user"
             footer={<>
               <button className="btn-secondary" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
               <button className="btn-primary" onClick={create} disabled={busy}>Create user</button>
             </>}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Full name" required><Input value={f.full_name ?? ''} onChange={(e) => set('full_name', e.target.value)} /></Field>
          <Field label="Email" required><Input type="email" value={f.email ?? ''} onChange={(e) => set('email', e.target.value)} /></Field>
          <Field label="Role" required>
            <Select value={f.role} onChange={(e) => set('role', e.target.value)}
                    options={Object.entries(ROLE_LABEL).map(([v, l]) => ({ value: v, label: l }))} />
          </Field>
          <Field label="Site" required>
            <Select value={f.site_id} onChange={(e) => set('site_id', e.target.value)}
                    options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
          </Field>
          <Field label="Phone"><Input value={f.phone ?? ''} onChange={(e) => set('phone', e.target.value)} /></Field>
          <Field label="Designation"><Input value={f.designation ?? ''} onChange={(e) => set('designation', e.target.value)} /></Field>
          <Field label="Temporary password" required
                 hint="Random and hard to guess. You may type your own: 8+ characters with a letter and a number. They must set their own at first sign-in.">
            <div className="flex gap-2">
              <Input value={f.password} onChange={(e) => set('password', e.target.value)} className="font-mono" />
              <button type="button" className="btn-secondary btn-sm shrink-0" title="Suggest another"
                      onClick={() => set('password', generateTempPassword())}>
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </Field>
          <Field label="Approval limit (₹)" hint="0 means no monetary approval authority">
            <Input type="number" value={f.can_approve_upto ?? 0} onChange={(e) => set('can_approve_upto', e.target.value)} />
          </Field>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          The staff code (USR-…) is allocated automatically and continues the existing series.
        </p>
      </Modal>

      {/* ---------------------------------------------------------------- edit */}
      <Modal open={!!editing} onClose={() => setEditing(null)} wide
             title={`Edit ${editing?.full_name ?? ''}`}
             subtitle={editing ? `${editing.user_code ?? 'no code'} · ${editing.email}` : undefined}
             footer={<>
               <button className="btn-secondary" onClick={() => setEditing(null)} disabled={busy}>Cancel</button>
               <button className="btn-primary" onClick={saveEdit} disabled={busy}>Save changes</button>
             </>}>
        {editing && (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Full name" required>
                <Input value={editing.full_name ?? ''} onChange={(e) => setE('full_name', e.target.value)} />
              </Field>
              <Field label="Phone">
                <Input value={editing.phone ?? ''} onChange={(e) => setE('phone', e.target.value)} />
              </Field>
              <Field label="Role" required>
                <Select value={editing.role} onChange={(e) => setE('role', e.target.value)}
                        options={Object.entries(ROLE_LABEL).map(([v, l]) => ({ value: v, label: l }))} />
              </Field>
              <Field label="Site" required>
                <Select value={editing.site_id ?? 'HO'} onChange={(e) => setE('site_id', e.target.value)}
                        options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
              </Field>
              <Field label="Designation">
                <Input value={editing.designation ?? ''} onChange={(e) => setE('designation', e.target.value)} />
              </Field>
              <Field label="Approval limit (₹)" hint="0 means no monetary approval authority">
                <Input type="number" value={editing.can_approve_upto ?? 0}
                       onChange={(e) => setE('can_approve_upto', e.target.value)} />
              </Field>
              {/* Board 01-09-2026. Separate from the sign-in address on
                  purpose — see the alert below. */}
              <Field label="Contact email printed on purchase orders" className="sm:col-span-2"
                     hint="Their real working address, e.g. abhishek.chowdhury@ovalprojects.com. Printed in the Bill To block of every purchase order this person raises, so a vendor can reply to a mailbox somebody actually reads. Leave it blank to print the sign-in address below.">
                {/*
                  The System Administrator is TYPING this address, so of course
                  the box shows it. The board's 01-09-2026 rule is that trails,
                  registers and detail panels name the person instead of
                  exposing their address — not that the one form where the
                  address is maintained may not display what was just entered.
                  check-no-emails-on-screen: allow
                */}
                <Input value={editing.contact_email ?? ''}  /* check-no-emails-on-screen: allow */
                       placeholder={editing.email ?? ''}
                       onChange={(e) => setE('contact_email', e.target.value)} />
              </Field>
            </div>
            <div className="mt-4">
              <Alert tone="info">
                <strong>{editing.email}</strong> is the sign-in address and cannot be changed here —
                it is the identity the audit trail records and the key every document uses to decide
                whether it is yours to correct. If somebody's sign-in really changes, deactivate this
                account and create a new one. To change only what a <em>vendor</em> sees, use the
                contact email above.
              </Alert>
            </div>
          </>
        )}
      </Modal>

      {/* ------------------------------------------------------ reset password */}
      <Modal open={!!pwdFor} onClose={() => setPwdFor(null)}
             title={`New password for ${pwdFor?.full_name ?? ''}`}
             subtitle={pwdFor?.email}
             footer={<>
               <button className="btn-secondary" onClick={() => setPwdFor(null)} disabled={busy}>Cancel</button>
               <button className="btn-primary" onClick={savePassword} disabled={busy}>Set password</button>
             </>}>
        <Field label="Temporary password" required
               hint="Random and hard to guess. You may type your own: 8+ characters with a letter and a number. It takes effect immediately, and they must set their own at the next sign-in.">
          <div className="flex gap-2">
            <Input value={newPwd} onChange={(e) => setNewPwd(e.target.value)} autoFocus className="font-mono" />
            <button type="button" className="btn-secondary btn-sm shrink-0" title="Suggest another"
                    onClick={() => setNewPwd(generateTempPassword())}>
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </Field>
        <div className="mt-4">
          <Alert tone="warning">
            Hand the temporary password over in person or by phone — not in a group chat. At the next
            sign-in they will be asked to choose their own, which nobody else will know.
          </Alert>
        </div>
      </Modal>

      {/* -------------------------------------- the temporary password, shown once */}
      <Modal open={!!handover} onClose={() => setHandover(null)}
             title={`Temporary password for ${handover?.name ?? ''}`}
             subtitle="Shown once - copy it now"
             footer={<button className="btn-primary" onClick={() => setHandover(null)}>Done</button>}>
        {handover && (
          <div className="space-y-4">
            <div>
              <p className="label">Sign-in</p>
              {/* The administrator just typed or chose this login; it is their
                  own working form, not a trail naming somebody else.
                  check-no-emails-on-screen: allow */}
              <p className="font-mono text-sm text-slate-800">{handover.login}</p>
            </div>
            <div>
              <p className="label">Temporary password</p>
              <div className="flex items-center gap-2">
                <p className="select-all rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-lg font-semibold text-slate-900">
                  {handover.password}
                </p>
                <button type="button" className="btn-secondary btn-sm" onClick={copyHandover}>
                  <Copy className="h-4 w-4" /> Copy
                </button>
              </div>
            </div>
            <Alert tone="warning" title="Hand it over in person or by phone">
              Not in a group chat. When they sign in with it, the app makes them choose their own
              password straight away - after that, nobody but them knows it. Once you close this box
              the temporary password is not shown again; if it is lost, use Reset password.
            </Alert>
          </div>
        )}
      </Modal>

      {/* ------------------------------------------------ deactivate/reactivate */}
      <Confirm
        open={!!toggling}
        title={toggling ? (toggling.is_active ? `Deactivate ${toggling.full_name}?` : `Reactivate ${toggling.full_name}?`) : ''}
        message={toggling?.is_active
          ? 'They will not be able to sign in. Their name stays on every document they approved, so the audit trail is untouched. You can reactivate them at any time.'
          : 'They will be able to sign in again with their existing password.'}
        confirmLabel={toggling?.is_active ? 'Deactivate' : 'Reactivate'}
        tone={toggling?.is_active ? 'danger' : 'primary'}
        onCancel={() => setToggling(null)}
        onConfirm={async () => {
          const t = toggling
          setToggling(null)
          await callAdmin(
            { action: t.is_active ? 'deactivate' : 'reactivate', email: t.email },
            t.is_active ? `${t.full_name} can no longer sign in.` : `${t.full_name} can sign in again.`,
          )
        }}
      />
    </div>
  )
}

// ===========================================================================
// Sync queue
// ===========================================================================
const STATUS_STYLE: Record<string, string> = {
  pending: 'pill-amber', sending: 'pill-blue', failed: 'pill-red', done: 'pill-green',
}

export function SyncQueue() {
  const { online, syncing, lastSyncAt, lastError } = useSyncStore()
  const entries = useLiveQuery(() => localDb.outbox.orderBy('created_at').reverse().toArray(), [], [])
  const toast = useToast()

  const pending = entries.filter((e) => e.status === 'pending' || e.status === 'sending')
  const failed = entries.filter((e) => e.status === 'failed')

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Connection" value={online ? 'Online' : 'Offline'} tone={online ? 'good' : 'warn'} />
        <Stat label="Waiting to upload" value={pending.length} tone={pending.length ? 'warn' : 'good'} />
        <Stat label="Failed" value={failed.length} tone={failed.length ? 'bad' : 'good'} />
        <Stat label="Last sync" value={lastSyncAt ? dateTime(new Date(lastSyncAt)) : 'never'} />
      </div>

      <Card
        title="Offline sync queue"
        subtitle="Everything you enter is saved on this device first, then uploaded in order"
        actions={<button className="btn-primary btn-sm" onClick={async () => {
          const r = await flushOutbox()
          toast.success(`${r.sent} uploaded, ${r.failed} failed.`)
        }} disabled={!online || syncing}>
          <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} /> Sync now
        </button>}
      >
        <Alert tone="info" title="How this works">
          Each queued entry carries a unique transaction id. If a 2G call drops halfway, the retry is
          recognised by the server and applied only once — stock can never be double-posted. Entries upload
          strictly in the order you created them, because an approval cannot arrive before its document.
        </Alert>
        {lastError && <div className="mt-3"><Alert tone="error" title="Last sync error">{lastError}</Alert></div>}
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {entries.length === 0 && (
          <EmptyState icon={<CheckCircle2 className="h-9 w-9" />} title="Nothing queued"
                      message="Everything you have entered has been uploaded." />
        )}
        {entries.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead><tr><th>What</th><th>Type</th><th>Created</th><th>Status</th><th>Attempts</th><th>Error</th><th /></tr></thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td className="max-w-[280px] truncate text-sm">{e.label}</td>
                    <td className="text-xs"><span className="pill-grey">{e.doc_type}</span></td>
                    <td className="text-xs text-slate-500">{dateTime(new Date(e.created_at))}</td>
                    <td><span className={STATUS_STYLE[e.status]}>{e.status}</span></td>
                    <td className="num text-xs">{e.attempts}</td>
                    <td className="max-w-[280px] truncate text-xs text-red-600">{e.last_error ?? '—'}</td>
                    <td className="text-right">
                      {e.status === 'failed' && (
                        <span className="flex justify-end gap-1">
                          <button className="btn-secondary btn-sm" onClick={() => retryEntry(e.id!)}>Retry</button>
                          <button className="btn-ghost btn-sm text-red-600" onClick={() => discardEntry(e.id!)}
                                  title="Discard — the entry is lost"><Trash2 className="h-3.5 w-3.5" /></button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

// ===========================================================================
// Master data editor
//
// One form for four very different tables. The shape of each is declared as
// data rather than as four near-identical components, so adding a field later
// is one line rather than a new screen.
// ===========================================================================
interface MasterField {
  key: string
  label: string
  type?: 'text' | 'number' | 'select' | 'textarea' | 'checkbox'
  hint?: string
  required?: boolean
  options?: Array<{ value: string; label: string }>
  wide?: boolean
  /**
   * Offer what is already in use on this column as you type. Free-text
   * columns like Category drift badly otherwise — "Steel", "steel" and
   * "Steel " become three categories and every report splits three ways.
   */
  suggestFrom?: { table: string; column: string }
  /**
   * Hold this box in capitals as it is typed. Material names are stored
   * uppercase since 12-09-2026 (the database enforces it either way); doing it
   * in the box too means what was typed is what is saved, with no silent
   * correction after the fact.
   */
  uppercase?: boolean
  /**
   * Render a dropdown of people holding a role, storing their profile id.
   * Used for the Project Manager on a site — a free-text box there would let
   * somebody assign a requisition to a person who does not exist.
   */
  peopleWithRole?: string
  /**
   * A dropdown fed from a master table. Unlike suggestFrom — which offers what
   * is already in use and still lets anything be typed — this is a CLOSED list.
   * Five spellings of "number" is five units to a database and one to a person.
   */
  optionsFrom?: { table: string; value: string; label: string; activeOnly?: boolean }
  /**
   * The two-level category list. `level: 'category'` shows the categories for
   * that domain; `level: 'sub'` shows only the sub-categories belonging to
   * whatever the category box currently holds, and empties itself when the
   * category changes underneath it.
   *
   * A value already on the record that is not in the list is kept and shown
   * rather than dropped — a legacy category must not vanish just because
   * somebody opened the form to correct a phone number.
   */
  categoryList?: { domain: 'MATERIAL' | 'VENDOR'; level: 'category' | 'sub'; parentKey?: string }
  /**
   * 0233 (04-11-2026): the site cash-flow group / line (materials only), from
   * material_cashflow_lines. Required on a NEW material; on an existing one it
   * may stay empty, but once set it can only be changed, never emptied.
   */
  cashflow?: 'group' | 'line'
  /**
   * Used only on the Categories tab itself: pick which category this new
   * sub-category belongs under. Stores the row id, and narrows to whichever
   * domain the form's domain box is set to. Leave it empty to create a
   * top-level category.
   */
  parentPicker?: { domainKey: string }
  /**
   * Leave the column out of the save unless the form actually holds a value
   * for it (added 28-09-2026, for the timber cross-section). A record opened
   * from an older copy of the master list may not carry the column at all;
   * without this, saving an unrelated change would write NULL over it.
   */
  sendOnlyIfPresent?: boolean
}

/** One row of the category master, as the form needs it. */
interface CategoryRow {
  id: number; domain: string; parent_id: number | null; name: string; sort_order: number
}

const MASTER_FORMS: Record<string, {
  table: string
  title: string
  idField: string
  /** The id is typed by hand on these tables — they are codes, not sequences. */
  idEditable: boolean
  /**
   * Set when the database hands out the id. The form shows what the next one
   * will be, greyed out, and never sends it — the value that counts is
   * decided by a trigger inside the insert, so two people saving at the same
   * moment cannot both be given MAT-104.
   */
  idRpc?: string
  fields: MasterField[]
}> = {
  materials: {
    table: 'materials', title: 'material', idField: 'id', idEditable: false,
    idRpc: 'rpc_next_material_id',
    fields: [
      { key: 'id', label: 'Material ID',
        hint: 'Given out by the system, in sequence. Nothing to remember and nothing to type.' },
      { key: 'item_code', label: 'Item code', required: true, hint: 'e.g. CEM-OPC-43' },
      { key: 'item_name', label: 'Material name', required: true, wide: true, uppercase: true,
        hint: 'Saved in capitals, the way it is written on the rack.' },
      { key: 'description', label: 'Description', type: 'textarea', wide: true },
      { key: 'hsn_code', label: 'HSN code' },
      { key: 'category', label: 'Category', required: true,
        hint: 'Pick the closest one. Use Others only when nothing fits.',
        categoryList: { domain: 'MATERIAL', level: 'category' } },
      { key: 'sub_category', label: 'Sub category', required: true,
        hint: 'The list narrows to whatever the category above is set to.',
        categoryList: { domain: 'MATERIAL', level: 'sub', parentKey: 'category' } },
      // 0233: the site cash-flow group and line. A new material cannot be used
      // until the System Administrator approves these two.
      { key: 'cashflow_group', label: 'Cash-flow group', cashflow: 'group',
        hint: 'Where this material is planned on the site cash flow (Material Payment). Pipeline, gas, fuel or office item: choose "Not on civil cash flow".' },
      { key: 'cashflow_line', label: 'Cash-flow line', cashflow: 'line',
        hint: 'The line within that group. A new material is checked by the System Administrator before it can be used.' },
      { key: 'unit_of_measure', label: 'Stock UOM', required: true,
        optionsFrom: { table: 'uoms', value: 'code', label: 'description', activeOnly: true },
        hint: 'How the STORE counts it. Pick from the list — if the unit you need is missing, add it on the Units tab first.' },
      { key: 'purchase_uom', label: 'Purchase UOM', required: true,
        optionsFrom: { table: 'uoms', value: 'code', label: 'description', activeOnly: true },
        hint: 'How the VENDOR sells it — KG for TMT bars that the store counts in NO.' },
      { key: 'conversion_factor', label: 'Conversion factor', type: 'number', required: true,
        hint: '1 stock UOM = how many purchase UOM. Use 1 when they are the same.' },
      { key: 'gst_rate', label: 'GST %', type: 'number', required: true },
      { key: 'base_rate', label: 'Base rate per stock UOM (₹)', type: 'number',
        hint: 'Optional. A standing price per STOCK unit (per bar for TMT, per block for AAC), used before this material has ever been bought.' },
      { key: 'min_stock_level', label: 'Minimum stock', type: 'number', required: true,
        hint: 'Below this the material is flagged on the stock screen. A new material starts with the figure set on System Settings.' },
      { key: 'max_stock_level', label: 'Maximum stock', type: 'number', required: true,
        hint: 'The most worth holding at one site. A new material starts with the figure set on System Settings.' },
      // Timber cross-section, 28-09-2026. Until now these could only be set by
      // migration 0106, once, so any runner added or misspelt since then never
      // showed the tally sheet at the gate. Both filled = measured timber;
      // both blank = an ordinary material, exactly as before.
      { key: 'cft_width_in', label: 'Timber width (inches)', type: 'number', sendOnlyIfPresent: true,
        hint: 'Only for timber stocked in CFT, like Wooden Runner 3"X4" → 3. Fill both boxes and the store-man gets the timber tally sheet on every receipt, issue, return and transfer. Leave both blank for everything else.' },
      { key: 'cft_thickness_in', label: 'Timber thickness (inches)', type: 'number', sendOnlyIfPresent: true,
        hint: 'The second size, like Wooden Runner 3"X4" → 4. Must be filled together with the width, or both left blank.' },
      { key: 'is_active', label: 'Active', type: 'checkbox' },
    ],
  },
  vendors: {
    table: 'vendors', title: 'vendor', idField: 'id', idEditable: false,
    idRpc: 'rpc_next_vendor_id',
    fields: [
      { key: 'id', label: 'Vendor ID',
        hint: 'Given out by the system, in sequence. Nothing to remember and nothing to type.' },
      { key: 'company_name', label: 'Company name', required: true, wide: true },
      { key: 'gstin', label: 'GSTIN',
        hint: 'Checked in full — the 15-character shape, the check digit, and that the first two digits match the state below. Leave blank for an unregistered vendor.' },
      { key: 'pan', label: 'PAN' },
      { key: 'contact_person', label: 'Contact person' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'address', label: 'Address', type: 'textarea', wide: true },
      { key: 'state_name', label: 'State', required: true,
        hint: 'Decides CGST+SGST versus IGST on every purchase order. Defaults to Tripura.',
        suggestFrom: { table: 'vendors', column: 'state_name' } },
      { key: 'category', label: 'Category', required: true,
        hint: 'What OVAL buys from them, not what they call themselves.',
        categoryList: { domain: 'VENDOR', level: 'category' } },
      { key: 'sub_category', label: 'Sub category', required: true,
        hint: 'The list narrows to whatever the category above is set to.',
        categoryList: { domain: 'VENDOR', level: 'sub', parentKey: 'category' } },
      { key: 'bank_name', label: 'Bank' },
      { key: 'account_number', label: 'Account number' },
      { key: 'ifsc', label: 'IFSC' },
      { key: 'is_gst_registered', label: 'GST registered', type: 'checkbox' },
      { key: 'is_active', label: 'Active', type: 'checkbox' },
    ],
  },
  sites: {
    table: 'sites', title: 'site', idField: 'id', idEditable: true,
    fields: [
      { key: 'id', label: 'Site ID', required: true, hint: 'e.g. SITE-TRP-08. Cannot be changed later.' },
      { key: 'site_name', label: 'Site name', required: true, wide: true },
      { key: 'site_code', label: 'Site code', required: true,
        hint: 'Three letters. Appears in every document number raised at this site — TR8.' },
      { key: 'address', label: 'Address', type: 'textarea', wide: true },
      { key: 'state_name', label: 'State', required: true },
      { key: 'gst_state_code', label: 'GST state code', required: true, hint: 'Tripura is 16' },
      { key: 'project_type', label: 'Project type' },
      { key: 'customer_name', label: 'Customer' },
      // Board 11-08-2026. Stage 2 of every requisition raised at this site
      // waits with this person and nobody else, so a site left blank here
      // stops its requisitions dead.
      { key: 'project_manager_id', label: 'Project Manager', peopleWithRole: 'project_manager',
        wide: true,
        hint: 'Approves every requisition raised at this site. Leave it unset and they cannot move past stage 2.' },
      // Management 31-08-2026. This form is already the System Administrator's
      // alone — the board's matrix gives Sites detail-CRUD to admin and an eye
      // icon to everybody else — and fn_guard_site_jv_flags() enforces the
      // same thing in the database, so these two cannot be changed by an API
      // call either.
      { key: 'requires_original_indent', label: 'Financed by a joint venture', type: 'checkbox',
        hint: 'Tick this and every purchase order shipped here must quote the JV partner’s Original Indent No and Date before it can be submitted, and is BILLED TO the joint venture instead of to OVAL. Leave it clear for an ordinary OVAL-financed site. The two boxes below then become compulsory.' },
      { key: 'jv_partner_name', label: 'JV entity — registered legal name', wide: true,
        hint: 'Exactly as it appears on the JV’s GST certificate. This is PRINTED as the Bill To name on every purchase order for this site, so it must match the GSTIN below.' },
      // Board 01-09-2026. OVAL contracts in its own name on JV projects for an
      // interim period of six to ten months, so this stays CLEAR. The JV's
      // details above are kept on file, ready for the day it is ticked.
      { key: 'bill_to_jv', label: 'Bill purchase orders to the JV entity', type: 'checkbox',
        hint: 'Leave this CLEAR. Under the board’s interim arrangement (01-09-2026) OVAL contracts in its own name on JV projects, so the Bill To block prints OVAL’s name, address, GSTIN and buyer’s contact exactly as it does for every other site. Tick it only when the board confirms the interim period has ended — the Original Indent No and Date are unaffected either way.' },
      { key: 'jv_gstin', label: 'JV GSTIN',
        hint: 'Kept on file. It is printed as the Bill To GSTIN in place of OVAL’s 16AABCO9053D1ZL only when the box above is ticked, and is checked in full at that moment — the 15-character shape, the check digit, and that the state matches the one OVAL bills from. The letterhead always keeps OVAL’s own GSTIN.' },
      { key: 'jv_billing_address', label: 'JV registered address', type: 'textarea', wide: true,
        hint: 'Only if the JV is registered somewhere other than OVAL’s own office. Leave it blank — as it is for the JV on the books today — and the purchase order prints OVAL’s registered address, exactly as it always has.' },
      { key: 'is_active', label: 'Active', type: 'checkbox' },
    ],
  },
  uoms: {
    table: 'uoms', title: 'unit of measure', idField: 'code', idEditable: true,
    fields: [
      { key: 'code', label: 'Code', required: true,
        hint: 'What appears on every document — NO, KG, CFT. Short, upper case, and never changed once used.' },
      { key: 'description', label: 'Description', required: true, wide: true,
        hint: 'What a person calls it. "Cubic foot", not "CFT".' },
      { key: 'uom_class', label: 'Measures', type: 'select',
        options: [
          { value: 'COUNT',  label: 'Count — number, pair, set' },
          { value: 'WEIGHT', label: 'Weight — kg, tonne' },
          { value: 'LENGTH', label: 'Length — metre, foot' },
          { value: 'AREA',   label: 'Area — square metre' },
          { value: 'VOLUME', label: 'Volume — cubic metre, litre' },
          { value: 'PACK',   label: 'Pack — bag, roll, drum' },
          { value: 'TIME',   label: 'Time — day, month' },
        ],
        hint: 'Only for grouping the list. A conversion between two classes — kilograms per bar — is normal and allowed.' },
      { key: 'sort_order', label: 'Order in the list', type: 'number' },
      { key: 'is_active', label: 'Active', type: 'checkbox' },
    ],
  },
  categories: {
    table: 'category_master', title: 'category', idField: 'id', idEditable: false,
    fields: [
      { key: 'domain', label: 'Applies to', required: true, type: 'select',
        options: [
          { value: 'MATERIAL', label: 'Materials' },
          { value: 'VENDOR',   label: 'Vendors' },
        ],
        hint: 'Materials and vendors keep separate lists. A material is pipe or cement; a vendor is a supplier or a subcontractor.' },
      { key: 'parent_id', label: 'Belongs under',
        parentPicker: { domainKey: 'domain' },
        hint: 'Leave empty to create a new top-level category. Pick one to add a sub-category under it.' },
      { key: 'name', label: 'Name', required: true, wide: true,
        hint: 'How it will read in the dropdown. Two levels only — a sub-category cannot have its own children.' },
      { key: 'sort_order', label: 'Position in the list', type: 'number',
        hint: 'Lower numbers appear first. Others is always pushed to the bottom whatever this says.' },
      { key: 'is_active', label: 'Active', type: 'checkbox' },
    ],
  },
  terms: {
    table: 'po_terms', title: 'purchase-order term', idField: 'id', idEditable: false,
    fields: [
      { key: 'term_text', label: 'Term', required: true, type: 'textarea', wide: true,
        hint: 'Printed on every purchase order that has this term ticked.' },
      { key: 'sort_order', label: 'Order on the PO', type: 'number' },
      { key: 'is_active', label: 'Active', type: 'checkbox' },
    ],
  },
}

function MasterForm({ kind, row, copyOf, onClose, onDone }: {
  kind: string; row: any | null
  /**
   * Copy, 29-09-2026 (materials only). A NEW record pre-filled from this one:
   * everything carried over except the id (the database gives out the next
   * one), the base rate (a different size costs differently) and the audit
   * columns. Item code and name arrive filled so only the size needs typing,
   * and save() refuses them unchanged.
   */
  copyOf?: any
  onClose: () => void; onDone: () => void
}) {
  const cfg = MASTER_FORMS[kind]
  const toast = useToast()
  const editing = !!row
  const copying = !editing && !!copyOf
  // Sensible starting values so a new row cannot be saved half-empty. The
  // stock levels are NOT NULL in the database — leaving them blank used to
  // fail with a constraint error the user could do nothing about.
  //
  // Every site and very nearly every vendor is in Tripura, so it is pre-filled.
  // It still shows in the box and can be typed over — a default the user can
  // see is safe; a hidden one is not.
  const [f, setF] = useState<any>(row ?? (copying ? (() => {
    // Only the form's own fields travel. Anything else on the source row
    // (created_at, updated_at, audit columns) is never part of a save anyway.
    const c: any = {}
    if (kind === 'vendors') {
      // Vendors, 29-09-2026: a copy is ANOTHER FIRM in the same trade, so only
      // the classification travels. Name, GSTIN, PAN, contacts and address
      // belong to the other firm. Bank details are never copied, so one firm's
      // account cannot ride onto another's record by accident.
      for (const k of ['state_name', 'category', 'sub_category', 'is_gst_registered']) c[k] = copyOf[k]
      c.is_active = true
      return c
    }
    for (const fl of cfg.fields) c[fl.key] = copyOf[fl.key]
    delete c[cfg.idField]
    c.base_rate = null
    c.is_active = true
    return c
  })() : {
    is_active: true,
    conversion_factor: 1,
    sort_order: 99,
    min_stock_level: 10,
    max_stock_level: 1000,
    ...(kind === 'vendors' ? { state_name: 'Tripura', is_gst_registered: true } : {}),
    ...(kind === 'sites' ? { state_name: 'Tripura', gst_state_code: '16' } : {}),
    // Every closed list starts on its first option. save() turns '' into NULL,
    // and these columns are NOT NULL with a default — an explicit NULL defeats
    // the default and the insert fails.
    ...Object.fromEntries(
      (MASTER_FORMS[kind]?.fields ?? [])
        .filter((fl) => fl.options?.length)
        .map((fl) => [fl.key, fl.options![0].value]),
    ),
  }))

  // 0212: a NEW material starts with the stock levels set on System Settings
  // (10 and 1,000 until changed). Once only, only while the boxes still hold
  // those starting numbers - never on an edit or a copy, which keep their own.
  const { ready: settingsReady, materialMinStockDefault, materialMaxStockDefault } = useCompanySettings()
  const [stockPrefilled, setStockPrefilled] = useState(false)
  useEffect(() => {
    if (kind !== 'materials' || editing || copying || stockPrefilled || !settingsReady) return
    setStockPrefilled(true)
    setF((p: any) => ({
      ...p,
      min_stock_level: Number(p.min_stock_level) === 10 ? materialMinStockDefault : p.min_stock_level,
      max_stock_level: Number(p.max_stock_level) === 1000 ? materialMaxStockDefault : p.max_stock_level,
    }))
  }, [kind, editing, copying, stockPrefilled, settingsReady, materialMinStockDefault, materialMaxStockDefault])

  // Vendor papers - GST certificate, PAN, bank mandate. Optional, and only on
  // the vendor form. Held here until the row is saved, because a brand-new
  // vendor has no id to file them under until the database allocates one.
  const [vendorDocs, setVendorDocs] = useState<PendingAttachment[]>([])
  const [docsVersion, setDocsVersion] = useState(0)

  // What is already in use on the free-text columns, so the form can offer it.
  const [suggest, setSuggest] = useState<Record<string, string[]>>({})
  useEffect(() => {
    let live = true
    const wanted = cfg.fields.filter((fl) => fl.suggestFrom)
    if (wanted.length === 0) return
    void Promise.all(wanted.map(async (fl) => {
      const { table, column } = fl.suggestFrom!
      const { data } = await supabase.from(table).select(column).limit(2000)
      const vals = [...new Set(((data ?? []) as any[])
        .map((r) => String(r[column] ?? '').trim())
        .filter(Boolean))].sort((a, b) => a.localeCompare(b))
      return [fl.key, vals] as const
    })).then((pairs) => { if (live) setSuggest(Object.fromEntries(pairs)) })
    return () => { live = false }
  }, [kind])
  // Closed lists pulled from a master table.
  const [lists, setLists] = useState<Record<string, Array<{ value: string; label: string }>>>({})
  useEffect(() => {
    let live = true
    const wanted = cfg.fields.filter((fl) => fl.optionsFrom)
    if (wanted.length === 0) return
    void Promise.all(wanted.map(async (fl) => {
      const o = fl.optionsFrom!
      let q = supabase.from(o.table).select('*')
      if (o.activeOnly) q = q.eq('is_active', true)
      const { data } = await q.limit(500)
      const rows = ((data ?? []) as any[])
        .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999)
                        || String(a[o.value]).localeCompare(String(b[o.value])))
      return [fl.key, rows.map((r) => ({
        value: String(r[o.value]),
        label: `${r[o.value]} — ${r[o.label]}`,
      }))] as const
    })).then((pairs) => { if (live) setLists(Object.fromEntries(pairs)) })
    return () => { live = false }
  }, [kind])

  // People available for any role-backed dropdown on this form.
  const [people, setPeople] = useState<Record<string, Array<{ value: string; label: string }>>>({})
  useEffect(() => {
    let live = true
    const wanted = cfg.fields.filter((fl) => fl.peopleWithRole)
    if (wanted.length === 0) return
    void Promise.all(wanted.map(async (fl) => {
      const { data } = await supabase.from('profiles')
        .select('id, full_name, email')
        .eq('role', fl.peopleWithRole!).eq('is_active', true)
        .order('full_name')
      return [fl.key, ((data ?? []) as any[]).map((r) => ({
        value: r.id, label: `${r.full_name} <${r.email}>`,
      }))] as const
    })).then((pairs) => { if (live) setPeople(Object.fromEntries(pairs)) })
    return () => { live = false }
  }, [kind])

  // The two-level category list. Loaded whole — it is a few hundred short
  // rows — and filtered in the browser, because the Categories tab needs both
  // domains at once and the material form needs one.
  const needsCats = cfg.fields.some((fl) => fl.categoryList || fl.parentPicker)
  const [cats, setCats] = useState<CategoryRow[]>([])
  useEffect(() => {
    if (!needsCats) return
    let live = true
    void supabase.from('category_master')
      .select('id, domain, parent_id, name, sort_order')
      .eq('is_active', true)
      .order('sort_order').order('name')
      .then(({ data }) => { if (live) setCats((data ?? []) as CategoryRow[]) })
    return () => { live = false }
  }, [needsCats])

  // 0233: the cash-flow groups and lines, in blueprint order. A copy carries
  // the group and line over and is checked like any other new material.
  const needsCashflow = cfg.fields.some((fl) => fl.cashflow)
  const cfLines = useCashflowLines(needsCashflow)

  const [busy, setBusy] = useState(false)
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }))

  /**
   * The options for one category box.
   *
   * `current` is what the record already holds. If that value is not in the
   * list — an old category from before this list existed, or one somebody has
   * since retired — it is added back at the bottom and labelled, so that
   * opening the form does not silently blank it. Losing a classification
   * because somebody opened a form to fix a phone number is worse than
   * showing an untidy one.
   */
  function categoryOptions(fl: MasterField, current: string): Array<{ value: string; label: string }> {
    const spec = fl.categoryList!
    const mine = cats.filter((c) => c.domain === spec.domain)
    let rows: CategoryRow[]
    if (spec.level === 'category') {
      rows = mine.filter((c) => c.parent_id === null)
    } else {
      const parentName = String(f[spec.parentKey ?? 'category'] ?? '')
      const parent = mine.find((c) => c.parent_id === null && c.name === parentName)
      rows = parent ? mine.filter((c) => c.parent_id === parent.id) : []
    }
    // "Others" belongs at the bottom wherever it lands in the sort order.
    const opts = [...rows]
      .sort((a, b) => (a.name === 'Others' ? 1 : b.name === 'Others' ? -1 : 0)
                   || a.sort_order - b.sort_order
                   || a.name.localeCompare(b.name))
      .map((c) => ({ value: c.name, label: c.name }))
    if (current && !opts.some((o) => o.value === current)) {
      opts.push({ value: current, label: `${current} — not in the standard list` })
    }
    return opts
  }

  // The id this record is going to get. Shown greyed out so the box is never
  // an empty thing somebody feels they have to fill in. It is only a preview:
  // asking costs nothing and reserves nothing, so opening the form and
  // changing your mind does not leave a hole in the numbering.
  const autoId = !editing && !!cfg.idRpc
  const [nextId, setNextId] = useState<string | null>(null)
  useEffect(() => {
    if (!autoId) return
    let live = true
    void supabase.rpc(cfg.idRpc!).then(({ data }) => {
      if (!live) return
      // On error the box says what will happen rather than sitting on
      // "Assigning…" forever. The number is decided at save either way.
      setNextId(typeof data === 'string' ? data : 'Assigned when you save')
    })
    return () => { live = false }
  }, [autoId, cfg.idRpc])

  async function save() {
    for (const fl of cfg.fields) {
      // Nothing to validate on a field the person cannot type into.
      if (autoId && fl.key === cfg.idField) continue
      if (fl.required && !String(f[fl.key] ?? '').trim()) {
        toast.error(`${fl.label} is required.`); return
      }
    }
    // Copy, 29-09-2026: a copy must be a DIFFERENT material. Compared ignoring
    // case and spaces, because the name is saved in capitals anyway. The
    // database refuses a repeated item code by itself; the name it would not.
    if (copying && kind === 'vendors') {
      // Nothing in the database stops two records for one firm, and a vendor
      // split across two ids splits its orders, payables and liabilities too.
      const norm = (s: any) => String(s ?? '').trim().replace(/\s+/g, ' ').toUpperCase()
      if (norm(f.company_name) === norm(copyOf.company_name)) {
        toast.error('Change the company name — it is still the name of the vendor you copied. A copy is for a different firm.'); return
      }
      const g = norm(f.gstin)
      if (g) {
        // Inactive vendors included: a switched-off record is still the same firm.
        const { data: dup, error: dupErr } = await supabase.from('vendors')
          .select('id, company_name').ilike('gstin', g).limit(1)
        if (dupErr) { toast.error(`Could not check the GSTIN: ${dupErr.message}`); return }
        if (dup && dup.length) {
          toast.error(`GSTIN ${g} already belongs to ${dup[0].id} ${dup[0].company_name}. That firm is already on file — use it rather than adding it again.`); return
        }
      }
    }
    if (copying && kind === 'materials') {
      const same = (a: any, b: any) =>
        String(a ?? '').trim().toUpperCase() === String(b ?? '').trim().toUpperCase()
      if (same(f.item_code, copyOf.item_code)) {
        toast.error(`Change the item code — ${copyOf.item_code} belongs to the material you copied.`); return
      }
      if (same(f.item_name, copyOf.item_name)) {
        toast.error('Change the material name — it is still the name of the material you copied. Type the new size.'); return
      }
    }
    // 0233: the cash-flow group and line - both on a new material; on an
    // existing one both or neither, and never emptied once set.
    if (needsCashflow) {
      const g = String(f.cashflow_group ?? '').trim()
      const l = String(f.cashflow_line ?? '').trim()
      if (!editing && (!g || !l)) {
        toast.error('Choose the Cash-flow group and Cash-flow line. For a pipeline, gas, fuel or office item choose "Not on civil cash flow".'); return
      }
      if (g && !l) { toast.error('Choose the Cash-flow line as well.'); return }
      if (editing && row.cashflow_group && !g) {
        toast.error('The cash-flow group cannot be emptied. Choose "Not on civil cash flow" if it is not a civil purchase.'); return
      }
    }
    // Timber cross-section, 28-09-2026: both sizes or neither, above zero, and
    // only on a material the store counts in CFT - the tally sheet works out
    // cubic feet, so on anything else it would put the wrong number in stock.
    // The database refuses a half-filled pair as well; this says why first.
    if (kind === 'materials') {
      const w = String(f.cft_width_in ?? '').trim()
      const t = String(f.cft_thickness_in ?? '').trim()
      if (w || t) {
        if (!w || !t) {
          toast.error('Fill in both timber sizes - width and thickness - or leave both blank.'); return
        }
        if (!(Number(w) > 0) || !(Number(t) > 0)) {
          toast.error('Timber width and thickness must both be more than zero.'); return
        }
        if (String(f.unit_of_measure ?? '').toUpperCase() !== 'CFT') {
          toast.error('Timber sizes are only for a material stocked in CFT. Change the Stock UOM, or leave both sizes blank.'); return
        }
      }
    }
    setBusy(true)
    try {
      const payload: any = {}
      for (const fl of cfg.fields) {
        // Never send the id on an update — it is the key, not a field.
        // Never send it on an auto-numbered insert either: the database picks
        // it. Sending a blank would be sending a decision we do not have.
        if (fl.key === cfg.idField && (editing || autoId)) continue
        if (fl.sendOnlyIfPresent && f[fl.key] === undefined) continue
        let v = f[fl.key]
        if (v === '' || v === undefined) v = null
        if (fl.type === 'number' && v !== null) v = Number(v)
        if (fl.type === 'checkbox') v = !!v
        payload[fl.key] = v
      }

      let error: { message: string } | null = null
      let assigned: string | null = null
      if (editing) {
        ({ error } = await supabase.from(cfg.table).update(payload).eq(cfg.idField, row[cfg.idField]))
      } else if (autoId) {
        // Materials and vendors: no id is sent at all. A trigger fills it in
        // inside the insert, holding a lock, so simultaneous saves get
        // consecutive numbers instead of the same one. Reading it back is how
        // we find out what it decided — the preview in the greyed-out box was
        // only ever a good guess.
        const res = await supabase.from(cfg.table).insert(payload).select(cfg.idField).single()
        error = res.error
        assigned = (res.data as any)?.[cfg.idField] ?? null
      } else if (cfg.table === 'po_terms') {
        // Terms carry a readable running number the vendor can refer to on the
        // printed order — T009, T010. The database allocates it so two people
        // adding a term at once cannot collide, and nobody sees a UUID.
        //
        // Tested by table name rather than by "not one of the other cases",
        // which is how the Categories tab would otherwise have quietly asked
        // for a purchase-order term number.
        const { data: termId, error: idErr } = await supabase.rpc('rpc_next_term_id')
        if (idErr) throw new Error(idErr.message)
        ;({ error } = await supabase.from(cfg.table).insert({ ...payload, id: termId }))
        assigned = typeof termId === 'string' ? termId : null
      } else {
        // Sites and units of measure: the code is meaningful and typed by hand.
        // Categories: an ordinary auto-incrementing row.
        ({ error } = await supabase.from(cfg.table).insert(payload))
      }

      if (error) throw new Error(error.message)

      // Vendor papers, once there is an id to file them under. On a new vendor
      // that id is what the database just handed back - never the preview in
      // the greyed-out box, which is only a good guess.
      //
      // Queued rather than uploaded inline, so this uses the same offline
      // outbox as every other attachment in the system. Nothing new to go
      // wrong, and a dropped connection does not lose the file.
      if (kind === 'vendors' && vendorDocs.length) {
        const vendorId = editing ? String(row[cfg.idField]) : assigned
        if (vendorId) {
          await queueAttachments('VENDOR', vendorId, vendorDocs)
          toast.success(`${vendorDocs.length} document(s) queued for upload.`)
          setVendorDocs([])
          setDocsVersion((v) => v + 1)
        } else {
          // Saved, but we cannot say where the files belong. Better to say so
          // than to file them under a guess.
          toast.error('Vendor saved, but the documents could not be attached - '
                    + 'reopen the vendor and add them again.')
        }
      }

      toast.success(
        editing ? `Updated ${cfg.title}.`
                : assigned ? `Added ${cfg.title} ${assigned}.`
                           : `Added ${cfg.title}.`)
      // 0233: say plainly what happens next to the cash-flow values. The
      // database decides (the System Administrator's own entries need no
      // approval); read back what it decided rather than guessing.
      if (kind === 'materials') {
        const mid = editing ? String(row[cfg.idField]) : assigned
        if (mid) {
          const { data: st } = await supabase.from('materials')
            .select('cf_status, cf_proposed_group').eq('id', mid).maybeSingle()
          if (st?.cf_status === 'PENDING') {
            toast.success(`${mid} is waiting for the System Administrator to approve its cash-flow group and line. It cannot be used on any requisition, order or receipt until then.`)
          } else if (editing && st?.cf_proposed_group
                     && (f.cashflow_group !== row.cashflow_group || f.cashflow_line !== row.cashflow_line)) {
            toast.success('The cash-flow change is waiting for the System Administrator. The material stays usable on its current values meanwhile.')
          }
        }
      }
      onDone()
    } catch (e) {
      const raw = e instanceof Error ? e.message : 'Could not save'
      // Postgres reports a broken unique index in its own language. Item code
      // is the one people hit, and "duplicate key value violates unique
      // constraint" tells a storeman nothing about what to do next.
      toast.error(
        raw.includes('materials_item_code_unique')
          ? `Item code "${String(f.item_code ?? '').trim()}" already belongs to another material. Codes are unique — search for it in the list, or use a different code.`
          : raw.includes('uoms_pkey')
            ? `The unit code "${String(f.code ?? '').trim().toUpperCase()}" already exists. Edit the existing one instead.`
            // fn_assert_vendor_gstin already raises a full, plain sentence
            // explaining exactly which character is wrong. Do not replace it.
            : raw)
    } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} wide
           title={copying ? `Add ${cfg.title} — copied from ${copyOf[cfg.idField]}`
                          : `${editing ? 'Edit' : 'Add'} ${cfg.title}`}
           subtitle={editing ? String(row[cfg.idField])
                     : copying && kind === 'vendors' ? 'Only the state, category, sub-category and GST-registered carry over. Enter the new firm’s name, GSTIN, PAN, contacts, address and bank details, then save.'
                     : copying ? 'Change the item code and the material name to the new size, check the rest, then save. Base rate starts blank - a different size costs differently.'
                     : undefined}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy}>
               {editing ? 'Save changes' : `Add ${cfg.title}`}
             </button>
           </>}>
      <div className="grid gap-4 sm:grid-cols-2">
        {cfg.fields.map((fl) => {
          const locked = fl.key === cfg.idField && (editing || autoId)
          // The id box on a new material or vendor: filled in, greyed out,
          // nothing to remember. If the lookup has not come back yet it says
          // so rather than sitting empty and inviting a typed answer.
          if (autoId && fl.key === cfg.idField) {
            return (
              <Field key={fl.key} label={fl.label} hint={fl.hint}>
                <Input value={nextId ?? 'Assigning…'} disabled readOnly />
              </Field>
            )
          }
          if (fl.type === 'checkbox') {
            return (
              <label key={fl.key} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!f[fl.key]}
                       onChange={(e) => set(fl.key, e.target.checked)} />
                {fl.label}
              </label>
            )
          }
          // A CLOSED list declared inline on the field. This branch did not
          // exist: `type: 'select'` with static `options` fell straight through
          // to the plain text Input below, so the Measures box on a unit of
          // measure was free text. Somebody typed "Volume", the column only
          // accepts 'VOLUME', and the database refused it.
          //
          // The field was declared correctly and the type allowed `options` —
          // TypeScript cannot catch "you declared a capability nobody
          // implemented", which is exactly what this was.
          if (fl.options) {
            return (
              <Field key={fl.key} label={fl.label} required={fl.required} hint={fl.hint}
                     className={fl.wide ? 'sm:col-span-2' : undefined}>
                <Select value={String(f[fl.key] ?? '')}
                        onChange={(e) => set(fl.key, e.target.value)}
                        placeholder="Choose…"
                        options={fl.options} />
              </Field>
            )
          }
          if (fl.parentPicker) {
            const dom = String(f[fl.parentPicker.domainKey] ?? '')
            const tops = cats
              .filter((c) => c.domain === dom && c.parent_id === null)
              .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
              .map((c) => ({ value: String(c.id), label: c.name }))
            return (
              <Field key={fl.key} label={fl.label} hint={fl.hint}>
                <Select value={f[fl.key] == null ? '' : String(f[fl.key])}
                        disabled={!dom}
                        onChange={(e) => set(fl.key, e.target.value ? Number(e.target.value) : null)}
                        placeholder={dom ? 'Nothing — this is a top-level category' : 'Choose Materials or Vendors first'}
                        options={tops} />
              </Field>
            )
          }
          if (fl.cashflow) {
            const current = String(f[fl.key] ?? '')
            const isLine = fl.cashflow === 'line'
            const group = String(f.cashflow_group ?? '')
            const opts = isLine
              ? cfLines.filter((r) => r.group_name === group).map((r) => ({ value: r.line_name, label: r.line_name }))
              : [...new Set(cfLines.map((r) => r.group_name))].map((g) => ({ value: g, label: g }))
            const parked = editing && kind === 'materials' && row.cf_proposed_group
            return (
              <Field key={fl.key} label={fl.label} required={!editing} hint={fl.hint}
                     className={fl.wide ? 'sm:col-span-2' : undefined}>
                <Select value={current}
                        disabled={isLine && !group}
                        onChange={(e) => {
                          const v = e.target.value
                          if (isLine) { set(fl.key, v); return }
                          // A new group empties the line - unless the group
                          // has only one line, which is then the answer.
                          const lines = cfLines.filter((r) => r.group_name === v)
                          setF((p: any) => ({ ...p, cashflow_group: v,
                                              cashflow_line: lines.length === 1 ? lines[0].line_name : '' }))
                        }}
                        placeholder={isLine && !group ? 'Pick a cash-flow group first' : 'Choose…'}
                        options={opts} />
                {parked && !isLine && (
                  <p className="mt-1 text-xs text-amber-700">
                    Change waiting for approval: {row.cf_proposed_group} / {row.cf_proposed_line}
                  </p>
                )}
              </Field>
            )
          }
          if (fl.categoryList) {
            const current = String(f[fl.key] ?? '')
            const opts = categoryOptions(fl, current)
            const isSub = fl.categoryList.level === 'sub'
            const parentSet = !isSub || !!String(f[fl.categoryList.parentKey ?? 'category'] ?? '')
            return (
              <Field key={fl.key} label={fl.label} required={fl.required} hint={fl.hint}
                     className={fl.wide ? 'sm:col-span-2' : undefined}>
                <Select value={current}
                        disabled={!parentSet}
                        onChange={(e) => {
                          const v = e.target.value
                          if (isSub) { set(fl.key, v); return }
                          // Changing the category invalidates whatever sub
                          // category was chosen under the old one. Clearing it
                          // forces a deliberate re-pick instead of leaving
                          // "Valves / Bricks" sitting there looking plausible.
                          const children = cfg.fields
                            .filter((o) => o.categoryList?.parentKey === fl.key)
                            .map((o) => o.key)
                          setF((p: any) => ({
                            ...p, [fl.key]: v,
                            ...Object.fromEntries(children.map((k) => [k, ''])),
                          }))
                        }}
                        placeholder={parentSet ? 'Choose…' : 'Pick a category first'}
                        options={opts} />
              </Field>
            )
          }
          if (fl.optionsFrom) {
            return (
              <Field key={fl.key} label={fl.label} required={fl.required} hint={fl.hint}
                     className={fl.wide ? 'sm:col-span-2' : undefined}>
                <Select value={f[fl.key] ?? ''} onChange={(e) => set(fl.key, e.target.value)}
                        placeholder="Choose a unit…"
                        options={lists[fl.key] ?? []} />
              </Field>
            )
          }
          if (fl.peopleWithRole) {
            return (
              <Field key={fl.key} label={fl.label} required={fl.required} hint={fl.hint}
                     className={fl.wide ? 'sm:col-span-2' : undefined}>
                <Select value={f[fl.key] ?? ''} onChange={(e) => set(fl.key, e.target.value || null)}
                        placeholder="Not assigned"
                        options={people[fl.key] ?? []} />
              </Field>
            )
          }
          return (
            <Field key={fl.key} label={fl.label} required={fl.required} hint={fl.hint}
                   className={fl.wide ? 'sm:col-span-2' : undefined}>
              {fl.type === 'textarea'
                ? <Textarea rows={2} value={f[fl.key] ?? ''} onChange={(e) => set(fl.key, e.target.value)} />
                : <>
                    <Input type={fl.type === 'number' ? 'number' : 'text'} step="any"
                           value={f[fl.key] ?? ''} disabled={locked}
                           list={fl.suggestFrom ? `sugg-${fl.key}` : undefined}
                           autoComplete="off"
                           onChange={(e) => set(fl.key, fl.uppercase ? e.target.value.toUpperCase() : e.target.value)} />
                    {fl.suggestFrom && (
                      <datalist id={`sugg-${fl.key}`}>
                        {(suggest[fl.key] ?? []).map((v) => <option key={v} value={v} />)}
                      </datalist>
                    )}
                  </>}
            </Field>
          )
        })}
      </div>

      {kind === 'materials' && (
        <div className="mt-4">
          <Alert tone="warning" title="The conversion factor is the one to get right">
            It is how many purchase units make one stock unit — a 12mm TMT bar is stocked as
            <strong> NO</strong> but bought by <strong>KG</strong>, so the factor is 10.67. Get it wrong and
            every purchase order, goods receipt and stock figure for this material will be wrong.
          </Alert>
        </div>
      )}
      {kind === 'vendors' && (
        <div className="mt-4 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div>
            <p className="text-sm font-semibold text-slate-800">Vendor documents</p>
            <p className="mt-0.5 text-xs text-slate-600">
              All optional. Attach them now or come back and add them at any time.
              Visible to Procurement, Accounts and the administrator only.
            </p>
          </div>

          <AttachmentCollector
            docType="VENDOR"
            value={vendorDocs}
            onChange={setVendorDocs}
            hint={editing
              ? 'Pick the kind, choose the file, then press Save changes. Files upload in the background.'
              : 'Pick the kind and choose the file. They attach themselves once the vendor is saved and has an id.'}
          />

          {editing && (
            <div className="border-t border-slate-200 pt-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                On file
              </p>
              <VendorDocuments vendorId={String(row[cfg.idField])} canEdit refreshKey={docsVersion} />
            </div>
          )}
        </div>
      )}
      {kind === 'sites' && !editing && (
        <div className="mt-4">
          <Alert tone="warning" title="The site code is permanent in practice">
            Every document raised at this site carries it — <code>PR-TR8-20260901-001</code>. Changing it
            later leaves old documents pointing at a code that no longer exists.
          </Alert>
        </div>
      )}
    </Modal>
  )
}

// ===========================================================================
/**
 * A master record, read only.
 *
 * For every role the board gave View rather than CRUD - which after the
 * resolution of 31 Aug 2026 is most of them, on most masters.
 *
 * Deliberately NOT MasterForm with the inputs disabled. A disabled form still
 * has a Save button, still has save() attached, and is one mistaken prop away
 * from letting a viewer write. This renders text.
 *
 * The fields come from MASTER_FORMS - the same declaration the edit form is
 * built from - so a field added to a master appears here without anybody having
 * to remember this file exists.
 */
function MasterRecord({ kind, row, onClose }: {
  kind: MasterTab; row: any; onClose: () => void
}) {
  const cfg = MASTER_FORMS[kind]
  if (!cfg) return null

  const title = String(row[cfg.idField] ?? '')
  // The most name-like field, for the modal heading. Falls back to the id.
  const heading = String(
    row.item_name ?? row.company_name ?? row.site_name ?? row.name
    ?? row.term_text ?? row.description ?? title)

  return (
    <Modal open onClose={onClose} wide
           title={heading.length > 90 ? heading.slice(0, 90) + '…' : heading}
           subtitle={`${title} — read only`}
           footer={<button className="btn-secondary" onClick={onClose}>Close</button>}>
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        {cfg.fields.map((fl) => {
          const v = row[fl.key]
          const text = fl.type === 'checkbox'
            ? (v ? 'Yes' : 'No')
            : v === null || v === undefined || v === ''
              ? null
              : String(v)
          return (
            <div key={fl.key}>
              <dt className="text-xs font-medium text-slate-500">{fl.label}</dt>
              <dd className="mt-0.5 break-words text-sm text-slate-800">
                {text === null ? <span className="text-slate-400">—</span> : text}
              </dd>
            </div>
          )
        })}
      </dl>

      {kind === 'vendors' && (
        <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-semibold text-slate-800">Vendor documents</p>
          <p className="mb-3 mt-0.5 text-xs text-slate-600">
            Open to read. Adding and superseding are the Procurement team&rsquo;s job.
          </p>
          {/* canEdit false: no Supersede button. The database refuses it too,
              so this is the courtesy, not the control. */}
          <VendorDocuments vendorId={String(row[cfg.idField])} canEdit={false} />
        </div>
      )}
    </Modal>
  )
}

// ===========================================================================
/**
 * Company-wide switches that change how the system behaves.
 *
 * Only two so far, and both exist because loading three years of history
 * across 31 sites needs looser rules than running the business afterwards.
 * They are deliberately in the administrator's hands, not buried in a
 * migration, so the business can tighten them the day it is ready.
 */
function SystemSettings({ canEdit }: { canEdit: boolean }) {
  const toast = useToast()
  const [row, setRow] = useState<any | null>(null)
  const [busy, setBusy] = useState(false)

  const load = () => supabase.from('company_profile')
    .select('id, backdate_limit_days, block_negative_stock').eq('id', 1).maybeSingle()
    .then(({ data }) => setRow(data))

  useEffect(() => { void load() }, [])

  async function save(patch: Record<string, unknown>) {
    setBusy(true)
    try {
      const { error } = await supabase.from('company_profile').update(patch).eq('id', 1)
      if (error) throw new Error(error.message)
      toast.success('Setting saved. It applies immediately, everywhere.')
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save the setting')
    } finally { setBusy(false) }
  }

  if (!row) return <Card title="System settings"><Loading /></Card>

  const blocking = !!row.block_negative_stock
  const days = Number(row.backdate_limit_days ?? 1095)

  return (
    <div className="space-y-4">
      <Card title="Negative stock"
            subtitle="What happens when a back-dated document issues material the site did not hold on that date">
        <Alert tone={blocking ? 'success' : 'warning'}
               title={blocking ? 'Refusing negative stock' : 'Allowing it, and recording every instance'}>
          {blocking ? <>
            A goods issue or transfer is <strong>refused</strong> if the site did not have the
            material on the document's own date. This is standard ERP behaviour and where you
            should be once the backlog is loaded.
          </> : <>
            A back-dated issue is <strong>allowed</strong> even when the stock had not arrived yet,
            and every instance is written to the warnings list below. This is right while loading
            history — paperwork arrives out of order, and blocking would push people into inventing
            dates to get past the error, which is far worse than a figure somebody can see.
          </>}
        </Alert>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            className={blocking ? 'btn-secondary' : 'btn-primary'}
            disabled={!canEdit || busy}
            onClick={() => save({ block_negative_stock: !blocking })}
          >
            {blocking ? 'Switch back to warn only' : 'Start refusing negative stock'}
          </button>
          {!canEdit && (
            <span className="text-xs text-slate-500">
              Only the System Administrator may change this.
            </span>
          )}
        </div>

        <p className="mt-3 text-xs text-slate-500">
          Turn this on once the three-year backlog is in and the warnings list is empty. Check the
          list first — switching it on does not fix anything already posted.
        </p>
      </Card>

      <Card title="How far back documents may be dated"
            subtitle="Applies to every form, and is enforced by the database as well as the screen">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Days" className="w-40"
                 hint={`About ${(days / 365).toFixed(1)} years`}>
            <Input type="number" value={days} disabled={!canEdit}
                   onChange={(e) => setRow({ ...row, backdate_limit_days: e.target.value })} />
          </Field>
          <button className="btn-primary" disabled={!canEdit || busy}
                  onClick={() => save({ backdate_limit_days: Number(row.backdate_limit_days) || 1095 })}>
            Save
          </button>
          <div className="flex gap-2">
            {[[1095, '3 years'], [365, '1 year'], [90, '90 days'], [30, '30 days']].map(([d, label]) => (
              <button key={String(d)} className="btn-ghost btn-sm" disabled={!canEdit || busy}
                      onClick={() => save({ backdate_limit_days: d })}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Tighten this to 30 or 90 days once you are current. Everything picks it up at once — the
          date pickers, the error messages and the database checks all read this one number.
        </p>
      </Card>

      <NegativeStockList />
    </div>
  )
}

/** Every site and material whose running balance has ever gone below zero. */
function NegativeStockList() {
  const { data: rows, loading } = useQuery<any>('v_negative_stock', () =>
    supabase.from('v_negative_stock').select('*').order('lowest_balance'),
    [], { cacheable: false, idField: 'material_id' })

  const stillBad = rows.filter((r: any) => Number(r.balance_today) < 0).length

  return (
    <Card title={`Negative stock warnings (${rows.length})`}
          subtitle="Where the ledger has ever gone below zero — derived from the ledger, so it cannot drift">
      {loading && <Loading />}
      {!loading && rows.length === 0 && (
        <EmptyState icon={<CheckCircle2 className="h-8 w-8" />} title="No negative stock anywhere"
                    message="Every site has held enough of every material on every date. Safe to switch blocking on." />
      )}
      {rows.length > 0 && (
        <>
          <Alert tone={stillBad > 0 ? 'error' : 'warning'}
                 title={stillBad > 0
                   ? `${stillBad} still negative today — fix these now`
                   : 'All healthy today; it is the history that reads wrong'}>
            {stillBad > 0
              ? 'A material showing negative stock today means the ledger is genuinely out of step with the store. Post an adjustment, or find the receipt that was never entered.'
              : 'These sites hold the material today, but a document was back-dated to before the stock arrived. Back-date the receipt that supplied it and the history reads correctly.'}
          </Alert>
          <div className="table-wrap mt-3">
            <table className="tbl">
              <thead>
                <tr><th>Site</th><th>Material</th><th className="num">Lowest it reached</th>
                    <th>First negative</th><th>Last negative</th><th className="num">Days</th>
                    <th className="num">Today</th><th>Verdict</th></tr>
              </thead>
              <tbody>
                {rows.map((r: any) => (
                  <tr key={`${r.site_id}-${r.material_id}`}
                      className={Number(r.balance_today) < 0 ? 'bg-red-50/60' : ''}>
                    <td className="text-xs">{r.site_name}</td>
                    <td className="text-sm font-medium">{r.item_name}</td>
                    <td className="num font-semibold text-red-600">{qty(r.lowest_balance)}</td>
                    <td className="text-xs">{ddmmyyyy(r.first_negative_on)}</td>
                    <td className="text-xs">{ddmmyyyy(r.last_negative_on)}</td>
                    <td className="num text-xs">{r.days_negative}</td>
                    <td className={`num text-sm font-semibold ${Number(r.balance_today) < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                      {qty(r.balance_today)}
                    </td>
                    <td className="text-xs text-slate-600">{r.verdict}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  )
}
